/**
 * Org Event Detail — drill-in view inside the org dashboard shell.
 *
 * URL: /org-dashboard?event={formId}&view={responses|analytics|campaigns|seating|checkin}
 *
 * Reuses the org shell (sidebar, brand, header) so corporate users never
 * bounce out to the personal /dashboard page. Starts with a corporate-tuned
 * Responses tab; other tabs stub to "coming soon" for now.
 */

window.OrgEventDetail = (function () {
  const state = {
    formId: null,
    form: null,
    responses: [],
    view: 'responses',
    loading: false,
    search: '',
    statusFilter: 'all',         // all | yes | no | pending | waitlist
    sort: null,                  // { key, dir: 'asc'|'desc' } — task #855, per open event
    selected: new Set(),
    pollTimer: null,
    editing: null,               // key like "responseId:column" while a cell is being edited
    // Per-user column visibility — set of column keys the user has hidden.
    // Critical columns (_select, guestName, _actions) ignore this set.
    hiddenColumns: (() => {
      try {
        const raw = localStorage.getItem('orgEventDetail.hiddenColumns');
        return new Set(raw ? JSON.parse(raw) : []);
      } catch { return new Set(); }
    })(),
    // "Show additional attendees": a response with 2+ attendees expands so each
    // additional attendee gets their own (read-only) row under the registrant.
    // ON by default since task #1105 (the team: a primary-only table hid the
    // +1s, and their own answers now show on those rows). It was off by default
    // when it shipped (June 2026, "one row per response") with no reason beyond
    // compactness. Per-user preference, persisted like hiddenColumns: a host
    // who switched it off keeps it off ('0'); nobody else has a stored value.
    expandAttendees: (() => {
      try { return localStorage.getItem('orgEventDetail.expandAttendees') !== '0'; }
      catch { return true; }
    })(),
    // The "Waiting for your approval" box folded to its header (#839). Open by
    // default; per-user, kept across events and reloads like the two above.
    approvalsCollapsed: (() => {
      try { return localStorage.getItem('orgEventDetail.approvalsCollapsed') === '1'; }
      catch { return false; }
    })()
  };

  // ---- Per-EVENT column preferences: widths and display names (task #616) --
  //
  // Deliberately keyed by event, not global like hiddenColumns: a delegate list
  // and a gala guest list have different questions, so one shared set of widths
  // would be wrong for both. Held in this browser, per user — renaming a column
  // is a reading aid, and it must never touch the question your guests answered.
  //
  // Widths are what the host dragged; labels are only set for columns they have
  // actually renamed, so a question edited on the form still updates its header
  // unless it was deliberately given a short name.
  const COL_PREFS_KEY = 'orgEventDetail.colPrefs';
  let _colPrefsCache = null;
  let _colPrefsFor = null;

  function _colPrefs() {
    if (_colPrefsCache && _colPrefsFor === state.formId) return _colPrefsCache;
    let all = {};
    try { all = JSON.parse(localStorage.getItem(COL_PREFS_KEY) || '{}') || {}; } catch { all = {}; }
    const mine = all[state.formId || '_'] || {};
    _colPrefsCache = { widths: mine.widths || {}, labels: mine.labels || {}, order: Array.isArray(mine.order) ? mine.order : [] };
    _colPrefsFor = state.formId;
    return _colPrefsCache;
  }

  function _persistColPrefs() {
    try {
      let all = {};
      try { all = JSON.parse(localStorage.getItem(COL_PREFS_KEY) || '{}') || {}; } catch { all = {}; }
      const p = _colPrefs();
      // Drop the entry entirely once nothing is customised, so the store doesn't
      // grow a row per event the host merely opened.
      if (!Object.keys(p.widths).length && !Object.keys(p.labels).length && !p.order.length) delete all[state.formId || '_'];
      else all[state.formId || '_'] = { widths: p.widths, labels: p.labels, order: p.order };
      localStorage.setItem(COL_PREFS_KEY, JSON.stringify(all));
    } catch {}
  }

  /** The host's name for a column, falling back to the question's own text. */
  function _columnLabel(key, original) {
    const custom = _colPrefs().labels[key];
    return (custom && String(custom).trim()) ? String(custom).trim() : String(original || '');
  }

  /**
   * Attributes for a cell's inner wrap so a dragged column actually narrows.
   *
   * The width can't live on the <td>: this table is `table-layout: auto`,
   * which treats a cell's max-width as a suggestion and sizes the column to
   * its widest content regardless. The wrap inside the cell is a normal block
   * box, so capping IT caps the column — and `is-sized` turns on the ellipsis
   * that makes the clipped value readable.
   */
  function _cellSizeClass(col) {
    return (col && _colPrefs().widths[col.key]) ? ' is-sized' : '';
  }

  function _cellSizeStyle(col) {
    const w = col && _colPrefs().widths[col.key];
    // ~14px for the cell's own padding, so the text stops inside the border.
    return w ? ` style="max-width:${Math.max(28, w - 14)}px;"` : '';
  }

  /** Row-number, checkbox and actions columns have no name to rename. */
  function _isRenamableColumn(col) {
    return !!col && !['_rownum', '_select', '_actions'].includes(col.key);
  }

  function _persistHiddenColumns() {
    try {
      localStorage.setItem('orgEventDetail.hiddenColumns', JSON.stringify([...state.hiddenColumns]));
    } catch {}
  }

  function _persistExpandAttendees() {
    try { localStorage.setItem('orgEventDetail.expandAttendees', state.expandAttendees ? '1' : '0'); }
    catch {}
  }

  const POLL_MS = 30000;

  function _esc(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // Canonical response field is `guestName`; personal dashboard uses the same.
  // Fall back to `name`, then `email`, then a placeholder so rows never render blank.
  function _guestLabel(r, fallback = '—') {
    return r?.guestName || r?.name || r?.email || fallback;
  }

  // Task #854: a response's phone lives in `phoneNumber` — the form, Add guest
  // and Import all write it there, and it is the only phone field the response
  // PUT accepts. `phone` is read as a fallback only (nothing writes it on the
  // main guest; +1s keep theirs in additionalGuestDetails[].phone).
  function _phoneOf(r) {
    const stored = String(r?.phoneNumber || r?.phone || '').trim();
    // The spaced international form an import writes ("+971 50 123 1234",
    // task #863), shown only while it is still the same number — an edit in
    // the cell changes phoneNumber alone, and then that is what shows.
    const display = String(r?.phoneNumberDisplay || '').trim();
    const digits = (x) => x.replace(/\D/g, '');
    return display && stored && digits(display) === digits(stored) ? display : stored;
  }

  function _fmtDate(v) {
    try {
      const d = v?.toDate?.() || (v?._seconds ? new Date(v._seconds * 1000) : new Date(v));
      if (isNaN(d)) return '';
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
             ' · ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  }

  /**
   * The dashboard shell that owns the toast, the polling loop and the viewer
   * modal.
   *
   * `class OrgDashboard {}` at the top level of a classic script creates a
   * binding in the global LEXICAL environment — reachable by name from another
   * classic script, but NOT a property of `window`. Three calls here went
   * through `window.OrgDashboard?.` and were therefore dead: no toast from this
   * view ever appeared, and the events-list poll was never paused while an
   * event was open nor resumed when it closed (found while wiring task #614).
   */
  function _dash() {
    return (typeof OrgDashboard !== 'undefined') ? OrgDashboard : null;
  }

  function _showToast(msg, type, opts) {
    const d = _dash();
    if (d && d._showToast) d._showToast(msg, type, opts);
    else console.log(`[${type || 'info'}] ${msg}`);
  }

  async function open(formId, view) {
    if (state.formId !== formId) state.sort = null;
    state.formId = formId;
    state.view = view || 'responses';
    state.selected = new Set();

    // Hide org content, show detail panel
    document.getElementById('orgContent').style.display = 'none';
    document.getElementById('summaryCards').style.display = 'none';
    const detail = document.getElementById('orgEventDetail');
    detail.style.display = 'block';

    // Update URL without reload
    const url = new URL(window.location.href);
    url.searchParams.set('event', formId);
    url.searchParams.set('view', state.view);
    history.pushState({ event: formId, view: state.view }, '', url);

    // Stop the dashboard poll; we'll run our own focused on this event
    { const d = _dash(); if (d && d._stopPolling) d._stopPolling(); }

    _renderChrome();
    await _load();
    _startPolling();
  }

  function close() {
    if (state.pollTimer) { clearInterval(state.pollTimer); state.pollTimer = null; }
    if (state.liveTimer) { clearInterval(state.liveTimer); state.liveTimer = null; }
    document.getElementById('orgEventDetail').style.display = 'none';
    document.getElementById('orgContent').style.display = 'block';
    document.getElementById('summaryCards').style.display = 'flex';

    const url = new URL(window.location.href);
    url.searchParams.delete('event');
    url.searchParams.delete('view');
    history.pushState({}, '', url);

    state.formId = null;
    state.form = null;
    state.responses = [];

    // Resume dashboard polling, and refresh the events list now: its cards
    // were drawn before this event was opened, and the poll's first tick is
    // 30s away, so a guest added or changed here showed the old totals (#1090).
    { const d = _dash(); if (d && d._startPolling) d._startPolling(); if (d && d.loadDashboard) d.loadDashboard().catch(() => {}); }
  }

  function switchView(view) {
    state.view = view;
    const url = new URL(window.location.href);
    url.searchParams.set('view', view);
    history.replaceState({ event: state.formId, view }, '', url);
    _renderChrome();
    _renderActiveTab();
  }

  function _renderChrome() {
    const title = state.form?.title || 'Loading…';
    const eventDate = state.form?.eventDate || '';
    const eventLocation = state.form?.eventLocation || '';
    document.getElementById('eventDetailTitle').textContent = title;
    const meta = document.getElementById('eventDetailMeta');
    const parts = [];
    if (eventDate) parts.push(`<i class="bi bi-calendar3"></i> ${_esc(eventDate)}`);
    if (eventLocation) parts.push(`<i class="bi bi-geo-alt"></i> ${_esc(eventLocation)}`);
    meta.innerHTML = parts.join(' &nbsp;·&nbsp; ');

    // Top-right of the event, on every tab (task #1094), as on the regular
    // event dashboard: Copy guest link (the custom link when the event has
    // one, _generalLink) and Edit form. Preview waits on #1088.
    const actions = document.getElementById('eventDetailHeaderActions');
    if (actions) {
      const editHref = state.form ? _editFormHref() : '';
      actions.innerHTML = state.form ? `
        <button type="button" class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.copyGeneralLink()" title="${_esc(_generalLink())}">
          <i class="bi bi-link-45deg"></i> Copy guest link
        </button>
        ${_canEditEvent() && editHref ? `<a class="btn btn-org-outline btn-sm" href="${_esc(editHref)}" target="_blank" rel="noopener">
          <i class="bi bi-pencil-square"></i> Edit form
        </a>` : ''}` : '';
    }

    document.querySelectorAll('.event-detail-tab').forEach(t => {
      t.classList.toggle('active', t.dataset.view === state.view);
    });
  }

  // Task #862: the spinner is for the FIRST load of an event only. Every
  // approve, deny, delete or status change used to call this and blank the
  // whole tab to "Loading…" and back (dropping the scroll position too);
  // with the event already on screen it now refreshes underneath and redraws
  // in place.
  let _loadedFormId = null;
  async function _load() {
    const quiet = _loadedFormId === state.formId && !!state.form;
    if (!quiet) {
      state.loading = true;
      _seatingLoaded = false;
      _renderActiveTab();
    }
    if (quiet) {
      try { await _quietReload(); } catch (err) { _showToast('Could not refresh: ' + err.message, 'danger'); }
      return;
    }
    try {
      const [formData, responsesData] = await Promise.all([
        fetch(`/api/forms/${encodeURIComponent(state.formId)}`,
          { headers: { Authorization: `Bearer ${await window.firebaseAuth.currentUser.getIdToken()}` } })
          .then(r => r.ok ? r.json() : Promise.reject(new Error('form load failed'))),
        _fetchResponses({ Authorization: `Bearer ${await window.firebaseAuth.currentUser.getIdToken()}` })
          .then(r => r.ok ? r.json() : Promise.reject(new Error('responses load failed')))
      ]);
      state.form = formData.form || formData;
      state.responses = responsesData.responses || [];
      _loadedFormId = state.formId;
      // Kick off seating load in the background so the Seating tab is snappy
      // The Check-in tab says whether checking in seats people (#846), which
      // it only knows once seating has loaded.
      // The Responses tab's Table column (#856) appears once it has loaded too.
      _loadSeating().then(() => { if (state.view === 'checkin' || state.view === 'responses') _liveRender(); }).catch(() => {});
    } catch (err) {
      console.error('Failed to load event detail:', err);
      _showToast('Could not load event: ' + err.message, 'danger');
    } finally {
      state.loading = false;
      _renderChrome();
      _renderActiveTab();
    }
  }

  // ---- Live sync (task #853) -------------------------------------------------
  //
  // Check-ins at the door, table moves and seating edits made on ANY device
  // show here within a few seconds, on every tab, without a refresh. Every
  // LIVE_POLL_MS the page asks for the event's change counter (one tiny read,
  // GET /api/checkin/:formId/pulse); only when it has moved are the responses
  // and the seating chart fetched again. The slower POLL_MS refresh still
  // runs regardless, for things that do not move the counter (a new RSVP, an
  // edit made on the personal dashboard).
  //
  // A redraw never lands on someone mid-gesture: not while a cell, a column
  // name or the quick-add row is being typed in, a chip is being dragged or a
  // menu is open. It waits for the next tick instead. Search boxes keep focus
  // and caret across a redraw (see _renderActiveTab).
  const LIVE_POLL_MS = 5000;
  // The Check-in status picker (#894) is a filter like the searches: focus on
  // it must not pause live arrivals.
  const SEARCH_INPUT_IDS = new Set(['eventDetailSearch', 'checkinSearchTab', 'seatingSearchInput', 'checkinSort']);
  let _liveRev = null;
  let _liveBusy = false;
  let _livePendingRender = false;
  let _lastLocalWriteAt = 0;   // our own saves: don't let a read that left before them undo them on screen

  function _noteLocalWrite() { _lastLocalWriteAt = Date.now(); }

  function _stableJson(v) {
    if (Array.isArray(v)) return '[' + v.map(_stableJson).join(',') + ']';
    if (v && typeof v === 'object') {
      return '{' + Object.keys(v).sort().filter(k => v[k] !== undefined)
        .map(k => JSON.stringify(k) + ':' + _stableJson(v[k])).join(',') + '}';
    }
    return JSON.stringify(v === undefined ? null : v);
  }

  function _seatingSig(cfg) {
    const c = cfg || {};
    const rules = c.rules || {};
    return _stableJson({
      tables: Array.isArray(c.tables) ? c.tables : [],
      mode: c.assignmentMode || 'manual',
      keepTogether: rules.keepTogether || [],
      keepApart: rules.keepApart || [],
      groupByCompany: rules.groupByCompany === true
    });
  }

  function _liveRenderBlocked() {
    if (state.editing) return true;
    const body = document.getElementById('eventDetailBody');
    if (!body) return true;
    if (body.querySelector('.dragging, .dropdown-menu.show, .oed-col-rename-input')) return true;
    if (_ctm) return true; // the Check-in tab's table list is open (#871)
    const a = document.activeElement;
    if (a && body.contains(a) && a.matches('input, textarea, select, [contenteditable="true"]')
        && !SEARCH_INPUT_IDS.has(a.id)) return true;
    return false;
  }

  function _liveRender() {
    if (state.loading) return;
    // Outreach loads its own data on every render; nothing on it is live.
    if (state.view === 'outreach') { _livePendingRender = false; return; }
    if (_liveRenderBlocked()) { _livePendingRender = true; return; }
    _livePendingRender = false;
    // The door view is patched row by row, never rebuilt (#864).
    if (state.view === 'checkin') {
      const el = document.getElementById('eventDetailBody');
      if (el && _patchCheckin(el)) return;
    }
    _renderActiveTab();
  }

  // ---- Subscription gate (task #1018) ----------------------------------------
  // A workspace that never subscribed collects replies but cannot open them:
  // the server answers 402 ORG_SUBSCRIPTION_REQUIRED, and the Responses tab
  // says "subscribe" instead of "No responses match." (OrgDashboard decides
  // from the org the server sent, org.needsSubscription.)
  function _paywalled() {
    const d = _dash();
    return !!state.paywall || !!(d && d.needsSubscription && d.needsSubscription());
  }

  /** The event's responses, or an empty list without asking while the workspace waits for a subscription. */
  async function _fetchResponses(headers, formId = state.formId) {
    const empty = { ok: true, status: 200, json: async () => ({ responses: [] }) };
    if (_paywalled()) { state.paywall = true; return empty; }
    const r = await fetch(`/api/forms/${encodeURIComponent(formId)}/responses?limit=500`, { headers });
    if (r.status === 402) { state.paywall = true; return empty; }
    return r;
  }

  // A button that waits for the subscription: dimmed, and saying why.
  function _gatedAttrs(why) {
    return _paywalled() ? ` disabled title="${_esc(why)}"` : '';
  }

  function _paywallHtml() {
    return `<div class="event-detail-empty event-detail-paywall">
        <i class="bi bi-lock"></i>
        <p>In order to view any responses for your event, please subscribe to our monthly/annual plan</p>
        <button class="btn btn-org-primary btn-sm mt-2" type="button" onclick="OrgDashboard.openSubscribeModal()">
          <i class="bi bi-rocket-takeoff"></i> Subscribe here
        </button>
      </div>`;
  }

  // The event's shared link, as the server builds it (canonicalFormUrl): the
  // clean /<slug> once the link is live, else the form by id.
  function _generalLink() {
    const f = state.form || {};
    const slug = typeof f.slug === 'string' ? f.slug.trim() : '';
    return (slug && f.linkStatus === 'active' && /^[a-z0-9-]{3,50}$/i.test(slug))
      ? `${location.origin}/${slug}`
      : `${location.origin}/form.html?id=${encodeURIComponent(f.eventSlug || state.formId)}`;
  }

  // Nobody has replied yet (subscribed): the two ways to get replies.
  function _noRepliesYetHtml() {
    return `<div class="event-detail-empty event-detail-start">
        <i class="bi bi-inbox"></i>
        <p>No responses yet.</p>
        <div class="d-flex flex-wrap justify-content-center gap-2 mt-2">
          <button class="btn btn-org-primary btn-sm" type="button" onclick="OrgEventDetail.sendCampaignFromResponses()">
            <i class="bi bi-send"></i> Send campaign
          </button>
          ${_linkMenuHtml()}
        </div>
      </div>`;
  }

  /** An org member who may change the event (the events list's own rule). */
  function _canEditEvent() {
    const d = _dash();
    return !!d && ['admin', 'owner', 'editor'].includes(d.userRole);
  }

  /** The event's builder, in its language (OrgDashboard._getEditLink). */
  function _editFormHref(focus) {
    const d = _dash();
    const base = d && d._getEditLink
      ? d._getEditLink({ ...(state.form || {}), id: state.formId })
      : `/org-builder.html?formId=${encodeURIComponent(state.formId)}`;
    return focus ? `${base}&focus=${encodeURIComponent(focus)}` : base;
  }

  // "Copy link" with the event's QR beside it (task #1017 round 2): on the
  // empty Responses tab and in its toolbar, so the link and the QR for
  // posters stay one click away once replies come in. "Edit link…" (task
  // #1094) opens the builder at the custom-link box on the Share step - the
  // link's own menu, rather than one more button on a full toolbar.
  function _linkMenuHtml() {
    return `<div class="dropdown">
            <button class="btn btn-org-outline btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false"
                    title="Copy link, QR code, edit link" aria-label="Link">
              <i class="bi bi-link-45deg"></i><span class="oed-btn-label"> Copy link</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><button class="dropdown-item" type="button" onclick="OrgEventDetail.copyGeneralLink()">
                <i class="bi bi-link-45deg me-2"></i>Copy link</button></li>
              <li><button class="dropdown-item" type="button" onclick="OrgEventDetail.downloadEventQr()">
                <i class="bi bi-qr-code me-2"></i>Download QR</button></li>
              ${_canEditEvent() ? `<li><a class="dropdown-item" href="${_esc(_editFormHref('custom-link'))}" target="_blank" rel="noopener">
                <i class="bi bi-pencil me-2"></i>Edit link…</a></li>` : ''}
            </ul>
          </div>`;
  }

  async function copyGeneralLink() {
    const url = _generalLink();
    try { await navigator.clipboard.writeText(url); _showToast('Link copied', 'success'); }
    catch (_) { window.prompt('Copy this link', url); }
  }

  // "Send campaign" opens the new-campaign window on the Outreach tab.
  function sendCampaignFromResponses() {
    if (typeof switchView === 'function') switchView('outreach');
    newCampaign();
  }

  async function _liveRefresh() {
    const formId = state.formId;
    if (!formId || state.loading) return;
    const startedAt = Date.now();
    const token = await window.firebaseAuth.currentUser.getIdToken();
    const headers = { Authorization: `Bearer ${token}` };
    const [rr, sr] = await Promise.all([
      _fetchResponses(headers, formId),
      fetch(`/api/seating/${encodeURIComponent(formId)}`, { headers }).catch(() => null)
    ]);
    if (state.formId !== formId || !rr.ok) return;
    // One of our own saves went out after these reads did — they may predate
    // it. Skip this round; the save moved the counter, so the next one comes.
    if (_lastLocalWriteAt >= startedAt) { _liveRev = -1; return; }
    let changed = false;

    const next = (await rr.json()).responses || [];
    if (_stableJson(next) !== _stableJson(state.responses)) {
      state.responses = next;
      _pruneSelection();
      changed = true;
    }
    if (sr && sr.ok) {
      const sd = await sr.json();
      if (sd.success && sd.config && _seatingLoaded && _seatingSig(sd.config) !== _seatingSig(state.seating)) {
        state.seating = sd.config;
        _seatingState(); // normalize
        changed = true;
      } else if (sd.success && sd.config && _seatingLoaded && state.seating && sd.config.version !== undefined) {
        // Same chart, newer version number (a save elsewhere that changed
        // nothing visible): adopt it, or this dashboard's next save would be
        // refused as made on an old chart (#858). Nothing to redraw.
        state.seating.version = sd.config.version;
      }
    }
    if (changed || _livePendingRender) _liveRender();
  }

  // The refresh after one of the host's own actions (#862): the same data
  // _load fetches, redrawn in place — no spinner, scroll kept.
  async function _quietReload() {
    const formId = state.formId;
    const token = await window.firebaseAuth.currentUser.getIdToken();
    const headers = { Authorization: `Bearer ${token}` };
    const [fr, rr, sr] = await Promise.all([
      fetch(`/api/forms/${encodeURIComponent(formId)}`, { headers }),
      _fetchResponses(headers, formId),
      _seatingLoaded ? fetch(`/api/seating/${encodeURIComponent(formId)}`, { headers }).catch(() => null) : Promise.resolve(null)
    ]);
    if (state.formId !== formId) return;
    if (!fr.ok || !rr.ok) throw new Error('load failed');
    const formData = await fr.json();
    state.form = formData.form || formData;
    state.responses = (await rr.json()).responses || [];
    _pruneSelection();
    if (sr && sr.ok) {
      const sd = await sr.json();
      if (sd.success && sd.config) { state.seating = sd.config; _seatingState(); }
    }
    _seatIdxCache = null;
    _renderChrome();
    _renderActiveTab();
  }

  async function _livePulseTick() {
    if (document.visibilityState === 'hidden' || !state.formId || _liveBusy) return;
    if (_livePendingRender && !_liveRenderBlocked()) _liveRender();
    _liveBusy = true;
    try {
      const r = await fetch(`/api/checkin/${encodeURIComponent(state.formId)}/pulse`, { cache: 'no-store' });
      if (!r.ok) return;
      const { rev } = await r.json();
      if (typeof rev !== 'number') return;
      if (_liveRev === null) { _liveRev = rev; return; }
      if (rev !== _liveRev) {
        _liveRev = rev;
        await _liveRefresh();
      }
    } catch {
      // Offline or a blip: the next tick tries again.
    } finally {
      _liveBusy = false;
    }
  }

  function _startPolling() {
    if (state.pollTimer) return;
    _liveRev = null;
    state.pollTimer = setInterval(() => {
      if (document.visibilityState === 'hidden' || !state.formId || _liveBusy) return;
      _liveBusy = true;
      _liveRefresh().catch(() => {}).finally(() => { _liveBusy = false; });
    }, POLL_MS);
    state.liveTimer = setInterval(_livePulseTick, LIVE_POLL_MS);
    _livePulseTick();
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && state.formId && state.liveTimer) {
      // Back from another tab/app: catch up now rather than on the next tick.
      _liveRev = -1;
      _livePulseTick();
    }
  });

  function _renderActiveTab() {
    const el = document.getElementById('eventDetailBody');
    if (!el) return;
    _seatIdxCache = null; // seating may have moved since the last draw (#856)
    _answeredFn = null;   // a local edit may have answered a row (#1090)

    if (state.loading) {
      el.innerHTML = '<div class="text-center py-5"><div class="org-spinner"></div><p class="text-muted mt-2">Loading…</p></div>';
      return;
    }

    // Every re-render replaces the whole tab body, and with it the response
    // table's scroll container — so renaming a header, editing a cell or
    // changing a filter threw the host back to the first column of a table they
    // had scrolled well into (task #616). Carry the scroll position across.
    //
    // Only within the same table: on a tab switch the new body has no
    // spreadsheet wrap (or the old one didn't), so there is nothing to restore
    // and a stale offset can't leak in.
    const WRAP_SEL = '.event-detail-table-wrap.is-spreadsheet-wrap';
    const prevWrap = el.querySelector(WRAP_SEL);
    const keepScroll = prevWrap ? { left: prevWrap.scrollLeft, top: prevWrap.scrollTop } : null;
    // A search box is part of the markup being replaced, so typing in one (each
    // keystroke re-renders) or a live update (#853) used to drop the focus
    // after a single character. Put the cursor back where it was.
    const active = document.activeElement;
    const keepFocus = active && SEARCH_INPUT_IDS.has(active.id) && el.contains(active)
      ? { id: active.id, start: active.selectionStart, end: active.selectionEnd } : null;
    const restoreFocus = () => {
      if (!keepFocus) return;
      const input = document.getElementById(keepFocus.id);
      if (!input || input === document.activeElement) return;
      input.focus({ preventScroll: true });
      try { input.setSelectionRange(keepFocus.start, keepFocus.end); } catch (_) { /* type=search may refuse */ }
    };
    const restoreScroll = () => {
      restoreFocus();
      if (!keepScroll || (!keepScroll.left && !keepScroll.top)) return;
      const next = el.querySelector(WRAP_SEL);
      if (!next) return;
      next.scrollLeft = keepScroll.left;
      next.scrollTop = keepScroll.top;
    };

    if (state.view === 'responses') { _renderResponses(el); return restoreScroll(); }
    if (state.view === 'analytics') return _renderAnalytics(el);
    if (state.view === 'outreach') return _renderOutreach(el);
    if (state.view === 'seating') { _renderSeating(el); return restoreFocus(); }
    if (state.view === 'checkin') { _renderCheckin(el); return restoreFocus(); }
    _renderResponses(el);
    return restoreScroll();
  }

  // ============================================================================
  // RESPONSES
  // ============================================================================

  // Does this row hold a seat? An uploaded guest keeps the attendance the
  // import gave them (a blank cell was stored as 'yes' until 2026-10-11)
  // whether or not they have answered, so seating, check-in and name tags
  // plan for the whole list the host uploaded. The regular dashboard seats
  // them the same way, and the server's capacity gate counts them too.
  function _seatStatus(r) {
    // The response schema varies by form; prefer explicit attendance fields, fall
    // back to approval status for gated forms.
    const att = (r.attending || r.attendingStatus || r.status || '').toString().toLowerCase();
    if (r.approvalStatus === 'pending') return 'pending';
    if (r.waitlisted || att === 'waitlist') return 'waitlist';
    // Invited but not answered yet: a guest-list import (task #844) writes
    // attending 'pending'. Counted apart, never as attending.
    if (r.attending === 'pending') return 'awaiting';
    if (att === 'yes' || att === 'attending' || r.attending === true) return 'yes';
    if (att === 'no' || att === 'declined' || r.attending === false) return 'no';
    return 'yes';
  }

  // Has this guest ANSWERED? FeatureGates.answeredPredicate, the one rule the
  // events list (organization.service getOrgDashboard), the regular dashboard
  // and reminder audiences all use. It needs every row of the form (an old
  // upload's 'yes' counts as an answer only when its batch also holds a
  // 'no'/'maybe'), so it is built once per response list and dropped on every
  // redraw, when a local edit may have changed a row.
  let _answeredFn = null;
  let _answeredFor = null;
  function _isAnswered(r) {
    if (!window.FeatureGates || !window.FeatureGates.answeredPredicate) return true;
    if (!_answeredFn || _answeredFor !== state.responses) {
      _answeredFor = state.responses;
      _answeredFn = window.FeatureGates.answeredPredicate(state.responses);
    }
    return _answeredFn(r);
  }

  // The answer a host is SHOWN: the status pill, the counters, the filter,
  // the sort and the export. An uploaded guest nobody has heard from is
  // "Awaiting reply" (task #1090): the Responses header counted a 91-name
  // upload as 91 attending while the events list, by answeredPredicate,
  // counted 7, so the same event read 98 on one screen and 7 on the other.
  function _normalizeStatus(r) {
    const s = _seatStatus(r);
    if (s === 'pending' || s === 'waitlist' || s === 'awaiting') return s;
    return _isAnswered(r) ? s : 'awaiting';
  }

  // Shared resolver (assets/js/utils/guest-counts.js). `guestCount` is the
  // total party size INCLUDING the primary — do not add +1.
  function _partySize(r) {
    return resolveGuestCounts(r).total;
  }

  // `total` is the answers (every row but those awaiting a reply), the same
  // figure the events list shows as Total; the awaiting rows have their own
  // counter. `seatGuests` counts uploaded guests as seats, for the capacity
  // bar, which shows the number the server's capacity gate enforces.
  function _statsForResponses(list) {
    const stats = { total: 0, yes: 0, no: 0, pending: 0, waitlist: 0, awaiting: 0, guests: 0, seatGuests: 0 };
    list.forEach(r => {
      const s = _normalizeStatus(r);
      stats[s] = (stats[s] || 0) + 1;
      if (s !== 'awaiting') stats.total++;
      if (s === 'yes') stats.guests += _partySize(r);
      if (_seatStatus(r) === 'yes') stats.seatGuests += _partySize(r);
    });
    return stats;
  }

  function _filteredResponses() {
    const q = state.search.toLowerCase().trim();
    const list = state.responses.filter(r => {
      const status = _normalizeStatus(r);
      if (state.statusFilter !== 'all' && status !== state.statusFilter) return false;
      if (q) {
        const hay = `${r.guestName || ''} ${r.name || ''} ${r.email || ''} ${_phoneOf(r)} ${r.company || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return _sortResponses(list);
  }

  // ---- Column sorting (task #855) ------------------------------------------
  //
  // Click a header to sort ascending, again for descending, a third time to go
  // back to the order the replies came in. Applied inside _filteredResponses,
  // so the table, its row numbers and Export CSV all follow what is on screen.
  // Held for the open event only: a sort is a way of looking, not a setting.
  // Blank cells sink to the bottom in both directions — a column sorted to find
  // who is missing a phone is read from the top, either way round.
  const UNSORTABLE_COLUMN_KEYS = new Set(['_rownum', '_select', '_actions']);
  const STATUS_SORT_RANK = { yes: 0, pending: 1, waitlist: 2, awaiting: 3, no: 4 };
  const _sortCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  function _sortValue(r, col) {
    if (col.key === 'status') return STATUS_SORT_RANK[_normalizeStatus(r)] ?? 9;
    if (col.key === '_total') return _partySize(r);
    if (col.key === 'submittedAt') {
      const d = _toDate(r.submittedAt || r.createdAt);
      return d ? d.getTime() : null;
    }
    if (col.key === '_checkin') {
      const d = r.checkedInAt ? _toDate(r.checkedInAt) : null;
      return d ? d.getTime() : null;
    }
    const v = _readCell(r, col);
    if (v == null) return null;
    if (typeof v === 'number') return v;
    const s = String(v).trim();
    return s === '' ? null : s;
  }

  function _sortResponses(list) {
    const sort = state.sort;
    if (!sort) return list;
    const col = _allPossibleColumns().find(c => c.key === sort.key);
    if (!col) return list;
    const dir = sort.dir === 'desc' ? -1 : 1;
    return list
      .map((r, i) => ({ r, i, v: _sortValue(r, col) }))
      .sort((a, b) => {
        const aBlank = a.v == null, bBlank = b.v == null;
        if (aBlank || bBlank) return (aBlank === bBlank) ? a.i - b.i : (aBlank ? 1 : -1);
        const c = (typeof a.v === 'number' && typeof b.v === 'number')
          ? a.v - b.v
          : _sortCollator.compare(String(a.v), String(b.v));
        return c ? c * dir : a.i - b.i;
      })
      .map(x => x.r);
  }

  function sortBy(key) {
    if (UNSORTABLE_COLUMN_KEYS.has(key)) return;
    const cur = state.sort;
    if (!cur || cur.key !== key) state.sort = { key, dir: 'asc' };
    else if (cur.dir === 'asc') state.sort = { key, dir: 'desc' };
    else state.sort = null;
    _renderActiveTab();
  }

  // Task #839: the event's own approval queue, at the top of its Responses
  // tab. It used to be one list across every event on the Events page, away
  // from the event's numbers and capacity. Shown only while someone is
  // waiting; the table's Pending filter and row checkboxes still work too.
  const APPROVALS_SHOWN = 25;
  function _approvalsPanelHtml() {
    const pending = state.responses.filter(r => _normalizeStatus(r) === 'pending');
    if (!pending.length) return '';
    const att = (r) => {
      const a = (r.attending || r.attendingStatus || r.status || '').toString().toLowerCase();
      const yes = !(a === 'no' || a === 'declined' || r.attending === false);
      const n = yes ? _partySize(r) : 0;
      return yes ? `Attending${n ? ` · ${n} guest${n === 1 ? '' : 's'}` : ''}` : 'Not attending';
    };
    const rows = pending.slice(0, APPROVALS_SHOWN).map(r => `
        <div class="approval-row">
          <div class="approval-row-info">
            <div class="approval-row-name">${_esc(r.guestName || r.name || 'Anonymous')}</div>
            <div class="approval-row-meta">
              ${r.email ? `<span>${_esc(r.email)}</span>` : ''}
              <span class="approval-row-tag">${_esc(att(r))}</span>
            </div>
          </div>
          <div class="approval-row-actions">
            <button class="btn btn-org-ghost btn-sm" onclick="OrgEventDetail.approve('${_esc(r.id)}')" title="Approve" aria-label="Approve"><i class="bi bi-check2"></i></button>
            <button class="btn btn-org-ghost btn-sm" onclick="OrgEventDetail.deny('${_esc(r.id)}')" title="Deny" aria-label="Deny"><i class="bi bi-x"></i></button>
          </div>
        </div>`).join('');
    const more = pending.length > APPROVALS_SHOWN
      ? `<p class="panel-hint mb-0 mt-2">and ${pending.length - APPROVALS_SHOWN} more - <a href="#" onclick="OrgEventDetail.showPendingInTable();return false;">show them all in the table</a></p>`
      : '';
    const collapsed = state.approvalsCollapsed;
    return `
      <div class="management-panel event-approvals mb-3${collapsed ? ' is-collapsed' : ''}" id="eventApprovalsPanel">
        <div class="panel-header">
          <h5><button type="button" class="event-approvals-toggle" onclick="OrgEventDetail.toggleApprovals()" aria-expanded="${collapsed ? 'false' : 'true'}" aria-controls="eventApprovalsBody" title="${collapsed ? 'Show' : 'Hide'} the waiting list"><i class="bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-down'} event-approvals-chevron"></i><i class="bi bi-clock-history me-2"></i>Waiting for your approval <span class="approval-badge ms-2">${pending.length}</span></button></h5>
          <div class="d-flex gap-2">
            <button class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.approveAllPending()"><i class="bi bi-check2-all"></i> Approve all</button>
            <button class="btn btn-org-ghost btn-sm" onclick="OrgEventDetail.denyAllPending()"><i class="bi bi-x"></i> Deny all</button>
          </div>
        </div>
        <div class="panel-body" id="eventApprovalsBody"${collapsed ? ' hidden' : ''}>${rows}${more}</div>
      </div>`;
  }

  // Fold/unfold in place: re-rendering the tab would reset the table's scroll.
  function toggleApprovals() {
    state.approvalsCollapsed = !state.approvalsCollapsed;
    try { localStorage.setItem('orgEventDetail.approvalsCollapsed', state.approvalsCollapsed ? '1' : '0'); } catch {}
    const panel = document.getElementById('eventApprovalsPanel');
    if (!panel) return;
    const c = state.approvalsCollapsed;
    panel.classList.toggle('is-collapsed', c);
    const body = document.getElementById('eventApprovalsBody');
    if (body) body.hidden = c;
    const btn = panel.querySelector('.event-approvals-toggle');
    if (btn) {
      btn.setAttribute('aria-expanded', c ? 'false' : 'true');
      btn.title = (c ? 'Show' : 'Hide') + ' the waiting list';
    }
    const chev = panel.querySelector('.event-approvals-chevron');
    if (chev) chev.className = `bi ${c ? 'bi-chevron-right' : 'bi-chevron-down'} event-approvals-chevron`;
  }

  // Task #844: the event dashboard's Import Guests wizard
  // (response-import-wizard.js), pointed at this event. Both kinds of list
  // work: collected RSVPs, and a guest list that shows as "Awaiting reply".
  function openImport() {
    if (typeof window.openImportResponsesModal !== 'function') {
      _showToast('Import is not available right now. Please refresh the page.', 'danger');
      return;
    }
    window.openImportResponsesModal({
      formId: state.formId,
      formData: state.form,
      getResponses: () => state.responses,
      reload: () => _load(),
      paid: true,   // corporate includes every organizing tool; no upsell
      seating: true, // a Table column seats guests (members hold the seating permission)
      // The Invite-only switch on the guest-list option saves straight to the
      // form, as the corporate builder's own switch does (#844 round 2). A
      // member who may not edit the form gets the wizard's "could not change"
      // message here instead of a silent revert.
      toast: (msg, type) => _showToast(msg, type === 'error' ? 'danger' : type)
    });
  }

  function showPendingInTable() {
    state.statusFilter = 'pending';
    _renderActiveTab();
  }

  async function _decideAllPending(action) {
    const ids = state.responses.filter(r => _normalizeStatus(r) === 'pending').map(r => r.id);
    if (!ids.length) return;
    const verb = action === 'approve' ? 'Approve' : 'Deny';
    if (!confirm(`${verb} all ${ids.length} waiting response${ids.length === 1 ? '' : 's'}?`)) return;
    await _bulkDecide(action, ids);
  }
  function approveAllPending() { return _decideAllPending('approve'); }
  function denyAllPending() { return _decideAllPending('deny'); }

  /**
   * An unsubscribed workspace's counts for this event (task #1068). The guest
   * list itself is refused (402), but the events list the dashboard already
   * loaded carries each event's totals, so the counters can show that replies
   * have arrived - blurred - without a single name reaching the browser.
   */
  function _paywallStats() {
    const d = _dash();
    const f = ((d && d.dashboardData && d.dashboardData.forms) || []).find(x => x.id === state.formId);
    const s = (f && f.stats) || {};
    return {
      total: (f && f.responseCount) || 0,
      yes: s.attending || 0,
      no: s.notAttending || 0,
      guests: s.totalGuests || 0,
      pending: 0, waitlist: 0, awaiting: 0
    };
  }

  /** Placeholder rows drawn blurred under the Subscribe card - none of it real. */
  function _paywallRowsHtml(count) {
    const rows = Array.from({ length: Math.min(Math.max(count, 1), 8) }, (_, i) => `
      <tr><td>Guest name ${i + 1}</td><td>guest${i + 1}@example.com</td><td>+1 555 0100</td><td>${i % 3 === 2 ? 'Declined' : 'Attending'}</td></tr>`).join('');
    return `<div class="responses-paywall-rows" aria-hidden="true">
        <table class="table mb-0 org-table"><thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody></table>
      </div>`;
  }

  function _renderResponses(el) {
    const gatedStats = _paywalled() ? _paywallStats() : null;
    // Counters blur only when there is a number to hide (task #1068).
    const blurStat = gatedStats && gatedStats.total > 0 ? ' is-blurred' : '';
    const stats = gatedStats || _statsForResponses(state.responses);
    const filtered = _filteredResponses();

    const selectedCount = state.selected.size;
    // Task #850: the bar offers what fits the selection. Approve / Deny are
    // decisions on pending or waitlisted RSVPs and only appear when one is
    // selected; settled guests get a status change instead; Delete is always
    // there.
    const selRows = _selectedRows();
    const needsDecision = (r) => ["pending", "waitlist"].includes(_normalizeStatus(r));
    const anyDecision = selRows.some(needsDecision);
    const anySettled = selRows.some((r) => !needsDecision(r));
    const bulkBar = selectedCount > 0 ? `
      <div class="event-detail-bulk">
        <span><strong>${selectedCount}</strong> selected</span>
        ${anyDecision ? `
        <button class="btn btn-org-primary btn-sm" onclick="OrgEventDetail.bulkApprove()"><i class="bi bi-check2"></i> Approve</button>
        <button class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.bulkDeny()"><i class="bi bi-x"></i> Deny</button>` : ''}
        ${anySettled ? `
        <button class="btn btn-org-ghost btn-sm" onclick="OrgEventDetail.bulkSetStatus('yes')"><i class="bi bi-check2-circle"></i> Mark attending</button>
        <button class="btn btn-org-ghost btn-sm" onclick="OrgEventDetail.bulkSetStatus('no')"><i class="bi bi-slash-circle"></i> Mark declined</button>` : ''}
        <button class="btn btn-org-ghost btn-sm" onclick="OrgEventDetail.exportSelected()"><i class="bi bi-download"></i> Export</button>
        <button class="btn btn-org-ghost btn-sm oed-bulk-delete" onclick="OrgEventDetail.bulkDelete()"><i class="bi bi-trash"></i> Delete</button>
        <button class="btn btn-org-ghost btn-sm" onclick="OrgEventDetail.clearSelection()">Clear</button>
      </div>` : '';

    const capacity = parseInt(state.form?.maxResponses || state.form?.capacity || 0, 10) || 0;
    const capacityBar = capacity > 0 ? _capacityBar(stats.seatGuests ?? stats.guests, capacity) : '';

    el.innerHTML = `
      <div class="event-detail-stats">
        <div class="event-detail-stat${blurStat}"><span class="value">${stats.total}</span><span class="label">Total</span></div>
        <div class="event-detail-stat stat-yes${blurStat}"><span class="value">${stats.yes}</span><span class="label">Attending</span></div>
        <div class="event-detail-stat stat-guests${blurStat}"><span class="value">${stats.guests}</span><span class="label">Guests (incl. +1s)</span></div>
        <div class="event-detail-stat stat-no${blurStat}"><span class="value">${stats.no}</span><span class="label">Declined</span></div>
        ${stats.pending ? `<div class="event-detail-stat stat-pending"><span class="value">${stats.pending}</span><span class="label">Pending</span></div>` : ''}
        ${stats.waitlist ? `<div class="event-detail-stat stat-waitlist"><span class="value">${stats.waitlist}</span><span class="label">Waitlist</span></div>` : ''}
        ${stats.awaiting ? `<div class="event-detail-stat stat-awaiting"><span class="value">${stats.awaiting}</span><span class="label">Awaiting reply</span></div>` : ''}
      </div>

      ${_approvalsPanelHtml()}

      ${capacityBar}

      <div class="event-detail-toolbar is-responses">
        <div class="event-detail-toolbar-left">
          <input type="search" class="form-control form-control-sm oed-responses-search" id="eventDetailSearch" value="${_esc(state.search)}"
                 placeholder="Search by name, email, phone, company…">
          <select class="form-select form-select-sm oed-responses-status" id="eventDetailStatusFilter">
            <option value="all" ${state.statusFilter === 'all' ? 'selected' : ''}>All statuses</option>
            <option value="yes" ${state.statusFilter === 'yes' ? 'selected' : ''}>Attending</option>
            <option value="no" ${state.statusFilter === 'no' ? 'selected' : ''}>Declined</option>
            <option value="pending" ${state.statusFilter === 'pending' ? 'selected' : ''}>Pending</option>
            <option value="waitlist" ${state.statusFilter === 'waitlist' ? 'selected' : ''}>Waitlist</option>
            <option value="awaiting" ${state.statusFilter === 'awaiting' ? 'selected' : ''}>Awaiting reply</option>
          </select>
          <!-- Task #495: enter a guest who is already confirmed by other means —
               a phone call, a reply-all, a colleague's list — without sending
               them through the public form. -->
          <button class="btn btn-org-primary btn-sm" onclick="OrgEventDetail.openAddGuest()"${_gatedAttrs('Subscribe to add guests')}>
            <i class="bi bi-person-plus"></i> Add guest
          </button>
          <!-- Task #844: the event dashboard's CSV import, same wizard. -->
          <button class="btn btn-org-outline btn-sm" type="button" onclick="OrgEventDetail.openImport()"
                  ${_paywalled() ? _gatedAttrs('Subscribe to import guests') : 'title="Import guests from a CSV file"'}>
            <i class="bi bi-upload"></i> Import
          </button>
        </div>
        <div class="event-detail-toolbar-right">
          ${state.responses.some(r => Array.isArray(r.additionalGuestDetails) && r.additionalGuestDetails.length)
            ? `<button class="btn btn-org-outline btn-sm${state.expandAttendees ? ' active' : ''}" type="button"
                    id="eventDetailExpandAttendees" aria-pressed="${state.expandAttendees}"
                    title="${state.expandAttendees ? 'Hide the additional attendees, one row per registrant' : 'Show each additional attendee on their own row'}">
                <i class="bi ${state.expandAttendees ? 'bi-toggle-on' : 'bi-toggle-off'}" aria-hidden="true"></i>
                <span>Show additional attendees</span>
              </button>`
            : ''}
          <div class="dropdown event-detail-columns-dropdown">
            <button class="btn btn-org-outline btn-sm dropdown-toggle" type="button"
                    data-bs-toggle="dropdown" data-bs-auto-close="outside"
                    title="Add or hide columns" aria-label="Columns">
              <i class="bi bi-layout-three-columns"></i>
              <span class="oed-btn-label">Columns</span>
              <span class="event-detail-columns-badge" id="columnsHiddenBadge" hidden></span>
            </button>
            <div class="dropdown-menu dropdown-menu-end event-detail-columns-menu" id="columnsMenu"></div>
          </div>
          ${_linkMenuHtml()}
          <button class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.exportAll()" aria-label="Export CSV"${_paywalled() ? _gatedAttrs('Subscribe to export responses') : ' title="Export CSV"'}><i class="bi bi-download"></i><span class="oed-btn-label"> Export CSV</span></button>
          <!-- Task #1083: a print-ready PDF of name tags (org-name-tags.js). -->
          <button class="btn btn-org-outline btn-sm" type="button" onclick="OrgEventDetail.openNameTags()"
                  aria-label="Name tags" ${_paywalled() ? _gatedAttrs('Subscribe to print name tags') : 'title="Print name tags for your guests"'}>
            <i class="bi bi-person-badge"></i><span class="oed-btn-label"> Name tags</span>
          </button>
          <!-- Task #614: the read-only viewer link was only reachable from the
               events list, so grabbing it meant leaving the event you were
               working in. Same modal, opened from here. -->
          <button class="btn btn-org-outline btn-sm" type="button"
                  onclick="OrgEventDetail.showViewerLink()" aria-label="Viewer Link"
                  ${_paywalled() ? _gatedAttrs('Subscribe to share a viewer link') : 'title="Read-only link to this event’s responses, for stakeholders"'}>
            <i class="bi bi-eye"></i>
            <span class="oed-btn-label">Viewer Link</span>
          </button>
        </div>
      </div>

      ${bulkBar}

      ${_paywalled() ? (gatedStats.total > 0
          ? `<div class="responses-paywall">${_paywallRowsHtml(gatedStats.total)}${_paywallHtml()}</div>`
          : _paywallHtml())
        : !state.responses.length ? _noRepliesYetHtml()
        : filtered.length === 0
        ? `<div class="event-detail-empty"><i class="bi bi-inbox"></i><p>No responses match.</p></div>`
        : _renderSpreadsheet(filtered)}
    `;

    // Wire handlers
    const search = document.getElementById('eventDetailSearch');
    if (search) search.addEventListener('input', (e) => { state.search = e.target.value; _renderActiveTab(); });
    const statusFilter = document.getElementById('eventDetailStatusFilter');
    if (statusFilter) statusFilter.addEventListener('change', (e) => { state.statusFilter = e.target.value; _renderActiveTab(); });

    const expandToggle = document.getElementById('eventDetailExpandAttendees');
    if (expandToggle) expandToggle.addEventListener('click', () => {
      state.expandAttendees = !state.expandAttendees;
      _persistExpandAttendees();
      _renderActiveTab();
    });

    // Task #1125: the header box shows the selection (all / some / none of
    // the rows on screen). Ticking it from empty selects every row shown;
    // from all or some it clears the selection, exactly like the bar's Clear.
    // The click is read from the state it was drawn with, not e.target.checked,
    // because a browser ticks an indeterminate box on click.
    const selectAll = document.getElementById('eventDetailSelectAll');
    if (selectAll) {
      selectAll.indeterminate = selectAll.dataset.selState === 'some';
      selectAll.addEventListener('change', () => {
        if (selectAll.dataset.selState === 'none') {
          filtered.forEach(r => state.selected.add(r.id));
          _renderActiveTab();
        } else {
          clearSelection();
        }
      });
    }
    el.querySelectorAll('.event-detail-row-check').forEach(cb => {
      cb.addEventListener('change', (e) => {
        const id = e.target.value;
        if (e.target.checked) state.selected.add(id);
        else state.selected.delete(id);
        _renderActiveTab();
      });
    });

    _renderColumnsMenu();
    _wireSpreadsheetEdits(el);
  }

  // Always-visible columns the user can't hide — these are functionally
  // necessary (row selection, identity, actions).
  const REQUIRED_COLUMN_KEYS = new Set(['_rownum', '_select', 'guestName', '_actions']);

  // Task #899: ticking a column redraws the tab - toolbar, menu and all - so
  // the menu used to close after every click. Open the new one again, scrolled
  // where it was, with focus back on the box just ticked, so several columns
  // can be switched in one go. It still closes on a click outside.
  function _reopenColumnsMenu(scrollTop, key) {
    const menu = document.getElementById('columnsMenu');
    const toggle = menu && menu.parentElement && menu.parentElement.querySelector('[data-bs-toggle="dropdown"]');
    if (!toggle || !window.bootstrap || !window.bootstrap.Dropdown) return;
    window.bootstrap.Dropdown.getOrCreateInstance(toggle).show();
    menu.scrollTop = scrollTop || 0;
    const box = [...menu.querySelectorAll('input[data-col-key]')].find(i => i.dataset.colKey === key);
    if (box) box.focus({ preventScroll: true });
  }

  function _renderColumnsMenu() {
    const menu = document.getElementById('columnsMenu');
    if (!menu) return;
    const cols = _allPossibleColumns().filter(c => !REQUIRED_COLUMN_KEYS.has(c.key));
    const hiddenCount = cols.reduce((n, c) => n + (state.hiddenColumns.has(c.key) ? 1 : 0), 0);

    const items = cols.map(c => {
      const hidden = state.hiddenColumns.has(c.key);
      // The handle moves the column (task #1126): drag it, or focus it and
      // use the arrow keys. It sits inside the label, so its own clicks are
      // kept from ticking the box.
      return `
        <label class="dropdown-item event-detail-columns-item" data-col-key="${_esc(c.key)}">
          <span class="oed-col-grip" data-col-key="${_esc(c.key)}" tabindex="0" role="button"
                title="Drag to move this column" aria-label="Move ${_esc(c.label)} (arrow keys)"><i class="bi bi-grip-vertical" aria-hidden="true"></i></span>
          <input type="checkbox" data-col-key="${_esc(c.key)}" ${hidden ? '' : 'checked'}>
          <span>${_esc(c.label)}</span>
        </label>`;
    }).join('');

    menu.innerHTML = `
      <div class="event-detail-columns-hint">Check to add, uncheck to hide. Drag <i class="bi bi-grip-vertical" aria-hidden="true"></i> to reorder.</div>
      ${items || '<div class="text-muted small px-2 py-1">No optional columns</div>'}
      <div class="dropdown-divider"></div>
      <button type="button" class="dropdown-item event-detail-columns-reset">
        <i class="bi bi-arrow-counterclockwise"></i> Show all columns
      </button>`;

    // Update the badge on the toolbar button so the user can see how many
    // columns are currently hidden — the "add back" affordance.
    const badge = document.getElementById('columnsHiddenBadge');
    if (badge) {
      if (hiddenCount > 0) {
        badge.textContent = `${hiddenCount} hidden`;
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }

    menu.querySelectorAll('input[data-col-key]').forEach(cb => {
      cb.addEventListener('change', (e) => {
        const key = e.target.dataset.colKey;
        if (e.target.checked) state.hiddenColumns.delete(key);
        else                  state.hiddenColumns.add(key);
        _persistHiddenColumns();
        const scrollTop = menu.scrollTop;
        _renderActiveTab();
        _reopenColumnsMenu(scrollTop, key);
      });
    });
    menu.querySelector('.event-detail-columns-reset')?.addEventListener('click', () => {
      state.hiddenColumns.clear();
      _persistHiddenColumns();
      // Reset means reset: widths and renamed headers go back too (task #616).
      // This is where a host looks when the table has got away from them, and
      // hunting for each dragged divider individually is not a recovery.
      // The column order too (task #1126).
      const p = _colPrefs();
      p.widths = {};
      p.labels = {};
      p.order = [];
      _persistColPrefs();
      _renderActiveTab();
    });
    _wireColumnsMenuMoves(menu);
  }

  // Reorder from the Columns menu (task #1126). Pointer events, so a finger
  // on a phone drags the handle as well as a mouse does.
  function _wireColumnsMenuMoves(menu) {
    const done = (key, focusGrip) => {
      const scrollTop = menu.scrollTop;
      _renderActiveTab();
      _reopenColumnsMenu(scrollTop, key);
      if (focusGrip) {
        const g = [...document.querySelectorAll('#columnsMenu .oed-col-grip')].find(x => x.dataset.colKey === key);
        if (g) g.focus();
      }
    };
    menu.querySelectorAll('.oed-col-grip').forEach(grip => {
      const key = grip.dataset.colKey;
      grip.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); });
      grip.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        e.stopPropagation();   // Bootstrap's dropdown moves focus on arrows
        const keys = [...menu.querySelectorAll('.oed-col-grip')].map(g => g.dataset.colKey);
        const i = keys.indexOf(key);
        const j = e.key === 'ArrowUp' ? i - 1 : i + 1;
        if (j < 0 || j >= keys.length) return;
        if (_moveColumn(key, keys[j], e.key === 'ArrowDown')) done(key, true);
      });
      grip.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        const item = grip.closest('.event-detail-columns-item');
        item.classList.add('is-col-dragged');
        let drop = null;
        const clear = () => document.querySelectorAll('#columnsMenu .is-drop-before, #columnsMenu .is-drop-after')
          .forEach(x => x.classList.remove('is-drop-before', 'is-drop-after'));
        const onMove = (ev) => {
          ev.preventDefault();
          const liveMenu = document.getElementById('columnsMenu') || menu;   // a live update may redraw it
          const m = liveMenu.getBoundingClientRect();
          if (ev.clientY < m.top + 24) liveMenu.scrollTop -= 10;
          else if (ev.clientY > m.bottom - 24) liveMenu.scrollTop += 10;
          const rows = [...liveMenu.querySelectorAll('.event-detail-columns-item')];
          let hit = rows.find(r => { const b = r.getBoundingClientRect(); return ev.clientY < b.top + b.height / 2; });
          const after = !hit;
          if (!hit) hit = rows[rows.length - 1];
          clear();
          if (!hit || hit.dataset.colKey === key) { drop = null; return; }
          hit.classList.add(after ? 'is-drop-after' : 'is-drop-before');
          drop = { key: hit.dataset.colKey, after };
        };
        const onUp = (ev) => {
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
          document.removeEventListener('pointercancel', onUp);
          clear();
          item.classList.remove('is-col-dragged');
          if (drop && ev.type !== 'pointercancel' && _moveColumn(key, drop.key, drop.after)) done(key, false);
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
      });
    });
  }

  // ============================================================================
  // SPREADSHEET — wide editable response grid
  // ============================================================================

  // The full universe of columns this form could have. Every field the form
  // collects gets a column by default — empty cells are fine if the form
  // doesn't gather a given field; the user can hide unwanted columns via the
  // Columns menu and bring them back the same way. Custom fields come straight
  // from the form schema. The checkbox column keeps an explicit width: the
  // sticky Name offset is anchored to it.
  //
  // Note: Adults/Children columns are intentionally absent. The org dashboard
  // is a corporate context — events here track individual professional
  // attendance, not family party size. Total computes anyway from any
  // numberOfAdults/numberOfChildren in the data, in case a form collects them.
  /**
   * Does this built-in detail column belong to THIS event?
   *
   * Task #625 round 3 — "I didn't add Transport or Department to my form, so it
   * shouldn't show up when adding a guest manually and it also shouldn't be
   * there when I export … in general, only fields that are included in the RSVP
   * form should be there." The sheet used to carry all six on every event,
   * which is why the export and the Add-guest dialog offered questions the form
   * had never asked.
   *
   * Company / job title / department / dietary are real questions and follow the
   * form's own toggles. Transport and Notes are the sheet's host-kept columns —
   * no guest ever sees them, so no toggle can switch them on — and they now show
   * only on an event that already has something in them. That keeps an older
   * list that used them readable and exportable without offering them to
   * everyone else.
   */
  const DETAIL_COLUMN_KEYS = ['company', 'jobTitle', 'department', 'dietary', 'transportation', 'notes', 'heardAboutUs'];

  function _detailColumnOn(key) {
    const asked = {
      company: state.form?.includeCompany === true,
      jobTitle: state.form?.includeJobTitle === true,
      department: state.form?.includeDepartment === true,
      // Default true, matching every other reader of this flag.
      dietary: state.form?.includeDietary !== false
    };
    if (asked[key]) return true;
    // Never hide data that is already there.
    return (state.responses || []).some((r) => {
      const v = _readCell(r, { key });
      return v != null && String(v).trim() !== '';
    });
  }

  function _allPossibleColumns() {
    const cols = [
      // Task #624: a plain running count down the left edge, for cross-checking
      // a delegate list against a badge run or a security sheet. Required, so
      // it cannot be hidden from the columns menu and cannot be edited.
      { key: '_rownum', label: '#', type: 'rownum', width: 44, sticky: 1 },
      { key: '_select', label: '', type: 'checkbox', width: 44, sticky: 2 },
      { key: 'guestName', label: 'Name', type: 'text', sticky: 3, editable: true },
      { key: 'status', label: 'Status', type: 'status', editable: true },
      { key: '_total', label: 'Total', type: 'computed' },
      // Where the guest sits (task #856), read off the seating chart. Only on an
      // event that has tables; it updates live with the chart (#853).
      ...(_seatingHasTables() ? [{ key: '_table', label: 'Table', type: 'computed' }] : []),
      // When each guest arrived (task #904). Appears with the first check-in,
      // and live with the door like the Table column.
      ...(_checkinStarted() ? [{ key: '_checkin', label: 'Check-in', type: 'computed', multiDay: _checkinMultiDay() }] : []),
      { key: 'email', label: 'Email', type: 'email', editable: true },
      { key: 'phone', label: 'Phone', type: 'text', editable: true },
      { key: 'company',        label: 'Company',    type: 'text', editable: true },
      { key: 'jobTitle',       label: 'Job title',  type: 'text', editable: true },
      { key: 'department',     label: 'Department', type: 'text', editable: true },
      { key: 'dietary',        label: 'Dietary',    type: 'text', editable: true },
      { key: 'transportation', label: 'Transport',  type: 'text', editable: true },
      { key: 'notes',          label: 'Notes',      type: 'text', editable: true },
      // Asked of walk-ins at the door (#906); shows once anyone has an answer.
      { key: 'heardAboutUs',   label: 'Heard about us', type: 'text', editable: true }
    ].filter(c => !DETAIL_COLUMN_KEYS.includes(c.key) || _detailColumnOn(c.key));

    (state.form?.customFields || []).forEach(f => {
      if (!f || f.type === 'message') return; // display-only field — nothing to edit
      cols.push({
        key: `cf:${f.id}`,
        label: _columnLabel(`cf:${f.id}`, f.label || f.id),
        originalLabel: f.label || f.id,
        type: 'text',
        // Image fields hold a URL, not editable text — rendered as a thumbnail
        // + download link (read-only) instead of an editable cell.
        editable: f.type !== 'image',
        customFieldId: f.id,
        customFieldType: f.type,
        customFieldPerGuest: f.perGuestQuantity === true,
        // The question's own choices, in the order the host wrote them. Used to
        // give every choice a stable colour (task #623) and to offer them as a
        // real dropdown rather than a free-text box.
        customFieldOptions: Array.isArray(f.options)
          ? f.options.map(o => (o && typeof o === 'object' ? (o.label ?? o.value ?? '') : o))
              .map(o => String(o == null ? '' : o).trim()).filter(Boolean)
          : []
      });
    });

    cols.push({ key: 'submittedAt', label: 'Submitted', type: 'date' });
    cols.push({ key: '_actions',    label: '',          type: 'actions' });
    return _applyColumnOrder(cols);
  }

  // ---- Column order (task #1126) ------------------------------------------
  // Dragged on a header or in the Columns menu, kept per event with the widths
  // and names above. #, the checkbox and Name stay first and the row actions
  // last; everything between moves. Applied where the list is built, so the
  // table, the menu and Export CSV all follow it.
  const PINNED_FIRST_KEYS = new Set(['_rownum', '_select', 'guestName']);
  function _isMovableColumn(key) {
    return !PINNED_FIRST_KEYS.has(key) && key !== '_actions';
  }

  function _applyColumnOrder(cols) {
    const order = _colPrefs().order;
    if (!order.length) return cols;
    const movable = cols.filter(c => _isMovableColumn(c.key));
    const byKey = new Map(movable.map(c => [c.key, c]));
    const out = order.filter(k => byKey.has(k)).map(k => byKey.get(k));
    const placed = new Set(out.map(c => c.key));
    // A column the saved order has never seen (a new question, Table once the
    // event has tables) goes after the column it normally follows.
    movable.forEach((c, i) => {
      if (placed.has(c.key)) return;
      let at = 0;
      for (let j = i - 1; j >= 0; j--) {
        const k = out.findIndex(o => o.key === movable[j].key);
        if (k >= 0) { at = k + 1; break; }
      }
      out.splice(at, 0, c);
      placed.add(c.key);
    });
    return [...cols.filter(c => PINNED_FIRST_KEYS.has(c.key)), ...out, ...cols.filter(c => c.key === '_actions')];
  }

  /** Move column `key` next to `targetKey` (before it, or after it). */
  function _moveColumn(key, targetKey, after) {
    const keys = _allPossibleColumns().map(c => c.key).filter(_isMovableColumn);
    if (!keys.includes(key) || !keys.includes(targetKey) || key === targetKey) return false;
    const was = keys.join('|');
    keys.splice(keys.indexOf(key), 1);
    keys.splice(keys.indexOf(targetKey) + (after ? 1 : 0), 0, key);
    if (keys.join('|') === was) return false;
    _colPrefs().order = keys;
    _persistColPrefs();
    return true;
  }

  // Drag a header sideways to move its column. Mouse and pen only: on a touch
  // screen a sideways swipe on the header scrolls the table, so phones use the
  // handles in the Columns menu instead. A press that does not travel stays a
  // click, so sorting and double-click-to-rename work as before.
  function _wireColumnMoves(root) {
    const table = root.querySelector('.event-detail-table.is-spreadsheet');
    const wrap = root.querySelector('.event-detail-table-wrap.is-spreadsheet-wrap');
    if (!table || !wrap) return;
    table.querySelectorAll('thead th.is-movable').forEach(th => {
      th.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 || e.pointerType === 'touch') return;
        if (e.target.closest('.oed-col-resizer, .oed-col-rename, input')) return;
        const key = th.dataset.col;
        const startX = e.clientX;
        let dragging = false;
        let drop = null;
        const line = document.createElement('div');
        line.className = 'oed-col-drop-line';
        const onMove = (ev) => {
          if (!dragging) {
            if (Math.abs(ev.clientX - startX) < 6) return;
            dragging = true;
            table.classList.add('is-col-dragging');
            th.classList.add('is-col-dragged');
            document.body.appendChild(line);
          }
          ev.preventDefault();
          // Looked up on every move: a live update can redraw the table
          // mid-drag, and the drop has to land on the table now on screen.
          const liveWrap = root.querySelector('.event-detail-table-wrap.is-spreadsheet-wrap') || wrap;
          // Near an edge of the scrolling table, scroll it so a column can be
          // carried to one that is off screen.
          const w = liveWrap.getBoundingClientRect();
          if (ev.clientX < w.left + 40) liveWrap.scrollLeft -= 14;
          else if (ev.clientX > w.right - 40) liveWrap.scrollLeft += 14;
          const ths = [...liveWrap.querySelectorAll('thead th.is-movable')];
          let hit = ths.find(t => { const r = t.getBoundingClientRect(); return ev.clientX < r.left + r.width / 2; });
          const after = !hit;
          if (!hit) hit = ths[ths.length - 1];
          if (!hit) return;
          const r = hit.getBoundingClientRect();
          drop = { key: hit.dataset.col, after };
          const x = Math.min(Math.max(after ? r.right : r.left, w.left), w.right);
          line.style.left = (x - 1) + 'px';
          line.style.top = w.top + 'px';
          line.style.height = Math.max(0, Math.min(w.bottom, window.innerHeight) - w.top) + 'px';
        };
        const onUp = (ev) => {
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
          document.removeEventListener('pointercancel', onUp);
          line.remove();
          table.classList.remove('is-col-dragging');
          th.classList.remove('is-col-dragged');
          if (!dragging) return;
          // The release also arrives as a click on the header, which would sort.
          const eat = (ce) => { ce.stopPropagation(); ce.preventDefault(); };
          window.addEventListener('click', eat, { capture: true, once: true });
          setTimeout(() => window.removeEventListener('click', eat, { capture: true }), 0);
          // A cancelled pointer (a pen whose browser chose to pan) moves nothing.
          if (drop && ev.type !== 'pointercancel' && _moveColumn(key, drop.key, drop.after)) _renderActiveTab();
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
      });
    });
  }

  // Columns actually rendered — drops anything the user has hidden via the
  // Columns menu (except required cols).
  function _buildSpreadsheetColumns() {
    return _allPossibleColumns()
      .filter(c => REQUIRED_COLUMN_KEYS.has(c.key) || !state.hiddenColumns.has(c.key))
      // Built-in columns are renamable too — a host who calls it "Firm" rather
      // than "Company" should see "Firm" (task #616).
      .map(c => (_isRenamableColumn(c) && !c.originalLabel)
        ? { ...c, originalLabel: c.label, label: _columnLabel(c.key, c.label) }
        : c);
  }

  function _readCell(r, col) {
    if (col.key === '_total')          return _partySize(r);
    if (col.key === 'status')          return _normalizeStatus(r);
    if (col.key === 'guestName')       return r.guestName || r.name || '';
    if (col.key === 'firstName' || col.key === 'lastName') return _namePart(r, col.key);
    if (col.key === 'phone')           return _phoneOf(r);
    if (col.key === '_table')          return (_seatIdx().byResponse.get(r.id) || []).join(', ');
    if (col.key === '_checkin')        return r.checkedInAt ? _fmtDate(r.checkedInAt) : '';
    if (col.key === 'numberOfAdults')  return r.numberOfAdults ?? r.adultCount ?? '';
    if (col.key === 'numberOfChildren') return r.numberOfChildren ?? r.childrenCount ?? r.childCount ?? '';
    if (col.key === 'dietary')         return r.dietaryDetails || (typeof r.dietary === 'string' ? r.dietary : '') || r.dietaryRestrictions || '';
    if (col.key === 'submittedAt')     return _fmtDate(r.submittedAt || r.createdAt);
    if (col.customFieldId) {
      const v = r.customFieldsData?.[col.customFieldId];
      if (v == null) return '';
      if (typeof v === 'object') return v.value ?? v.answer ?? '';
      return v;
    }
    return r[col.key] ?? '';
  }

  // Translate an edited cell value into the PUT payload shape expected by
  // PUT /api/forms/:formId/responses/:responseId. Customfields preserve their
  // object envelope so the form-renderer keeps working.
  function _writeCellPayload(col, newValue, r) {
    const payload = {};
    if (col.key === 'status') {
      payload.attending = newValue === 'yes' ? 'yes' : 'no';
      // The host's answer for an uploaded guest is an answer (#1090): the
      // server records attendingStated itself; this keeps the local copy in
      // step so the row leaves "Awaiting reply" without a reload.
      if (r && r.importedEntry === true) payload.attendingStated = true;
      return payload;
    }
    if (col.key === 'numberOfAdults') {
      payload.numberOfAdults = Math.max(0, parseInt(newValue, 10) || 0);
      return payload;
    }
    if (col.key === 'numberOfChildren') {
      payload.numberOfChildren = Math.max(0, parseInt(newValue, 10) || 0);
      return payload;
    }
    if (col.key === 'dietary') {
      payload.dietaryDetails = newValue;
      return payload;
    }
    // The PUT allowlist has no `phone`, so a payload under that key saved
    // nothing while the cell showed a tick (#854).
    if (col.key === 'phone') {
      payload.phoneNumber = String(newValue || '').trim();
      return payload;
    }
    // A renamed guest on a form that keeps first and last name apart (#891):
    // the old halves would contradict the new name, so they go, and the
    // export splits the new name instead.
    if (col.key === 'guestName' && (r.firstName || r.lastName)) {
      payload.guestName = newValue;
      payload.firstName = '';
      payload.lastName = '';
      return payload;
    }
    if (col.customFieldId) {
      const existing = r.customFieldsData?.[col.customFieldId];
      const nextCfd = { ...(r.customFieldsData || {}) };
      if (existing && typeof existing === 'object') {
        // The answer's structured copies (the per-guest counts, the named
        // guests' picks, the translation keys) would go on saying the OLD
        // answer, and the counts are what the meal totals read first: "2
        // Chicken, 1 Fish" edited to "3 Chicken" kept counting 2 and 1. The
        // regular dashboard's editor drops them for the same reason.
        // Choice answers only: a per-attendee TEXT question keeps each extra
        // guest's own answer in attendeeValues, which the +1 rows show.
        const next = { ...existing, value: newValue };
        if (_isChoiceColumn(col)) {
          ['perGuestQuantities', 'attendeeValues', 'valueKey', 'isPerGuestQuantity', 'perAttendee'].forEach((k) => { delete next[k]; });
        }
        if (existing.displayValue !== undefined) next.displayValue = newValue;
        // A count per answer on a per-guest question, in the guest form's own
        // shape, so the totals stay right (task #1100).
        const counts = _choiceTakesCounts(col, r) ? _choiceCountsFromText(newValue, col.customFieldOptions, _partySize(r)) : null;
        if (counts) { next.perGuestQuantities = counts; next.isPerGuestQuantity = true; }
        nextCfd[col.customFieldId] = next;
      } else if (_choiceTakesCounts(col, r)) {
        const counts = _choiceCountsFromText(newValue, col.customFieldOptions, _partySize(r));
        nextCfd[col.customFieldId] = {
          label: col.originalLabel || col.label || '', originalLabel: col.originalLabel || col.label || '',
          value: newValue, type: col.customFieldType,
          ...(counts ? { perGuestQuantities: counts, isPerGuestQuantity: true } : {})
        };
      } else {
        nextCfd[col.customFieldId] = newValue;
      }
      payload.customFieldsData = nextCfd;
      return payload;
    }
    payload[col.key] = newValue;
    return payload;
  }

  // Three pinned columns since task #624 added the row number ahead of the
  // checkbox: # | select | Name. The left offsets live in org-dashboard.css and
  // must stay in step with the widths above.
  function _stickyClassFor(col) {
    if (col.sticky === 1) return 'sticky-col sticky-col-1';
    if (col.sticky === 2) return 'sticky-col sticky-col-2';
    if (col.sticky === 3) return 'sticky-col sticky-col-3';
    return '';
  }

  function _renderSpreadsheet(filtered) {
    const cols = _buildSpreadsheetColumns();
    const selState = _selectAllState(filtered);

    const headerCells = cols.map((c) => {
      const cls = [
        'col-' + c.key.replace(/[^a-z0-9-]/gi, '-'),
        _stickyClassFor(c),
        c.key === '_select' ? 'col-checkbox' : ''
      ].filter(Boolean).join(' ');
      // Only the checkbox column gets a hard-coded width; everything else
      // is sized by table-layout: auto + the cell-edit-wrap ghost — unless the
      // host has dragged it, in which case their width wins (task #616).
      const dragged = _colPrefs().widths[c.key];
      const widthStyle = dragged
        ? `style="min-width:${dragged}px;width:${dragged}px;max-width:${dragged}px;"`
        : (c.width ? `style="min-width:${c.width}px;width:${c.width}px;"` : '');
      const grip = `<span class="oed-col-resizer" data-col="${_esc(c.key)}" aria-hidden="true" title="Drag to resize · double-click to reset"></span>`;
      if (c.key === '_rownum') {
        return `<th class="${cls} col-rownum" data-col="${_esc(c.key)}" ${widthStyle} title="Row number">#${grip}</th>`;
      }
      if (c.key === '_select') {
        return `<th class="${cls}" data-col="${_esc(c.key)}" ${widthStyle}><input type="checkbox" id="eventDetailSelectAll" data-sel-state="${selState}"${selState === 'all' ? ' checked' : ''} aria-label="Select all rows">${grip}</th>`;
      }
      // Any other column with no name to rename (the row-actions column):
      // a plain header, with no rename pencil, sort or resize grip. It fell
      // through to the named-column markup and offered a rename on hover over
      // an empty header (task #1108).
      if (!_isRenamableColumn(c)) {
        return `<th class="${cls}" data-col="${_esc(c.key)}" ${widthStyle}><span class="visually-hidden">Actions</span></th>`;
      }
      // The full question text stays on the hover tooltip even when a short
      // name is shown, so the original wording is never lost.
      const renamed = !!_colPrefs().labels[c.key];
      // Sorting (task #855): click the name to sort, again to reverse, a third
      // time to clear. Double-click still renames.
      const sortDir = state.sort && state.sort.key === c.key ? state.sort.dir : '';
      const sortable = !UNSORTABLE_COLUMN_KEYS.has(c.key);
      const ariaSort = sortable ? `aria-sort="${sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none'}"` : '';
      const sortIcon = sortable
        ? `<i class="oed-sort-icon bi ${sortDir === 'asc' ? 'bi-arrow-up' : sortDir === 'desc' ? 'bi-arrow-down' : 'bi-arrow-down-up'}" data-col="${_esc(c.key)}" aria-hidden="true"></i>`
        : '';
      return `<th class="${cls}${renamed ? ' is-renamed' : ''}${sortable ? ' is-sortable' : ''}${sortDir ? ' is-sorted' : ''}${_isMovableColumn(c.key) ? ' is-movable' : ''}" data-col="${_esc(c.key)}" data-original-label="${_esc(c.originalLabel || c.label)}" ${ariaSort} ${widthStyle} title="${_esc(c.originalLabel || c.label)}">
        <span class="oed-col-head">
          <span class="oed-col-label" data-col="${_esc(c.key)}" role="button" tabindex="0"
                title="${_esc(c.originalLabel || c.label)} — click to sort, double-click to rename${_isMovableColumn(c.key) ? ', drag to move' : ''}">${_esc(c.label)}</span>${sortIcon}
          <button type="button" class="oed-col-rename" data-col="${_esc(c.key)}" tabindex="-1"
                  title="Rename this column (this view only)" aria-label="Rename column"><i class="bi bi-pencil"></i></button>
        </span>${grip}</th>`;
    }).join('');

    // ONE counter for every row that is actually drawn, so with "Attendees on
    // separate rows" switched on the extra people are numbered too and the
    // count still matches a printed headcount (task #624).
    let rowNum = 0;
    const body = filtered.map(r => {
      let html = _renderSpreadsheetRow(r, cols, ++rowNum);
      // When the "Show additional attendees" toggle is on, append a read-only
      // row per additional attendee so their name/email/phone/job-title show
      // directly in the table (not only in the View-details popup).
      if (state.expandAttendees && Array.isArray(r.additionalGuestDetails) && r.additionalGuestDetails.length) {
        html += r.additionalGuestDetails
          .map((g, i) => _renderAttendeeSubRow(g, g.position || (i + 2), cols, ++rowNum, r.id))
          .join('');
      }
      return html;
    }).join('');

    return `
      <div class="table-fade-frame">
        <div class="event-detail-table-wrap is-spreadsheet-wrap">
          <table class="event-detail-table is-spreadsheet">
            <thead><tr>${headerCells}</tr></thead>
            <tbody>${body}${_renderQuickAddRow(cols)}</tbody>
          </table>
        </div>
      </div>`;
  }

  // ---- Spreadsheet quick-add row (task #625) ------------------------------
  //
  // An assistant typing a delegation list of forty people should not open a
  // dialog forty times. A blank row sits permanently at the foot of the table:
  // type across it, Tab to move, Enter (or Tab off the last cell) to commit —
  // then the row is empty again with the cursor back at the start, ready for
  // the next person.
  //
  // The columns are the SAME columns the table is showing, so what you can type
  // matches what you can see; a column you have hidden isn't asked for.

  // The corporate columns that go on the response doc as top-level keys. Kept
  // in step with MAIN_GUEST_DETAIL_KEYS in server/services/response-import
  // .service.js — the import route allowlists exactly these, so the quick-add
  // row, the Add-guest dialog and a CSV import all carry the same set.
  const QA_DETAIL_KEYS = ['company', 'jobTitle', 'department', 'transportation', 'notes', 'heardAboutUs'];

  /** Columns a person can actually type into. */
  function _quickAddFields(cols) {
    return cols.filter(c => !['_rownum', '_select', '_actions', '_total', '_table', '_checkin', 'submittedAt'].includes(c.key)
      && c.customFieldType !== 'image');
  }

  function _renderQuickAddRow(cols) {
    const fields = _quickAddFields(cols);
    if (!fields.length) return '';
    const cells = cols.map(col => {
      const baseCls = 'col-' + col.key.replace(/[^a-z0-9-]/gi, '-');
      const sticky = _stickyClassFor(col);
      const cls = [sticky, baseCls].filter(Boolean).join(' ');

      if (col.key === '_rownum') {
        return `<td class="${cls} col-rownum cell-readonly qa-plus" title="Add a guest"><i class="bi bi-plus-lg"></i></td>`;
      }
      if (col.key === '_select') return `<td class="${cls} col-checkbox"></td>`;
      if (col.key === '_total' || col.key === '_table' || col.key === '_checkin' || col.key === 'submittedAt') {
        return `<td class="${cls} cell-readonly"></td>`;
      }
      if (col.key === '_actions') {
        return `<td class="${cls} row-actions">
          <button type="button" class="btn btn-org-primary btn-xs qa-commit" title="Add this guest (or press Enter)">
            <i class="bi bi-check2"></i>
          </button>
        </td>`;
      }
      if (col.customFieldType === 'image') return `<td class="${cls} cell-readonly"></td>`;

      const last = fields[fields.length - 1].key === col.key ? ' data-qa-last="1"' : '';
      if (col.key === 'status') {
        return `<td class="${cls}">
          <select class="qa-input qa-select" data-c="status"${last}>
            <option value="yes" selected>Attending</option>
            <option value="no">Declined</option>
          </select></td>`;
      }
      if (_isChoiceColumn(col)) {
        const opts = col.customFieldOptions || [];
        return `<td class="${cls}">
          <select class="qa-input qa-select" data-c="${_esc(col.key)}"${last}>
            <option value="" selected>—</option>
            ${opts.map(o => `<option value="${_esc(o)}">${_esc(o)}</option>`).join('')}
          </select></td>`;
      }
      const type = col.type === 'email' ? 'email' : col.type === 'number' ? 'number' : 'text';
      // Only the first cell gets a placeholder: a row of eleven grey hints is
      // noise, and the header above each cell already names it.
      const ph = col.key === 'guestName' ? ' placeholder="Add a guest…"' : '';
      return `<td class="${cls}">
        <input type="${type}" class="qa-input" data-c="${_esc(col.key)}"${ph}${last}></td>`;
    }).join('');
    return `<tr class="oed-quickadd">${cells}</tr>`;
  }

  function _renderSpreadsheetRow(r, cols, rowNum) {
    const status = _normalizeStatus(r);
    const isPending = status === 'pending';
    const isWaitlist = status === 'waitlist';
    const selected = state.selected.has(r.id);

    const cells = cols.map(col => {
      const widthStyle = col.width ? `style="min-width:${col.width}px;width:${col.width}px;"` : '';
      const baseCls = 'col-' + col.key.replace(/[^a-z0-9-]/gi, '-');
      const sticky = _stickyClassFor(col);

      if (col.key === '_rownum') {
        return `<td class="${sticky} col-rownum cell-readonly" ${widthStyle}>${rowNum == null ? '' : rowNum}</td>`;
      }
      if (col.key === '_select') {
        return `<td class="${sticky} col-checkbox" ${widthStyle}>
          <input type="checkbox" class="event-detail-row-check" value="${_esc(r.id)}" ${selected ? 'checked' : ''}>
        </td>`;
      }

      if (col.key === 'status') {
        if (isPending || isWaitlist) {
          const pill = isPending
            ? '<span class="status-pill pill-pending"><i class="bi bi-hourglass-split"></i> Pending</span>'
            : '<span class="status-pill pill-waitlist"><i class="bi bi-people"></i> Waitlist</span>';
          return `<td class="${baseCls} cell-pill-cell" ${widthStyle}>${pill}</td>`;
        }
        // yes / no editable dropdown — wrap in cell-edit-wrap so the cell
        // sizes to the option text plus chevron, not the browser's default
        // select width. It wears the same compact chip as the choice columns
        // (task #623 r2): Attending green, Declined rose.
        const label = status === 'yes' ? 'Attending' : status === 'awaiting' ? 'Awaiting reply' : 'Declined';
        return `<td class="${baseCls} cell-pill-cell" ${widthStyle}>
          <div class="cell-edit-wrap is-select is-pill${_cellSizeClass(col)}"${_cellSizeStyle(col)}>
            <span class="cell-edit-ghost" aria-hidden="true">${_esc(label)}</span>
            <select class="cell-edit cell-select cell-pill cell-pill-status-${status}" data-r="${_esc(r.id)}" data-c="status">
              ${status === 'awaiting' ? '<option value="awaiting" selected disabled>Awaiting reply</option>' : ''}
              <option value="yes" ${status === 'yes' ? 'selected' : ''}>Attending</option>
              <option value="no"  ${status === 'no'  ? 'selected' : ''}>Declined</option>
            </select>
          </div>
        </td>`;
      }

      if (col.key === '_total') {
        return `<td class="${baseCls} cell-readonly" ${widthStyle}>${status === 'yes' ? _partySize(r) : '—'}</td>`;
      }

      if (col.key === '_table') {
        const t = _readCell(r, col);
        return `<td class="${baseCls} cell-readonly" ${widthStyle}>${t ? `<span class="oed-table-chip" title="${_esc(t)}"><i class="bi bi-grid-3x3-gap"></i> ${_esc(t)}</span>` : '<span class="text-muted">—</span>'}</td>`;
      }

      if (col.key === '_checkin') {
        return `<td class="${baseCls} cell-readonly" ${widthStyle}>${_checkinPill(r.checkedInAt, col)}</td>`;
      }

      if (col.key === 'submittedAt') {
        return `<td class="${baseCls} cell-readonly row-submitted" ${widthStyle}>${_esc(_readCell(r, col))}</td>`;
      }

      if (col.key === '_actions') {
        // Task #850: every row can be deleted from here too.
        const del = `<button class="btn btn-org-ghost btn-xs oed-row-delete" onclick="OrgEventDetail.deleteResponse('${_esc(r.id)}')" title="Delete guest"><i class="bi bi-trash"></i></button>`;
        // Task #1103: a pencil that opens the guest's answers to edit, where
        // three dots opened a dump of the raw record.
        const edit = `<button class="btn btn-org-ghost btn-xs oed-row-edit" onclick="OrgEventDetail.editGuest('${_esc(r.id)}')" title="Edit guest" aria-label="Edit ${_esc(_guestLabel(r, 'guest'))}"><i class="bi bi-pencil"></i></button>`;
        const actions = isPending ? `
          <button class="btn btn-org-primary btn-xs" onclick="OrgEventDetail.approve('${_esc(r.id)}')" title="Approve"><i class="bi bi-check2"></i></button>
          <button class="btn btn-org-outline btn-xs" onclick="OrgEventDetail.deny('${_esc(r.id)}')" title="Deny"><i class="bi bi-x"></i></button>
          ${edit}
          ${del}
        ` : `
          ${edit}
          ${del}
        `;
        return `<td class="${baseCls} row-actions" ${widthStyle}>${actions}</td>`;
      }

      // Image custom field: read-only thumbnail that opens the full photo, plus a
      // download link. (Holds a URL, so there's nothing to edit inline.)
      if (col.customFieldType === 'image') {
        const url = _readCell(r, col);
        if (!url) {
          return `<td class="${[sticky, baseCls].filter(Boolean).join(' ')} cell-readonly" ${widthStyle}>—</td>`;
        }
        const safe = _esc(url);
        return `<td class="${[sticky, baseCls].filter(Boolean).join(' ')} cell-readonly col-cf-image" ${widthStyle}>
          <a href="${safe}" target="_blank" rel="noopener" title="Open photo"><img src="${safe}" alt="" loading="lazy" style="width:34px;height:34px;object-fit:cover;border-radius:5px;border:1px solid #ddd;vertical-align:middle;"></a>
          <a href="${safe}" download target="_blank" rel="noopener" title="Download photo" style="margin-inline-start:6px;"><i class="bi bi-download"></i></a>
        </td>`;
      }

      // Choice question (select / radio): a coloured pill that is also the
      // editor (task #623). Same <select class="cell-edit"> contract the Status
      // column uses, so the existing change handler saves it unchanged.
      if (_isChoiceColumn(col)) {
        const cur = String(_readCell(r, col) ?? '').trim();
        const opts = col.customFieldOptions || [];
        // A stored answer that is no longer on the question's list must still be
        // visible and selected — dropping it would silently blank the cell and
        // the next edit would erase real data.
        const all = opts.includes(cur) || !cur ? opts : [cur, ...opts];
        const hue = _pillHue(cur, opts);
        const pillCls = cur ? ` cell-pill cell-pill-${hue}` : ' cell-pill-empty';
        // An UNANSWERED choice cell keeps the full-width wrap: a chip that hugs
        // its text would be a couple of pixels wide with nothing in it, and the
        // host would have no target to click to answer it (task #623 r3).
        return `<td class="${[sticky, baseCls].filter(Boolean).join(' ')} col-choice${cur ? ' cell-pill-cell' : ''}" ${widthStyle}>
          <div class="cell-edit-wrap is-select is-pill${cur ? '' : ' is-pill-empty'}${_cellSizeClass(col)}"${_cellSizeStyle(col)}>
            <span class="cell-edit-ghost" aria-hidden="true">${cur ? _esc(cur) : ' '}</span>
            <select class="cell-edit cell-select${pillCls}" data-r="${_esc(r.id)}" data-c="${_esc(col.key)}"${_choiceOpensEditor(col, r) ? ' data-opens-editor="1" aria-haspopup="dialog"' : ''}${cur ? ` title="${_esc(cur)}"` : ''}>
              <option value=""${cur ? '' : ' selected'}>—</option>
              ${all.map(o => `<option value="${_esc(o)}"${o === cur ? ' selected' : ''}>${_esc(o)}</option>`).join('')}
            </select>
          </div>
        </td>`;
      }

      // Editable text/number/email cell. The wrap's ghost ::after element
      // holds the value text invisibly, so the cell width is driven by the
      // value, not the browser's default <input> width.
      const value = _readCell(r, col);
      const inputType =
        col.type === 'number' ? 'number' :
        col.type === 'email'  ? 'email'  : 'text';
      // Sticky class is determined up-front for the whole row via _stickyClassFor(col).
      // Empty ghost text would collapse the cell — fall back to a single space
      // so the cell stays clickable. The visible header above still labels it.
      const ghostText = value ? _esc(value) : ' ';
      // Capped columns (notes + custom fields) get a hover tooltip showing
      // the full value, plus an expand button that opens a popover textarea.
      const isCapped = col.key === 'notes' || col.key.startsWith('cf:');
      const titleAttr = (isCapped && value) ? `title="${_esc(value)}"` : '';
      const expandBtn = isCapped
        ? `<button type="button" class="cell-edit-expand" data-r="${_esc(r.id)}" data-c="${_esc(col.key)}" tabindex="-1" title="Open expanded editor"><i class="bi bi-arrows-angle-expand"></i></button>`
        : '';
      return `<td class="${[sticky, baseCls].filter(Boolean).join(' ')}" ${widthStyle}>
        <div class="cell-edit-wrap${isCapped ? ' is-capped' : ''}${_cellSizeClass(col)}"${_cellSizeStyle(col)}>
          <span class="cell-edit-ghost" aria-hidden="true">${ghostText}</span>
          <input type="${inputType}" class="cell-edit" data-r="${_esc(r.id)}" data-c="${_esc(col.key)}" value="${_esc(value)}" ${titleAttr}>
          ${expandBtn}
        </div>
      </td>`;
    }).join('');

    return `<tr class="${isPending ? 'row-pending' : ''}">${cells}</tr>`;
  }

  // ---- Coloured category pills for choice questions (task #623) ----------
  //
  // A column of "Allocator of Capital (e.g., Family Office, Pension…)" repeated
  // forty times in plain grey text is unreadable at a glance; the same column as
  // soft coloured chips can be scanned in one pass. This is the Airtable
  // single-select treatment, and it only applies to questions that HAVE a fixed
  // set of answers — free text stays free text.
  //
  // Ten soft hues, assigned by the option's POSITION in the question's own
  // option list, so a given answer is the same colour in every row, on every
  // reload, for every viewer. A value that isn't one of the listed options (an
  // import, or a choice the host has since removed) falls back to a hash of the
  // text, which is stable for the same reason.
  const PILL_HUE_COUNT = 10;

  function _pillHue(value, options) {
    const v = String(value == null ? '' : value).trim();
    if (!v) return 0;
    const idx = (options || []).indexOf(v);
    if (idx >= 0) return idx % PILL_HUE_COUNT;
    let h = 0;
    for (let i = 0; i < v.length; i++) h = (h * 31 + v.charCodeAt(i)) >>> 0;
    return h % PILL_HUE_COUNT;
  }

  // True for the column types that have a fixed answer set worth colouring.
  // A question with a fixed list of answers: Dropdown, Single choice, and
  // Multiple choice (checkbox) - the Meal Choice preset is a checkbox question,
  // so leaving checkbox out gave the column a free-text box (task #1100).
  function _isChoiceColumn(col) {
    return !!col && (col.customFieldType === 'select' || col.customFieldType === 'radio'
      || _isMultiChoiceColumn(col));
  }
  function _isMultiChoiceColumn(col) {
    return !!col && col.customFieldType === 'checkbox' && (col.customFieldOptions || []).length > 0;
  }

  // Does this cell take a count per answer ("2 Chicken, 1 Beef")? A question
  // with per-guest quantities, on a party of more than one - the shape the
  // guest form's count mode stores (form.js readPerGuestChoiceAnswer).
  function _choiceTakesCounts(col, r) {
    return !!(col && col.customFieldPerGuest && r && _partySize(r) > 1);
  }

  // Does clicking this choice cell open the several-answers editor straight
  // away, instead of the dropdown? A Multiple choice question (a tick per
  // option), or any choice question that takes a count per answer (a number
  // per option). #1100 round 2: these were a dropdown ending in "Choose
  // several…" / "Set how many of each…", one click too many; a Dropdown or
  // Single choice question on one guest keeps the plain dropdown.
  function _choiceOpensEditor(col, r) {
    return _isMultiChoiceColumn(col) || (_isChoiceColumn(col) && _choiceTakesCounts(col, r));
  }

  // Read a single additional-attendee's value for a given table column.
  // additionalGuestDetails stores { position, name, email, phone, jobTitle,
  // company, department } — name maps to the Name column; the rest match by key.
  function _readGuestCell(guest, col) {
    if (col.key === 'guestName') return guest.name || '';
    if (['email', 'phone', 'company', 'jobTitle', 'department', 'dietary'].includes(col.key)) {
      return guest[col.key] || '';
    }
    return '';
  }

  /**
   * Which built-in columns were asked of each attendee INDIVIDUALLY.
   *
   * The form already records this: `guestDetailFields` is the list of things
   * the +1 boxes ask for. Anything outside it was asked once, of the party,
   * and therefore belongs to every attendee (task #615 round 3).
   */
  function _perAttendeeKeys() {
    const configured = Array.isArray(state.form && state.form.guestDetailFields)
      ? state.form.guestDetailFields.filter(Boolean)
      : [];
    return new Set(['guestName', 'name'].concat(configured));
  }

  /**
   * Contact details and per-person counts are NEVER inherited, whatever the
   * form asked.
   *
   * A +1 row carrying the registrant's email address is not a blank being
   * helpfully filled in — it is a wrong fact that a mail merge will act on,
   * writing to the same person once per member of their party. The counts are
   * excluded for the reason round 1 gave: repeating a party size on every row
   * makes any SUM of that column count the event several times over.
   */
  const NEVER_INHERITED = new Set(['guestName', 'name', 'email', 'phone', 'firstName', 'lastName',
    '_total', '_rownum', '_partyMember', '_select', '_actions',
    'numberOfAdults', 'numberOfChildren']);

  /**
   * One cell of a +1's export row.
   *
   * Three sources, in order: what this person answered for themselves, then
   * the party's answer to a question that was only asked once, then blank.
   * A question that IS asked per attendee stays blank when they left it
   * blank — inheriting there would put the registrant's job title on every
   * colleague.
   */
  /**
   * One attendee's OWN answer to a question asked of each guest, or null when
   * the answer was given once for the party. The guest form (and the Add
   * guest dialog, task #1104) store them in customFieldsData[id].attendeeValues,
   * slot 1 = the registrant; `<fieldId>__att<n>` is the older key this read
   * before - nothing stores it now, so +1 rows showed no per-guest answer.
   */
  function _attendeeAnswer(response, col, pos) {
    const cfd = response && response.customFieldsData;
    if (!cfd || !col.customFieldId) return null;
    const stored = cfd[col.customFieldId];
    if (stored && typeof stored === 'object' && Array.isArray(stored.attendeeValues)) {
      const v = stored.attendeeValues[pos - 1];
      return v == null ? '' : String(v);
    }
    const legacy = cfd[`${col.customFieldId}__att${pos}`];
    if (legacy != null && legacy !== '') return typeof legacy === 'object' ? (legacy.value ?? legacy.answer ?? '') : legacy;
    return null;
  }

  function _readGuestExportCell(guest, col, response, pos, perAttendee) {
    if (col.key === '_table') return _memberTable(response.id, guest.name);
    const own = _readGuestCell(guest, col);
    if (own !== '' && own != null) return own;

    if (col.customFieldId) {
      // Answered per guest: this guest's own answer, blank if they left it.
      const perAnswer = _attendeeAnswer(response, col, pos);
      if (perAnswer != null) return perAnswer;
      const def = (state.form?.customFields || []).find(f => f && f.id === col.customFieldId);
      if (def && def.perAttendee) return '';   // asked of them; they left it blank
      return _readCell(response, col);          // asked once — it is theirs too
    }

    if (NEVER_INHERITED.has(col.key)) return '';
    if (perAttendee.has(col.key)) return '';
    return _readCell(response, col);
  }

  // One read-only table row for an additional attendee (Guest 2, 3, …). It has
  // no checkbox/status/total/actions (those belong to the parent response) and
  // no editable inputs — sub-rows are a display-only expansion of one response.
  function _renderAttendeeSubRow(guest, pos, cols, rowNum, responseId) {
    const cells = cols.map(col => {
      const widthStyle = col.width ? `style="min-width:${col.width}px;width:${col.width}px;"` : '';
      const baseCls = 'col-' + col.key.replace(/[^a-z0-9-]/gi, '-');
      const sticky = _stickyClassFor(col);

      if (col.key === '_rownum') {
        return `<td class="${sticky} col-rownum cell-readonly" ${widthStyle}>${rowNum == null ? '' : rowNum}</td>`;
      }
      if (col.key === '_select') {
        return `<td class="${sticky} col-checkbox" ${widthStyle}></td>`;
      }
      if (col.key === 'guestName') {
        const nm = guest.name || '(no name)';
        // Name first, badge after it (task #660): the badge in front pushed
        // every sub-row's name to a different x than the primary rows above and
        // below it, and the name is what the column is for — the "Guest 2" tag
        // is a note about it.
        return `<td class="${[sticky, baseCls].filter(Boolean).join(' ')}" ${widthStyle}>
          <span class="event-detail-subrow-name">
            <span class="event-detail-subrow-label">${_esc(nm)}</span>
            <span class="event-detail-subrow-badge">Guest ${_esc(String(pos))}</span>
          </span>
        </td>`;
      }
      if (col.key === '_total') {
        return `<td class="${baseCls} cell-readonly col--total" ${widthStyle}>—</td>`;
      }
      if (col.key === '_table') {
        const t = _memberTable(responseId, guest.name);
        return `<td class="${baseCls} cell-readonly" ${widthStyle}>${t ? `<span class="oed-table-chip"><i class="bi bi-grid-3x3-gap"></i> ${_esc(t)}</span>` : '<span class="text-muted">—</span>'}</td>`;
      }
      // Check-in is per party: the people a guest brings arrived with them.
      if (col.key === '_checkin') {
        const parent = (state.responses || []).find(x => x.id === responseId);
        return `<td class="${baseCls} cell-readonly" ${widthStyle}>${_checkinPill(parent && parent.checkedInAt, col)}</td>`;
      }
      if (col.key === '_actions') {
        return `<td class="${baseCls} row-actions" ${widthStyle}></td>`;
      }
      // A question asked of each guest shows this guest's own answer (#1104).
      if (col.customFieldId) {
        const parent = (state.responses || []).find(x => x.id === responseId);
        const own = _attendeeAnswer(parent, col, pos);
        return `<td class="${[sticky, baseCls].filter(Boolean).join(' ')} cell-readonly" ${widthStyle}>${_esc(own || '')}</td>`;
      }
      const value = _readGuestCell(guest, col);
      return `<td class="${[sticky, baseCls].filter(Boolean).join(' ')} cell-readonly" ${widthStyle}>${_esc(value)}</td>`;
    }).join('');

    return `<tr class="event-detail-subrow">${cells}</tr>`;
  }

  // ---- Column resizing and renaming (task #616) ---------------------------
  //
  // A delegate list's questions are long ("Primary Investment Function:"), and
  // the header either crowds the view or truncates it. Both fixes are the
  // host's to make: drag the divider to the width they want, and give the
  // column a short name of their own. Neither touches the form — the question
  // your guests answer is unchanged, and the full text stays on hover.
  const MIN_COL_W = 56;

  /** The cell wraps in one column — what actually has to shrink. */
  function _columnWraps(th, key) {
    const table = th.closest('table');
    if (!table) return [];
    const cls = 'col-' + key.replace(/[^a-z0-9-]/gi, '-');
    return [...table.querySelectorAll(`td.${cls} .cell-edit-wrap`)];
  }

  function _wireColumnResizers(root) {
    root.querySelectorAll('.oed-col-resizer').forEach(grip => {
      const th = grip.closest('th');
      const key = grip.dataset.col;
      if (!th || !key) return;

      grip.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();   // don't start a rename or a sort
        const startX = e.clientX;
        const startW = th.getBoundingClientRect().width;
        const table = th.closest('table');
        table?.classList.add('is-col-resizing');

        const wraps = _columnWraps(th, key);
        const apply = (w) => {
          const px = Math.max(MIN_COL_W, Math.round(w));
          th.style.width = th.style.minWidth = th.style.maxWidth = px + 'px';
          // The header alone does nothing: `table-layout: auto` sizes a column
          // to its widest CONTENT and treats a cell's max-width as a suggestion,
          // so the column springs back unless the cells are capped too. Applied
          // live so the drag tracks the pointer rather than jumping on release.
          wraps.forEach(wr => {
            wr.classList.add('is-sized');
            wr.style.maxWidth = Math.max(28, px - 14) + 'px';
          });
        };
        const onMove = (ev) => apply(startW + (ev.clientX - startX));
        const onUp = () => {
          // Listen on the DOCUMENT, not the grip. The grip is 8px wide, and one
          // frame into the drag the pointer is over a neighbouring header —
          // setPointerCapture is supposed to keep delivering to the grip and
          // does not do so reliably, which made the drag simply not happen.
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
          document.removeEventListener('pointercancel', onUp);
          table?.classList.remove('is-col-resizing');
          _colPrefs().widths[key] = parseInt(th.style.width, 10)
            || Math.round(th.getBoundingClientRect().width);
          _persistColPrefs();
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
      });

      // Double-click the divider: back to sizing itself from its contents.
      grip.addEventListener('dblclick', (e) => {
        e.preventDefault();
        e.stopPropagation();
        th.style.width = th.style.minWidth = th.style.maxWidth = '';
        _columnWraps(th, key).forEach(wr => {
          wr.classList.remove('is-sized');
          wr.style.maxWidth = '';
        });
        delete _colPrefs().widths[key];
        _persistColPrefs();
      });
    });
  }

  function _wireColumnRenames(root) {
    const startRename = (th) => {
      if (!th || th.querySelector('.oed-col-rename-input')) return;
      const key = th.dataset.col;
      const head = th.querySelector('.oed-col-head');
      const labelEl = th.querySelector('.oed-col-label');
      if (!key || !head || !labelEl) return;
      const original = th.dataset.originalLabel || labelEl.textContent;

      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'oed-col-rename-input';
      input.maxLength = 60;
      input.value = labelEl.textContent.trim();
      input.placeholder = original;
      // Empty means "use the question's own words again", which is the only way
      // back — so say so rather than leaving the host to guess.
      input.title = 'Clear the box to go back to the original question text';
      head.style.display = 'none';
      th.insertBefore(input, head);
      input.focus();
      input.select();

      let done = false;
      const finish = (save) => {
        if (done) return;
        done = true;
        if (save) {
          const v = input.value.trim();
          if (v && v !== original) _colPrefs().labels[key] = v;
          else delete _colPrefs().labels[key];
          _persistColPrefs();
        }
        input.remove();
        head.style.display = '';
        if (save) _renderActiveTab();
      };
      input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter')  { ev.preventDefault(); finish(true); }
        if (ev.key === 'Escape') { ev.preventDefault(); finish(false); }
      });
      input.addEventListener('blur', () => finish(true));
    };

    root.querySelectorAll('.oed-col-rename').forEach(btn => {
      // mousedown.preventDefault keeps the click from stealing focus before the
      // input exists, which would fire its own blur-save immediately.
      btn.addEventListener('mousedown', (e) => e.preventDefault());
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); startRename(btn.closest('th')); });
    });
    // A single click sorts (task #855), but only after a double-click has had
    // its chance: the sort re-renders the header, so sorting on the first click
    // of a double-click would leave the rename nothing to land on.
    let sortTimer = null;
    const queueSort = (key) => {
      clearTimeout(sortTimer);
      sortTimer = setTimeout(() => sortBy(key), 230);
    };
    root.querySelectorAll('.oed-col-label').forEach(label => {
      const key = label.dataset.col;
      const sortable = !UNSORTABLE_COLUMN_KEYS.has(key);
      label.addEventListener('click', (e) => {
        if (!sortable || e.detail > 1) return;
        queueSort(key);
      });
      label.addEventListener('dblclick', (e) => {
        e.preventDefault();
        clearTimeout(sortTimer);
        startRename(label.closest('th'));
      });
      // Enter sorts, as a header's main action; F2 renames, as in a spreadsheet.
      label.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && sortable) { e.preventDefault(); sortBy(key); }
        if (e.key === 'F2') { e.preventDefault(); startRename(label.closest('th')); }
      });
    });
    root.querySelectorAll('.oed-sort-icon').forEach(icon => {
      icon.addEventListener('click', (e) => { e.preventDefault(); sortBy(icon.dataset.col); });
    });
  }

  /**
   * Wire the quick-add row: Tab across it, Enter to commit, Escape to clear.
   *
   * Committing goes through the same import endpoint the Add-guest dialog uses
   * (`skip-dupes`, so adding somebody who already replied never overwrites what
   * they submitted themselves). The columns the importer does not carry for the
   * main guest — company, job title, department and the rest — are written
   * straight afterwards with the same call an inline cell edit makes, against
   * the id the import hands back.
   */
  function _wireQuickAddRow(root, cols) {
    const row = root.querySelector('tr.oed-quickadd');
    if (!row) return;
    const inputs = [...row.querySelectorAll('.qa-input')];
    if (!inputs.length) return;
    const first = inputs[0];
    const nameEl = row.querySelector('.qa-input[data-c="guestName"]') || first;

    const clear = () => {
      inputs.forEach(el => {
        if (el.tagName === 'SELECT') el.selectedIndex = 0;
        else el.value = '';
        el.classList.remove('is-invalid');
      });
      state.quickAddDraft = null;
      row.classList.remove('is-active');
    };
    const focusFirst = () => { first.focus(); if (first.select) first.select(); };

    // Task #1106: what has been typed but not added yet. The row lights up
    // (is-active) while it holds anything, and the values are kept in state
    // so a redraw - a live update from another device, a sort, a filter -
    // puts them back instead of silently emptying the row.
    const typed = () => {
      const values = {};
      inputs.forEach(el => { values[el.dataset.c] = String(el.value || ''); });
      return values;
    };
    const hasTyped = (values) => Object.entries(values).some(([k, v]) => k !== 'status' && v.trim());
    const syncDraft = () => {
      const values = typed();
      const any = hasTyped(values);
      state.quickAddDraft = any ? { formId: state.formId, values } : null;
      row.classList.toggle('is-active', any);
    };
    const draft = state.quickAddDraft;
    if (draft && draft.formId === state.formId) {
      inputs.forEach(el => { if (draft.values[el.dataset.c] != null) el.value = draft.values[el.dataset.c]; });
      row.classList.add('is-active');
    }

    let busy = false;
    const commit = async ({ refocus = true } = {}) => {
      if (busy) return false;
      const values = {};
      inputs.forEach(el => { values[el.dataset.c] = String(el.value || '').trim(); });
      const name = values.guestName || '';
      // An untouched row is not an error — Tabbing off an empty quick-add row
      // should do nothing at all, not scold.
      const anything = Object.entries(values).some(([k, v]) => v && k !== 'status');
      if (!name) {
        if (!anything) return false;
        nameEl.classList.add('is-invalid');
        nameEl.focus();
        _showToast('Give this person a name before adding them', 'warning');
        return false;
      }

      busy = true;
      row.classList.add('is-saving');
      try {
        const token = await window.firebaseAuth.currentUser.getIdToken();
        // What the importer accepts for the main guest.
        const importRow = { guestName: name, attending: values.status || 'yes' };
        if (values.email) importRow.email = values.email;
        if (values.phone) importRow.phoneNumber = values.phone;
        if (values.dietary && importRow.attending !== 'no') importRow.dietary = values.dietary;
        // The corporate columns go in the SAME request now (task #625 r2). They
        // used to need a follow-up PUT because the import route dropped them —
        // which also meant the Add-guest dialog and a CSV import could not carry
        // them at all. The route allowlists them, so one call does the whole row.
        QA_DETAIL_KEYS.forEach(k => { if (values[k]) importRow[k] = values[k]; });
        const custom = {};
        cols.forEach(c => { if (c.customFieldId && values[c.key]) custom[c.customFieldId] = values[c.key]; });
        if (Object.keys(custom).length) importRow.customFields = custom;

        const res = await fetch(`/api/forms/${encodeURIComponent(state.formId)}/responses/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ mode: 'skip-dupes', rows: [importRow] })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) throw new Error(data.error || 'Could not add this guest');
        const result = (data.results || [])[0] || {};
        if (result.status === 'skipped') {
          _showToast(`${name} looks like somebody already on this list — nothing was added`, 'warning');
          nameEl.classList.add('is-invalid');
          nameEl.focus();
          return false;
        }
        if (result.status === 'invalid') throw new Error('That entry was rejected: ' + (result.reason || 'invalid'));

        _showToast(`${name} added`);
        clear();
        // Re-read from the server rather than pushing a guessed row into state:
        // the doc the server built is the one seating, check-in and the counters
        // must agree with (same reasoning as the Add-guest dialog).
        await _reloadResponses();
        _renderActiveTab();
        // _renderActiveTab replaced the row, so focus the NEW one.
        if (refocus) {
          const fresh = document.querySelector('tr.oed-quickadd .qa-input');
          if (fresh) fresh.focus();
        }
        return true;
      } catch (e) {
        _showToast('Could not add: ' + (e.message || e), 'danger');
        return false;
      } finally {
        busy = false;
        row.classList.remove('is-saving');
      }
    };

    inputs.forEach((el, i) => {
      el.addEventListener('input', () => { el.classList.remove('is-invalid'); syncDraft(); });
      el.addEventListener('change', syncDraft);
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); return; }
        if (e.key === 'Escape') { e.preventDefault(); clear(); focusFirst(); return; }
        // Tab off the LAST cell commits the row instead of jumping out of the
        // table — the whole point is not having to reach for the mouse.
        if (e.key === 'Tab' && !e.shiftKey && i === inputs.length - 1) {
          e.preventDefault();
          commit();
        }
      });
    });
    row.querySelector('.qa-commit')?.addEventListener('click', (e) => { e.preventDefault(); commit(); });
    root.querySelector('tr.oed-quickadd .qa-plus')?.addEventListener('click', focusFirst);

    // Clicking anywhere else adds the guest too (task #1106): typing a name
    // and clicking away used to leave it sitting in the row, unsaved, until
    // the next redraw emptied it - which read as "the row does nothing". A
    // row with no name is kept as typed and its Name cell marked, without
    // pulling the cursor back.
    row.addEventListener('focusout', (e) => {
      if (e.relatedTarget && row.contains(e.relatedTarget)) return;
      setTimeout(() => {
        if (!row.isConnected || row.contains(document.activeElement)) return;
        const values = typed();
        if (!hasTyped(values)) return;
        if (!String(values.guestName || '').trim()) { nameEl.classList.add('is-invalid'); return; }
        commit({ refocus: false });
      }, 0);
    });
  }

  function _wireSpreadsheetEdits(root) {
    const cols = _buildSpreadsheetColumns();
    const colByKey = Object.fromEntries(cols.map(c => [c.key, c]));

    _wireColumnResizers(root);
    _wireColumnRenames(root);
    _wireColumnMoves(root);
    _wireQuickAddRow(root, cols);

    root.querySelectorAll('input.cell-edit').forEach(input => {
      let initial = input.value;
      const wrap = input.closest('.cell-edit-wrap');
      const ghost = wrap?.querySelector('.cell-edit-ghost');
      input.addEventListener('focus', () => { state.editing = `${input.dataset.r}:${input.dataset.c}`; initial = input.value; });
      // Keep the ghost in sync so the cell grows as the user types
      // Keep the ghost in sync so the cell grows as the user types. Empty
      // value gets a space so the cell doesn't collapse on the next render.
      input.addEventListener('input', () => {
        if (ghost) ghost.textContent = input.value || ' ';
        // Keep the hover tooltip in sync (only present on capped cells)
        if (input.hasAttribute('title')) input.setAttribute('title', input.value);
      });
      input.addEventListener('blur', () => {
        state.editing = null;
        if (input.value !== initial) _saveCell(input.dataset.r, colByKey[input.dataset.c], input.value, input);
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter')  { e.preventDefault(); input.blur(); }
        if (e.key === 'Escape') {
          input.value = initial;
          if (ghost) ghost.textContent = initial || ' ';
          state.editing = null;
          input.blur();
        }
      });
    });

    root.querySelectorAll('select.cell-edit').forEach(sel => {
      const wrap = sel.closest('.cell-edit-wrap');
      const ghost = wrap?.querySelector('.cell-edit-ghost');
      // A Multiple choice cell, or one that takes a count per answer, opens
      // its editor on click instead of the dropdown (task #1100 r2). The
      // <select> stays as the cell's value holder: the editor writes the new
      // answer into it and fires 'change', which saves below. Keys that would
      // open the dropdown, or step its value while closed, open the editor too.
      if (sel.dataset.opensEditor) {
        sel.addEventListener('mousedown', (e) => {
          e.preventDefault();
          sel.focus();
          _openChoicePopover(sel, colByKey[sel.dataset.c]);
        });
        sel.addEventListener('keydown', (e) => {
          if (e.key === 'Tab' || e.key === 'Escape' || e.ctrlKey || e.metaKey) return;
          e.preventDefault();   // a closed select also steps on arrows and type-ahead
          if (['Enter', ' ', 'ArrowDown', 'ArrowUp', 'F4'].includes(e.key)) _openChoicePopover(sel, colByKey[sel.dataset.c]);
        });
      }
      sel.addEventListener('change', () => {
        const col = colByKey[sel.dataset.c];
        if (ghost) ghost.textContent = sel.options[sel.selectedIndex]?.text || sel.value;
        // Repaint the pill immediately — the row isn't re-rendered on an edit,
        // so without this the chip keeps the colour of the previous answer
        // until the next reload (task #623).
        if (_isChoiceColumn(col)) {
          const v = String(sel.value || '').trim();
          sel.className = sel.className.replace(/\s*cell-pill(-empty|-\d+)?\b/g, '');
          sel.classList.add(v ? 'cell-pill' : 'cell-pill-empty');
          if (v) sel.classList.add(`cell-pill-${_pillHue(v, col.customFieldOptions)}`);
          if (v) sel.setAttribute('title', v); else sel.removeAttribute('title');
          // The chip SHAPE lives on the cell and the wrapper, not on the
          // select: an answered cell centres its chip and lets it hug its own
          // text, an empty one keeps a full-width box to click. Answering an
          // empty cell left both in the empty shape until the next reload, so
          // the new chip genuinely did not match the column (task #623 r4).
          sel.closest('td')?.classList.toggle('cell-pill-cell', !!v);
          if (wrap) wrap.classList.toggle('is-pill-empty', !v);
        } else if (sel.dataset.c === 'status') {
          // Same repaint for the Attending/Declined chip — green ↔ rose has to
          // follow the answer without waiting for a reload (task #623 r2).
          const v = sel.value === 'yes' ? 'yes' : 'no';
          sel.classList.remove('cell-pill-status-yes', 'cell-pill-status-no');
          sel.classList.add('cell-pill', `cell-pill-status-${v}`);
        }
        _saveCell(sel.dataset.r, colByKey[sel.dataset.c], sel.value, sel);
      });
    });

    // Expand button on capped cells → opens a popover textarea so the user
    // can read/edit long values without having to scroll inside a 320px input.
    root.querySelectorAll('.cell-edit-expand').forEach(btn => {
      // mousedown.preventDefault keeps focus on the underlying input (or
      // nothing), so its blur-save doesn't fire before the popover opens
      btn.addEventListener('mousedown', (e) => e.preventDefault());
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const input = btn.parentElement.querySelector('input.cell-edit');
        if (input) _openCellPopover(input, colByKey[btn.dataset.c]);
      });
    });
  }

  /**
   * Floating textarea editor for capped cells (Notes + custom fields).
   * Anchors next to the cell, save on outside-click / Cmd+Enter, cancel on Esc.
   */
  function _openCellPopover(input, col) {
    if (!col) return;
    // Close any existing popover first
    document.querySelector('.cell-edit-popover')?.remove();

    const wrap = input.closest('.cell-edit-wrap');
    const ghost = wrap?.querySelector('.cell-edit-ghost');
    const initial = input.value;
    state.editing = `${input.dataset.r}:${input.dataset.c}:popover`;

    const popover = document.createElement('div');
    popover.className = 'cell-edit-popover';
    popover.innerHTML = `
      <div class="cell-edit-popover-header">
        <span class="cell-edit-popover-label">${_esc(col.label || col.key)}</span>
        <button type="button" class="cell-edit-popover-close" title="Close (Esc)" aria-label="Close">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <textarea class="cell-edit-popover-textarea" spellcheck="true"></textarea>
      <div class="cell-edit-popover-footer">
        <span class="cell-edit-popover-hint">Esc to cancel · ⌘/Ctrl + Enter to save</span>
        <button type="button" class="btn btn-org-primary btn-xs cell-edit-popover-save">Save</button>
      </div>`;

    document.body.appendChild(popover);
    const ta = popover.querySelector('.cell-edit-popover-textarea');
    ta.value = initial;

    // Position below the cell, clamped to viewport
    const rect = input.getBoundingClientRect();
    const popW = Math.max(rect.width, 380);
    const popH = 200; // approximate
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = rect.left + window.scrollX;
    let top  = rect.bottom + window.scrollY + 4;
    if (left + popW > window.scrollX + vw - 12) left = window.scrollX + vw - popW - 12;
    if (rect.bottom + popH > vh - 12) top = rect.top + window.scrollY - popH - 4;
    popover.style.left = `${Math.max(window.scrollX + 8, left)}px`;
    popover.style.top = `${Math.max(window.scrollY + 8, top)}px`;
    popover.style.width = `${popW}px`;

    setTimeout(() => { ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length); }, 0);

    const close = (save) => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      state.editing = null;
      const next = ta.value;
      popover.remove();
      if (save && next !== initial) {
        input.value = next;
        if (ghost) ghost.textContent = next || ' ';
        if (input.hasAttribute('title')) input.setAttribute('title', next);
        _saveCell(input.dataset.r, col, next, input);
      }
    };

    const onDocMouseDown = (e) => {
      if (!popover.contains(e.target)) close(true);
    };
    // Defer one tick so the click that opened us doesn't immediately close us
    setTimeout(() => document.addEventListener('mousedown', onDocMouseDown, true), 0);

    popover.querySelector('.cell-edit-popover-save').addEventListener('click', () => close(true));
    popover.querySelector('.cell-edit-popover-close').addEventListener('click', () => close(false));
    ta.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(false); }
      else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); close(true); }
    });
  }

  // ---- Multiple choice cells (task #1100) ---------------------------------
  //
  // A Multiple choice answer is "Chicken, Beef", or on a per-guest question
  // "2 Chicken, 1 Beef" - the text the guest form stores. Options are matched
  // whole, between ", " separators, because an option may itself contain a
  // comma ("Allocator of Capital (e.g., Family Office, ...)").
  const _reEsc = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  function _choicePicked(text, opt) {
    return (', ' + String(text || '') + ', ').includes(', ' + opt + ', ');
  }

  /**
   * The count per answer in a cell's text: "2 Chicken, 1 Beef" -> {Chicken: 2,
   * Beef: 1}. A single bare option on a party is the whole party's pick
   * ("Beef" on a party of 3 -> {Beef: 3}). null when nothing matches.
   */
  function _choiceCountsFromText(text, options, party) {
    const t = String(text || '').trim();
    if (!t) return null;
    const out = {};
    (options || []).forEach((opt) => {
      const m = t.match(new RegExp('(?:^|, )(\\d+) ' + _reEsc(opt) + '(?=, |$)'));
      if (m && parseInt(m[1], 10) > 0) out[opt] = parseInt(m[1], 10);
    });
    if (Object.keys(out).length) return out;
    if ((options || []).includes(t)) return { [t]: Math.max(1, party || 1) };
    return null;
  }

  /**
   * The editor for a Multiple choice cell: a tick per option, or on a
   * per-guest question with a party of 2+, a count per option. Opened by a
   * click on the cell (_choiceOpensEditor); saves through the same _saveCell
   * as every other cell.
   */
  function _openChoicePopover(sel, col) {
    if (!col) return;
    document.querySelector('.cell-edit-popover')?.remove();
    const r = state.responses.find(x => x.id === sel.dataset.r);
    if (!r) return;
    const opts = col.customFieldOptions || [];
    const counted = _choiceTakesCounts(col, r);
    const party = _partySize(r);
    const initial = String(_readCell(r, col) ?? '').trim();
    const stored = r.customFieldsData?.[col.customFieldId];
    const storedCounts = stored && typeof stored === 'object' && stored.perGuestQuantities
      && typeof stored.perGuestQuantities === 'object' ? stored.perGuestQuantities : null;
    const prefill = counted ? (storedCounts || _choiceCountsFromText(initial, opts, party) || {}) : null;
    state.editing = `${sel.dataset.r}:${sel.dataset.c}:popover`;

    const rows = opts.map((o, i) => counted
      ? `<label class="d-flex align-items-center justify-content-between gap-2 py-1">
           <span>${_esc(o)}</span>
           <input type="number" class="form-control form-control-sm oed-choice-count" data-i="${i}" min="0" max="${party}" step="1" inputmode="numeric" style="width:5rem" value="${parseInt(prefill[o], 10) || 0}">
         </label>`
      : `<label class="form-check d-flex align-items-center gap-2 py-1 mb-0">
           <input type="checkbox" class="form-check-input mt-0 oed-choice-tick" data-i="${i}"${_choicePicked(initial, o) ? ' checked' : ''}>
           <span>${_esc(o)}</span>
         </label>`).join('');

    const popover = document.createElement('div');
    popover.className = 'cell-edit-popover';
    popover.setAttribute('role', 'dialog');
    popover.setAttribute('aria-label', col.label || col.key);
    popover.innerHTML = `
      <div class="cell-edit-popover-header">
        <span class="cell-edit-popover-label">${_esc(col.label || col.key)}</span>
        <button type="button" class="cell-edit-popover-close" title="Close (Esc)" aria-label="Close">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      ${counted ? `<div class="small text-muted mb-1">How many of the party of ${party} chose each</div>` : ''}
      <div class="oed-choice-list" style="max-height:260px;overflow:auto">${rows}</div>
      <div class="cell-edit-popover-footer">
        <span class="cell-edit-popover-hint">Esc to cancel</span>
        <button type="button" class="btn btn-org-primary btn-xs cell-edit-popover-save">Save</button>
      </div>`;
    document.body.appendChild(popover);

    const rect = sel.getBoundingClientRect();
    const popW = Math.min(Math.max(rect.width, 300), window.innerWidth - 16);
    const popH = Math.min(340, 120 + opts.length * 34);
    let left = rect.left + window.scrollX;
    let top = rect.bottom + window.scrollY + 4;
    if (left + popW > window.scrollX + window.innerWidth - 8) left = window.scrollX + window.innerWidth - popW - 8;
    if (rect.bottom + popH > window.innerHeight - 12) top = rect.top + window.scrollY - popH - 4;
    popover.style.left = `${Math.max(window.scrollX + 8, left)}px`;
    popover.style.top = `${Math.max(window.scrollY + 8, top)}px`;
    popover.style.width = `${popW}px`;
    setTimeout(() => popover.querySelector('input')?.focus(), 0);

    const read = () => counted
      ? Array.from(popover.querySelectorAll('.oed-choice-count'))
          .map(inp => ({ o: opts[+inp.dataset.i], n: Math.max(0, parseInt(inp.value, 10) || 0) }))
          .filter(x => x.n > 0).map(x => `${x.n} ${x.o}`).join(', ')
      : Array.from(popover.querySelectorAll('.oed-choice-tick:checked'))
          .map(inp => opts[+inp.dataset.i]).join(', ');

    const close = (save) => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      state.editing = null;
      const next = read();
      popover.remove();
      if (!save || next === initial) return;
      // The dropdown shows the new answer: an answer that is not a single
      // option gets its own entry, replacing the old one.
      Array.from(sel.options).forEach((op) => {
        if (op.value && !opts.includes(op.value)) op.remove();
      });
      if (next && !opts.includes(next)) {
        const op = document.createElement('option');
        op.value = next; op.textContent = next;
        sel.insertBefore(op, sel.options[1] || null);
      }
      sel.value = next;
      sel.dispatchEvent(new Event('change'));
    };
    const onDocMouseDown = (e) => { if (!popover.contains(e.target)) close(true); };
    setTimeout(() => document.addEventListener('mousedown', onDocMouseDown, true), 0);
    popover.querySelector('.cell-edit-popover-save').addEventListener('click', () => close(true));
    popover.querySelector('.cell-edit-popover-close').addEventListener('click', () => close(false));
    popover.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(false); }
      else if (e.key === 'Enter') { e.preventDefault(); close(true); }
    });
  }

  async function _saveCell(responseId, col, newValue, el) {
    if (!col) return;
    const r = state.responses.find(x => x.id === responseId);
    if (!r) return;

    const payload = _writeCellPayload(col, newValue, r);

    const wrap = el.closest && el.closest('.cell-edit-wrap');
    el.classList.remove('cell-saved', 'cell-error');
    if (wrap) wrap.classList.remove('has-save-error');
    // A chip's title is its full (possibly clipped) answer. A failed save
    // borrows it for the reason, so put the answer back before trying again.
    if (el.dataset.titleBeforeError !== undefined) {
      if (el.dataset.titleBeforeError) el.setAttribute('title', el.dataset.titleBeforeError);
      else el.removeAttribute('title');
      delete el.dataset.titleBeforeError;
    }
    el.classList.add('cell-saving');
    _noteLocalWrite();
    try {
      const token = await window.firebaseAuth.currentUser.getIdToken();
      const res = await fetch(
        `/api/forms/${encodeURIComponent(state.formId)}/responses/${encodeURIComponent(responseId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload)
        }
      );
      if (!res.ok) throw new Error('Save failed (' + res.status + ')');

      // Merge into local state so the row keeps the new value when re-rendered
      Object.assign(r, payload);
      _noteLocalWrite();

      el.classList.remove('cell-saving');
      el.classList.add('cell-saved');
      setTimeout(() => el.classList.remove('cell-saved'), 1200);

      // Status / party-size edits change the stats strip and capacity bar — re-render
      if (col.key === 'status' || col.key === 'numberOfAdults' || col.key === 'numberOfChildren') {
        _renderActiveTab();
      }
    } catch (err) {
      console.error('Inline save failed:', err);
      el.classList.remove('cell-saving');
      el.classList.add('cell-error');
      // A failed save is the one state that must survive the host looking away,
      // so it is marked on the cell itself and not only in a toast that fades:
      // a dashed rose outline, a "!" on the chip, and the reason on hover. It
      // clears on the next attempt at that cell (task #623 r5).
      if (wrap) wrap.classList.add('has-save-error');
      el.dataset.titleBeforeError = el.getAttribute('title') || '';
      el.setAttribute('title', 'Not saved: ' + err.message + ' - change it again to retry.');
      _showToast('Could not save: ' + err.message, 'danger');
    }
  }

  function _capacityBar(filled, capacity) {
    const pct = Math.min(100, Math.round((filled / capacity) * 100));
    const over = filled > capacity;
    const label = `${filled} / ${capacity} spots ${over ? '(over capacity)' : `(${pct}% full)`}`;
    return `
      <div class="event-detail-capacity">
        <div class="capacity-header"><span>Capacity</span><span class="capacity-meta">${label}</span></div>
        <div class="capacity-bar"><div class="capacity-fill ${over ? 'over' : ''}" style="width:${Math.min(100, pct)}%;"></div></div>
      </div>`;
  }


  // ============================================================================
  // ANALYTICS — dedicated tab with charts + insights
  // ============================================================================

  function _renderAnalytics(el) {
    // Not "No data yet" when the replies are there but wait for a subscription.
    if (_paywalled()) { el.innerHTML = _paywallHtml(); return; }
    if (state.responses.length === 0) {
      el.innerHTML = `
        <div class="event-detail-empty">
          <i class="bi bi-graph-up"></i>
          <p>No data yet. Analytics will appear once responses start coming in.</p>
        </div>`;
      return;
    }

    const stats = _statsForResponses(state.responses);
    const insights = _computeInsights(state.responses, state.form);

    el.innerHTML = `
      <div class="analytics-insights">
        ${insights.map(_renderInsightCard).join('')}
      </div>

      <div class="analytics-grid">
        <div class="analytics-card analytics-card-wide">
          ${_timelineChart(state.responses, 14)}
        </div>
        <div class="analytics-card">
          ${_attendanceDonut(stats)}
        </div>
        <div class="analytics-card">
          ${_partyHistogram(state.responses)}
        </div>
      </div>`;
  }

  function _computeInsights(responses, form) {
    const cards = [];

    // Days until event
    // Local calendar day: new Date('YYYY-MM-DD') is UTC midnight, the day
    // before west of Greenwich.
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(form?.eventDate || ''));
    const eventDate = ymd ? new Date(+ymd[1], ymd[2] - 1, +ymd[3]) : (form?.eventDate ? new Date(form.eventDate) : null);
    if (eventDate && !isNaN(eventDate)) {
      // Midnight to midnight, rounded: a 25-hour fall-back day made the
      // ceiling of (event - now) one too high in the first hour of a day.
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const days = Math.round((new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()) - today) / (1000 * 60 * 60 * 24));
      if (days === 0) {
        cards.push({ icon: 'bi-calendar-event', tone: 'urgent', kicker: 'Event day', value: 'Today', sub: 'Good luck!' });
      } else if (days > 0) {
        const tone = days <= 3 ? 'urgent' : days <= 7 ? 'warn' : 'neutral';
        cards.push({ icon: 'bi-calendar-event', tone, kicker: 'Days until event', value: String(days), sub: days === 1 ? 'day' : 'days' });
      }
    }

    // Peak response day of week
    const dayCounts = [0,0,0,0,0,0,0];
    responses.forEach(r => {
      const d = _toDate(r.submittedAt || r.createdAt);
      if (d) dayCounts[d.getDay()]++;
    });
    const peakDayIdx = dayCounts.indexOf(Math.max(...dayCounts));
    if (dayCounts[peakDayIdx] > 0) {
      const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const pct = Math.round((dayCounts[peakDayIdx] / responses.length) * 100);
      cards.push({ icon: 'bi-calendar-week', tone: 'neutral', kicker: 'Peak response day', value: dayNames[peakDayIdx], sub: `${pct}% of responses` });
    }

    // Peak response hour
    const hourCounts = new Array(24).fill(0);
    responses.forEach(r => {
      const d = _toDate(r.submittedAt || r.createdAt);
      if (d) hourCounts[d.getHours()]++;
    });
    const peakHour = hourCounts.indexOf(Math.max(...hourCounts));
    if (hourCounts[peakHour] > 0) {
      const fmt = (h) => {
        const period = h < 12 ? 'am' : 'pm';
        const hh = h % 12 || 12;
        return `${hh} ${period}`;
      };
      cards.push({ icon: 'bi-clock', tone: 'neutral', kicker: 'Peak response hour', value: `${fmt(peakHour)}–${fmt((peakHour + 1) % 24)}`, sub: `${hourCounts[peakHour]} responses` });
    }

    // Response momentum — last 7 days vs prior 7 days
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let last7 = 0, prior7 = 0;
    responses.forEach(r => {
      const d = _toDate(r.submittedAt || r.createdAt);
      if (!d) return;
      const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const diff = Math.floor((today - dayStart) / (24 * 60 * 60 * 1000));
      if (diff >= 0 && diff < 7) last7++;
      else if (diff >= 7 && diff < 14) prior7++;
    });
    if (last7 + prior7 > 0) {
      let label, tone, sub;
      if (prior7 === 0 && last7 > 0) { label = 'Accelerating'; tone = 'good'; sub = `${last7} new this week`; }
      else if (last7 > prior7 * 1.2) { label = 'Accelerating'; tone = 'good'; sub = `+${Math.round(((last7 - prior7) / prior7) * 100)}% vs prior week`; }
      else if (last7 < prior7 * 0.8) { label = 'Slowing'; tone = 'warn'; sub = `${Math.round(((prior7 - last7) / prior7) * 100)}% fewer this week`; }
      else { label = 'Steady'; tone = 'neutral'; sub = `${last7} this week · ${prior7} prior`; }
      cards.push({ icon: 'bi-speedometer2', tone, kicker: 'Response momentum', value: label, sub });
    }

    // Last response
    const mostRecent = responses
      .map(r => _toDate(r.submittedAt || r.createdAt))
      .filter(d => d)
      .sort((a, b) => b - a)[0];
    if (mostRecent) {
      const mins = Math.floor((Date.now() - mostRecent) / 60000);
      let value, sub;
      if (mins < 1) { value = 'Just now'; sub = ''; }
      else if (mins < 60) { value = `${mins} min ago`; sub = 'Last response'; }
      else if (mins < 60 * 24) { value = `${Math.floor(mins / 60)} hr ago`; sub = 'Last response'; }
      else { value = `${Math.floor(mins / (60 * 24))} days ago`; sub = 'Last response'; }
      cards.push({ icon: 'bi-lightning-charge', tone: 'neutral', kicker: 'Last response', value, sub });
    }

    // Today's count
    const todayCount = responses.filter(r => {
      const d = _toDate(r.submittedAt || r.createdAt);
      if (!d) return false;
      return d.toDateString() === now.toDateString();
    }).length;
    if (todayCount > 0) {
      cards.push({ icon: 'bi-sun', tone: 'neutral', kicker: 'Today', value: String(todayCount), sub: todayCount === 1 ? 'response' : 'responses' });
    }

    return cards;
  }

  function _renderInsightCard(c) {
    return `
      <div class="analytics-insight tone-${c.tone}">
        <div class="insight-icon"><i class="bi ${c.icon}"></i></div>
        <div class="insight-body">
          <div class="insight-kicker">${_esc(c.kicker)}</div>
          <div class="insight-value">${_esc(c.value)}</div>
          ${c.sub ? `<div class="insight-sub">${_esc(c.sub)}</div>` : ''}
        </div>
      </div>`;
  }

  function _toDate(v) {
    const d = v?.toDate?.() || (v?._seconds ? new Date(v._seconds * 1000) : new Date(v));
    return isNaN(d) ? null : d;
  }

  function _timelineChart(responses, days) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const buckets = new Array(days).fill(0);
    responses.forEach(r => {
      const d = _toDate(r.submittedAt || r.createdAt);
      if (!d) return;
      const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const diff = Math.floor((today - dayStart) / (24 * 60 * 60 * 1000));
      if (diff >= 0 && diff < days) buckets[days - 1 - diff]++;
    });
    const max = Math.max(1, ...buckets);
    const total = buckets.reduce((a, b) => a + b, 0);

    const dayLabel = (idx) => {
      const d = new Date(today);
      d.setDate(d.getDate() - (days - 1 - idx));
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    };

    const bars = buckets.map((v, i) => {
      const pct = (v / max) * 100;
      const isToday = i === days - 1;
      return `<div class="tl-bar-wrap" title="${_esc(dayLabel(i))} — ${v} response${v === 1 ? '' : 's'}">
        <div class="tl-bar ${isToday ? 'is-today' : ''}" style="height:${Math.max(2, pct)}%;">
          ${v > 0 ? `<span class="tl-bar-val">${v}</span>` : ''}
        </div>
      </div>`;
    }).join('');

    return `
      <div class="analytics-card-head">
        <h4>Response timeline</h4>
        <span class="analytics-card-meta">${total} in last ${days} days</span>
      </div>
      <div class="tl-bars">${bars}</div>
      <div class="tl-axis">
        <span>${dayLabel(0)}</span>
        <span>${dayLabel(Math.floor(days / 2))}</span>
        <span>${dayLabel(days - 1)}</span>
      </div>`;
  }

  function _attendanceDonut(stats) {
    const total = stats.yes + stats.no + stats.pending + stats.waitlist;
    if (total === 0) return '<div class="analytics-empty">No responses yet.</div>';

    // Segments in draw order (yes first = bottom of stack)
    const segs = [
      { label: 'Attending', value: stats.yes, cls: 'seg-yes' },
      { label: 'Declined', value: stats.no, cls: 'seg-no' },
      { label: 'Pending', value: stats.pending, cls: 'seg-pending' },
      { label: 'Waitlist', value: stats.waitlist, cls: 'seg-waitlist' }
    ].filter(s => s.value > 0);

    const C = 113.1; // circumference of r=18 circle
    let offset = 0;
    const rings = segs.map((s, i) => {
      const pct = s.value / total;
      const dash = pct * C;
      const ring = `<circle class="donut-ring ${s.cls}" cx="21" cy="21" r="18" fill="none" stroke-width="5"
        stroke-dasharray="${dash.toFixed(2)} ${(C - dash).toFixed(2)}"
        stroke-dashoffset="${(-offset).toFixed(2)}" transform="rotate(-90 21 21)"/>`;
      offset += dash;
      return ring;
    }).join('');

    const legend = segs.map(s => `
      <div class="donut-legend-row">
        <span class="donut-swatch ${s.cls}"></span>
        <span class="donut-label">${_esc(s.label)}</span>
        <span class="donut-count">${s.value} <span class="text-muted">· ${Math.round((s.value / total) * 100)}%</span></span>
      </div>`).join('');

    const attendingPct = Math.round((stats.yes / total) * 100);

    return `
      <div class="analytics-card-head">
        <h4>Attendance</h4>
        <span class="analytics-card-meta">${total} responses</span>
      </div>
      <div class="donut-wrap">
        <svg viewBox="0 0 42 42" class="donut-svg" aria-label="Attendance breakdown">
          <circle cx="21" cy="21" r="18" fill="none" stroke="var(--org-surface-sub)" stroke-width="5"/>
          ${rings}
        </svg>
        <div class="donut-center">
          <div class="donut-center-value">${attendingPct}%</div>
          <div class="donut-center-label">attending</div>
        </div>
      </div>
      <div class="donut-legend">${legend}</div>`;
  }

  function _partyHistogram(responses) {
    const attending = responses.filter(r => _normalizeStatus(r) === 'yes');
    if (attending.length === 0) return '<div class="analytics-empty">No attendees yet.</div>';

    const buckets = new Map();
    attending.forEach(r => {
      const additionals = Math.max(0, _partySize(r) - 1);
      buckets.set(additionals, (buckets.get(additionals) || 0) + 1);
    });
    const sorted = [...buckets.entries()].sort((a, b) => a[0] - b[0]);
    const max = Math.max(...sorted.map(([, v]) => v));
    const totalGuests = attending.reduce((sum, r) => sum + _partySize(r), 0);
    const avgParty = (totalGuests / attending.length).toFixed(2);

    const rows = sorted.map(([count, num]) => {
      const pct = (num / max) * 100;
      const label = count === 0 ? 'Solo' : count === 1 ? '+1' : `+${count}`;
      return `
        <div class="party-row">
          <span class="party-label">${label}</span>
          <div class="party-bar"><div class="party-fill" style="width:${pct}%;"></div></div>
          <span class="party-count">${num}</span>
        </div>`;
    }).join('');

    return `
      <div class="analytics-card-head">
        <h4>Party size</h4>
        <span class="analytics-card-meta">avg ${avgParty} · ${totalGuests} total</span>
      </div>
      <div class="party-rows">${rows}</div>`;
  }

  // ============================================================================
  // ADD GUEST BY HAND (task #495)
  // ============================================================================
  //
  // Hosts collect RSVPs off-platform all the time — a phone call, a reply to a
  // group email, a list a colleague sends over. Until now the only way to get
  // those people into the event was to fill in the public form on their behalf.
  //
  // This posts a ONE-ROW import to /api/forms/:formId/responses/import rather
  // than writing a response doc from the browser. That endpoint already
  // allowlists every field, resolves custom-field labels to their real ids,
  // dedupes against existing guests, and stamps both `submittedAt` and
  // `timestamp` (a manually-added guest was once invisible in the list for
  // want of the second one). Re-implementing any of that here would be a
  // second, weaker copy of a path that is already hardened.

  /**
   * Remove the Add-guest modal and everything Bootstrap hung off it.
   *
   * Deliberately does NOT depend on `hide()` completing or on
   * `hidden.bs.modal` firing. This markup is rebuilt on every open and thrown
   * away on every close, so tearing it down directly is safe — and it is the
   * only way to guarantee the host is never left facing a dead grey backdrop
   * over a page that will not respond, which is what happened when the cleanup
   * was left to the animation callback. Ripping out a still-shown modal's
   * element also strands its backdrop, so those are cleared unconditionally.
   */
  function _teardownAddGuest() {
    const el = document.getElementById('addGuestModal');
    if (el) {
      try { bootstrap.Modal.getInstance(el)?.dispose(); } catch (e) { /* already disposed */ }
      el.remove();
    }
    document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove());
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('overflow');
  }

  function openAddGuest() {
    _teardownAddGuest();
    // Message fields are display-only; nothing to collect for them.
    const fields = (state.form?.customFields || []).filter(f => f && f.id && f.type !== 'message');
    const separateKids = state.form?.separateAdultsChildren === true;
    // Dietary is a BUILT-IN field, not one of the event's own questions, so it
    // was absent from this dialog entirely — a guest added by hand could never
    // carry one, even though the sheet has a Dietary column that shows and
    // edits it (task #495 round 5, "Dietary requirements are missing").
    // Default true, matching every other reader of this flag.
    const includeDietary = state.form?.includeDietary !== false;
    // The corporate columns (task #625 round 2, "import and add guest should
    // also include all the questions on the form"). Round 3: the dialog asks for
    // exactly the columns THIS event has — the same rule the sheet and the
    // export now follow (_detailColumnOn), so a form that never asked about
    // Transport or Department stops offering them anywhere.
    const CORPORATE_FIELDS = [
      { key: 'company',        label: 'Company' },
      { key: 'jobTitle',       label: 'Job title' },
      { key: 'department',     label: 'Department' },
      { key: 'transportation', label: 'Transport' },
      { key: 'notes',          label: 'Notes' },
      { key: 'heardAboutUs',   label: 'Heard about us' }
    ].filter((f) => _detailColumnOn(f.key));
    const corporateHtml = CORPORATE_FIELDS.length ? `
              <div class="row g-2 mb-2">
                ${CORPORATE_FIELDS.map((f) => `
                <div class="col-6">
                  <label class="form-label small" for="ag_${f.key}">${_esc(f.label)}</label>
                  <input type="text" class="form-control form-control-sm ag-detail" id="ag_${f.key}" data-detail-key="${f.key}">
                </div>`).join('')}
              </div>` : '';
    // When the event asks each named guest for their own meal, this dialog does
    // too — the top box becomes the main guest's, and every extra head gets one.
    const perGuestDietary = includeDietary && _agMainDietaryIsPerGuest();
    const dietaryOptions = (Array.isArray(state.form?.dietaryOptions) ? state.form.dietaryOptions : [])
      .map((o) => (o && typeof o === 'object' ? (o.label || o.value || '') : String(o == null ? '' : o)))
      .map((s) => s.trim())
      .filter(Boolean);

    // A question asked of EACH guest is the main guest's own answer, so its box
    // sits with their details, above Status / Party size, and is a dropdown
    // like the one in each extra guest's card (#1104 round 2: at the bottom,
    // as ticks, it read as the last extra guest's). A question asked once of
    // the party stays at the bottom.
    const perGuestIds = new Set(_agPerGuestQuestions().map((f) => f.id));
    const fieldControl = (f) => {
      const id = `ag_cf_${_esc(String(f.id))}`;
      const label = _esc(f.label || 'Question');
      const opts = Array.isArray(f.options) ? f.options : [];
      if (f.type === 'select' || f.type === 'radio' || (perGuestIds.has(f.id) && opts.length)) {
        return `<div class="mb-2">
          <label class="form-label small" for="${id}">${label}</label>
          <select class="form-select form-select-sm ag-custom" id="${id}" data-field-id="${_esc(String(f.id))}">
            <option value="">—</option>
            ${opts.map(o => `<option value="${_esc(String(o))}">${_esc(String(o))}</option>`).join('')}
          </select></div>`;
      }
      if (f.type === 'checkbox') {
        return `<div class="mb-2">
          <label class="form-label small">${label}</label>
          <div>${opts.map((o, i) => `
            <div class="form-check form-check-inline">
              <input class="form-check-input ag-custom-check" type="checkbox" id="${id}_${i}"
                     data-field-id="${_esc(String(f.id))}" value="${_esc(String(o))}">
              <label class="form-check-label small" for="${id}_${i}">${_esc(String(o))}</label>
            </div>`).join('')}</div></div>`;
      }
      if (f.type === 'textarea') {
        return `<div class="mb-2">
          <label class="form-label small" for="${id}">${label}</label>
          <textarea class="form-control form-control-sm ag-custom" id="${id}" rows="2" data-field-id="${_esc(String(f.id))}"></textarea></div>`;
      }
      return `<div class="mb-2">
        <label class="form-label small" for="${id}">${label}</label>
        <input type="text" class="form-control form-control-sm ag-custom" id="${id}" data-field-id="${_esc(String(f.id))}"></div>`;
    };
    const mainGuestQsHtml = fields.filter((f) => perGuestIds.has(f.id)).map(fieldControl).join('');
    const fieldHtml = fields.filter((f) => !perGuestIds.has(f.id)).map(fieldControl).join('');

    const html = `
      <div class="modal fade" id="addGuestModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" style="font-size:1rem;"><i class="bi bi-person-plus me-2"></i>Add a guest</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <p class="small text-body-secondary">Records an RSVP you already have. Nothing is emailed to them — this only adds them to your list, your counts, seating and check-in.</p>
              <div class="mb-2">
                <label class="form-label small" for="agName">Name <span class="text-danger">*</span></label>
                <input type="text" class="form-control form-control-sm" id="agName" autocomplete="name">
              </div>
              <div class="row g-2 mb-2">
                <div class="col-7">
                  <label class="form-label small" for="agEmail">Email</label>
                  <input type="email" class="form-control form-control-sm" id="agEmail" autocomplete="email">
                </div>
                <div class="col-5">
                  <label class="form-label small" for="agPhone">Phone</label>
                  <input type="tel" class="form-control form-control-sm" id="agPhone" dir="ltr" autocomplete="tel">
                </div>
              </div>
              <!-- The main guest's own Company / Job title sit with their contact
                   details, above the party: below "Who else is coming?" they read
                   as the last extra guest's (task #1104). -->
              ${corporateHtml}
              ${mainGuestQsHtml}
              <div class="row g-2 mb-2">
                <div class="col-6">
                  <label class="form-label small" for="agStatus">Status</label>
                  <select class="form-select form-select-sm" id="agStatus">
                    <option value="yes" selected>Attending</option>
                    <option value="maybe">Maybe</option>
                    <option value="no">Declined</option>
                  </select>
                </div>
                ${separateKids ? `
                <div class="col-3">
                  <label class="form-label small" for="agAdults">Adults</label>
                  <input type="number" class="form-control form-control-sm" id="agAdults" value="1" min="1" max="50">
                </div>
                <div class="col-3">
                  <label class="form-label small" for="agChildren">Children</label>
                  <input type="number" class="form-control form-control-sm" id="agChildren" value="0" min="0" max="50">
                </div>` : `
                <div class="col-6">
                  <label class="form-label small" for="agParty">Party size</label>
                  <input type="number" class="form-control form-control-sm" id="agParty" value="1" min="1" max="50">
                </div>`}
              </div>
              ${includeDietary ? `
              <div class="row g-2 mb-2" id="agDietaryRow">
                <div class="col-6">
                  <!-- With per-guest dietary on, this box is the MAIN guest's own
                       meal, not the party's — every other head gets its own below.
                       The label says so, otherwise a host reads one requirement as
                       covering all four of them (task #495 round 6). -->
                  <label class="form-label small" for="agDietary">${perGuestDietary ? 'Dietary requirements (this guest)' : 'Dietary requirements'}</label>
                  ${perGuestDietary ? _agDietarySelectHtml('agDietary', '') : (dietaryOptions.length ? `
                  <select class="form-select form-select-sm" id="agDietary">
                    <option value="">—</option>
                    ${dietaryOptions.map((o) => `<option value="${_esc(o)}">${_esc(o)}</option>`).join('')}
                  </select>` : `
                  <input type="text" class="form-control form-control-sm" id="agDietary" placeholder="None, Vegetarian, etc.">`)}
                </div>
                <div class="col-6">
                  <label class="form-label small" for="agDietaryDetails">Dietary details</label>
                  <input type="text" class="form-control form-control-sm" id="agDietaryDetails" placeholder="Allergies, anything else">
                </div>
              </div>` : ''}
              <!-- One name box per extra head, appearing as the count goes up.
                   Optional: a host recording "the Cohens, four of them" often
                   knows the number before the names. What they do fill in feeds
                   seating and the check-in list, exactly as a guest's own
                   answers would. -->
              <div class="mb-2" id="agGuestNamesWrap" style="display:none;">
                <div class="small text-body-secondary mb-1" id="agGuestNamesHint"></div>
                <div id="agGuestNames"></div>
              </div>
              ${fieldHtml ? `<hr class="my-3"><div class="small text-body-secondary mb-2">Your event's own questions</div>${fieldHtml}` : ''}
              <div class="alert alert-danger py-2 mb-0 mt-2" id="agError" style="display:none;font-size:0.85rem;"></div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-org-ghost btn-sm" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-org-primary btn-sm" id="agSave">Add guest</button>
            </div>
          </div>
        </div>
      </div>`;

    document.body.insertAdjacentHTML('beforeend', html);
    const modalEl = document.getElementById('addGuestModal');
    const modal = new bootstrap.Modal(modalEl);
    // Bootstrap's own modal events are NOT usable here. Measured on this page:
    // neither `hide.bs.modal` nor `hidden.bs.modal` ever reaches a listener,
    // even on a direct `.hide()` call, so a teardown hung off either of them
    // never runs — leaving the dialog on screen behind a live backdrop, over a
    // page that will not respond to a click. Every way out is therefore wired
    // directly: the two dismiss controls, Escape, and a click on the backdrop.
    modalEl.addEventListener('hide.bs.modal', _teardownAddGuest);   // harmless if it ever starts working
    modalEl.querySelectorAll('[data-bs-dismiss="modal"]').forEach((b) => {
      b.addEventListener('click', (e) => { e.preventDefault(); _teardownAddGuest(); });
    });
    modalEl.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); _teardownAddGuest(); }
    });
    modalEl.addEventListener('click', (e) => {
      // Only the backdrop itself — a click on the dialog must not close it.
      if (e.target === modalEl) _teardownAddGuest();
    });
    modalEl.querySelector('#agSave').addEventListener('click', () => _submitAddGuest(modalEl));

    // The name boxes follow the count live. `input` as well as `change`, or a
    // typed digit wouldn't register until the field lost focus.
    ['#agParty', '#agAdults', '#agChildren'].forEach((sel) => {
      const el = modalEl.querySelector(sel);
      if (!el) return;
      ['input', 'change'].forEach((ev) =>
        el.addEventListener(ev, () => _renderAgGuestNames(modalEl, separateKids)));
    });
    _renderAgGuestNames(modalEl, separateKids);

    // A declined guest is not eating — hide the dietary row rather than leaving
    // a question the host has to decide to ignore. Same intent as the main
    // dashboard's Add-guest dialog, which nests it under the attending fields.
    const agStatus = modalEl.querySelector('#agStatus');
    const agDietaryRow = modalEl.querySelector('#agDietaryRow');
    if (agStatus && agDietaryRow) {
      const syncDietaryRow = () => { agDietaryRow.style.display = agStatus.value === 'no' ? 'none' : ''; };
      agStatus.addEventListener('change', syncDietaryRow);
      syncDietaryRow();
    }
    // The per-guest meal boxes follow the same rule, and they live inside the
    // guest cards — so the cards have to be redrawn when the status changes.
    if (agStatus && _agPerGuestDietary()) {
      agStatus.addEventListener('change', () => _renderAgGuestNames(modalEl, separateKids));
    }

    modal.show();
    setTimeout(() => modalEl.querySelector('#agName')?.focus(), 300);
  }

  /**
   * The per-guest fields offered for guests 2, 3, … besides the name.
   *
   * Keys match `guestDetailFields` on the form and the `additionalGuestDetails`
   * entries the public corporate form already writes — so a party added here is
   * stored in exactly the shape the attendee-details card already renders.
   */
  /**
   * The dietary choices this event offers, as {key, label}.
   *
   * `key` is the canonical storage value — the SAME slug the public form writes
   * into `individualNames[].dietary` (see normalizeDietaryOptionKey in form.js /
   * dietaryKeyForOption in admin.js). Storing the visible label instead would
   * look right on the guest list and then fail to group with everyone else's
   * answer in the dietary breakdown, because those two read the key.
   *
   * An event with no configured list gets the same default five the response
   * editor falls back to, so the host is never handed an empty dropdown.
   */
  const AG_DIETARY_FALLBACK = ['No special requirements', 'Vegetarian', 'Vegan', 'Gluten-free', 'Other (please specify)'];
  const AG_OTHER_WORDS = ['other', 'specify', 'otro', 'otra', 'andere', 'sonstige', 'autre', 'outros', 'outra', 'آخر', 'אחר', 'אחרת'];
  function _agDietaryChoices() {
    const raw = (Array.isArray(state.form && state.form.dietaryOptions) ? state.form.dietaryOptions : [])
      .map((o) => (o && typeof o === 'object' ? (o.label || o.value || '') : String(o == null ? '' : o)))
      .map((s) => s.trim())
      .filter(Boolean);
    const list = raw.length ? raw : AG_DIETARY_FALLBACK;
    return list.map((label, i) => {
      if (AG_OTHER_WORDS.some((w) => label.toLowerCase().includes(w))) return { key: 'other', label };
      let key = label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      if (!key || !key.replace(/-/g, '')) key = `dietary_${i + 1}`;
      return { key, label };
    });
  }

  /**
   * Should each extra guest in this dialog get their own meal box?
   *
   * Yes whenever the event collects dietary at all — which is exactly what the
   * main dashboard's own Add-guest dialog does (buildGuestMealSelectHtml in
   * admin.js gates on includeDietary and nothing else).
   *
   * Round 6 gated this on `collectIndividualNames` as well, reasoning that a
   * count-based event never asks per person. That reasoning silently excluded
   * every CORPORATE event: a corporate form collects `collectGuestDetails`
   * instead, and turning that on force-disables collectIndividualNames (form.js,
   * applyGuestDetails) — so a corporate host got one requirement for a party of
   * four and no way to say who eats what, which is precisely the report that
   * reopened this ("I add guest manually in corporate I still don't see the
   * option to add dietary for both guests", task #495 round 7).
   *
   * A host writing down a party they already know is recording what they know;
   * the per-guest answers land in `individualNames[].dietary`, which the Dietary
   * column, the seating chart and the dietary breakdown all already read.
   */
  function _agPerGuestDietary() {
    return !!state.form && state.form.includeDietary !== false;
  }

  /**
   * Does the EVENT ITSELF ask each named guest for their own meal?
   *
   * Narrower than _agPerGuestDietary, and it governs only the box at the TOP of
   * the dialog: on such an event that box is the main guest's own meal (labelled
   * so, and storing the canonical key), because that is what their own guests
   * are asked. Otherwise it stays the event's ordinary party-level dietary
   * question — including the free-text box an event with no configured options
   * shows — and is stored as the label it has always been.
   */
  function _agMainDietaryIsPerGuest() {
    return _agPerGuestDietary() && !!state.form
      && (state.form.collectIndividualNames === true || state.form.collectGuestDetails === true);
  }

  /** One dietary <select> for a guest card, or for the main guest at the top. */
  function _agDietarySelectHtml(id, selectedKey) {
    const opts = _agDietaryChoices()
      .map((c) => `<option value="${_esc(c.key)}"${c.key === selectedKey ? ' selected' : ''}>${_esc(c.label)}</option>`)
      .join('');
    return `<select class="form-select form-select-sm" id="${id}" data-ag-diet-select="1">
              <option value="">—</option>${opts}
            </select>`;
  }

  /**
   * The event's questions the form asks EACH guest (task #1104): a question
   * marked "ask each attendee", or a choice question with per-guest
   * quantities (the Meal Choice preset). Those get a box in every extra
   * guest's card; a question asked once of the party stays at the bottom.
   */
  function _agPerGuestQuestions() {
    return (state.form?.customFields || []).filter((f) => f && f.id
      && f.type !== 'message' && f.type !== 'image'
      && (f.perAttendee === true
        || (f.perGuestQuantity === true && ['select', 'radio', 'checkbox'].includes(f.type)
            && Array.isArray(f.options) && f.options.length > 0)));
  }

  const AG_DETAIL_FIELDS = {
    email: { label: 'Email', type: 'email' },
    phone: { label: 'Phone', type: 'tel' },
    jobTitle: { label: 'Job title', type: 'text' },
    company: { label: 'Company', type: 'text' },
    department: { label: 'Department', type: 'text' }
  };

  /** Which detail fields this event's Add-guest form should offer. */
  function _agDetailKeys() {
    const configured = Array.isArray(state.form && state.form.guestDetailFields)
      ? state.form.guestDetailFields.filter((k) => k !== 'name' && AG_DETAIL_FIELDS[k])
      : [];
    // The form's own choice wins when it collects per-guest details. Otherwise
    // fall back to the three a host recording an RSVP by hand actually has —
    // the host is writing down what they know, not answering the public form.
    if (state.form && state.form.collectGuestDetails && configured.length) return configured;
    return ['email', 'phone', 'jobTitle'];
  }

  /**
   * Draw a card for every guest beyond the first (task #495 follow-ups).
   *
   * Position 1 is the main guest — that is the Name field at the top — so the
   * extras start at 2, which is the shape both the importer and the public form
   * store. Each card carries the name plus the same contact/job fields the
   * corporate form collects, because "the second guest's job title, email,
   * phone" is exactly what a host adding a colleague by hand has to record.
   * Anything already typed is kept when the count changes, so nudging the
   * number from 3 to 4 doesn't wipe what is already entered.
   */
  function _renderAgGuestNames(modalEl, separateKids) {
    const wrap = modalEl.querySelector('#agGuestNamesWrap');
    const list = modalEl.querySelector('#agGuestNames');
    const hint = modalEl.querySelector('#agGuestNamesHint');
    if (!wrap || !list) return;

    // Keep every value already typed, keyed by position + field. The dietary
    // <select> is included, so nudging the party size from 3 to 4 doesn't wipe
    // the meals already chosen.
    const kept = {};
    list.querySelectorAll('[data-ag-guest-field]').forEach((i) => {
      kept[`${i.dataset.position}|${i.dataset.agGuestField}`] = i.value;
    });

    const num = (sel, min, fallback) => {
      const el = modalEl.querySelector(sel);
      const v = parseInt(el && el.value, 10);
      return Number.isFinite(v) ? Math.max(min, Math.min(50, v)) : fallback;
    };

    const rows = [];
    if (separateKids) {
      const adults = num('#agAdults', 1, 1);
      const kids = num('#agChildren', 0, 0);
      let position = 2;
      for (let i = 2; i <= adults; i++) rows.push({ position: position++, guestType: 'adult', label: `Adult ${i}` });
      for (let i = 1; i <= kids; i++) rows.push({ position: position++, guestType: 'child', label: `Child ${i}` });
    } else {
      const party = num('#agParty', 1, 1);
      for (let i = 2; i <= party; i++) rows.push({ position: i, guestType: 'adult', label: `Guest ${i}` });
    }

    if (!rows.length) {
      wrap.style.display = 'none';
      list.innerHTML = '';
      return;
    }

    // Delegated once per list element: the cards are rebuilt on every party-size
    // change, so a listener bound to each <select> would die with its card.
    if (!list.dataset.agDietWired) {
      list.dataset.agDietWired = '1';
      list.addEventListener('change', (e) => {
        const sel = e.target && e.target.closest && e.target.closest('[data-ag-guest-field="dietary"]');
        if (!sel) return;
        const box = sel.parentElement && sel.parentElement.querySelector('.ag-guest-diet-details');
        if (!box) return;
        const on = String(sel.value || '') === 'other';
        box.style.display = on ? '' : 'none';
        if (!on) box.value = '';
      });
    }

    const detailKeys = _agDetailKeys();
    // Per-guest dietary (task #495 round 6). When the event asks each named
    // guest for their own meal, so must this dialog — otherwise a party added by
    // hand lands with one requirement for four people, and the Dietary column,
    // which lists "Esti: Vegan | Shahar: Fish", has nothing to say about them.
    // Hidden for a decline for the same reason the main dietary row is.
    const perGuestDiet = _agPerGuestDietary()
      && modalEl.querySelector('#agStatus')
      && modalEl.querySelector('#agStatus').value !== 'no';
    wrap.style.display = '';
    hint.textContent = 'Who else is coming?';
    list.innerHTML = rows.map((r) => {
      const val = (key) => _esc(kept[`${r.position}|${key}`] || '');
      // "Other (please specify)" needs somewhere to specify, per guest. The
      // only free-text box used to be the one at the top of the dialog, which
      // reads as the MAIN guest's — so a host picking Other for guest two had
      // nowhere to write the allergy (task #495 round 8).
      const dietVal = kept[`${r.position}|dietary`] || '';
      const dietHtml = !perGuestDiet ? '' : `
          <div class="col-6">
            <label class="form-label small mb-1" for="ag_g${r.position}_dietary">Dietary</label>
            <select class="form-select form-select-sm" id="ag_g${r.position}_dietary"
                    data-ag-guest-field="dietary" data-position="${r.position}" data-guest-type="${r.guestType}">
              <option value="">—</option>
              ${_agDietaryChoices().map((c) =>
                `<option value="${_esc(c.key)}"${c.key === dietVal ? ' selected' : ''}>${_esc(c.label)}</option>`).join('')}
            </select>
            <input type="text" class="form-control form-control-sm mt-1 ag-guest-diet-details"
                   id="ag_g${r.position}_dietaryDetails" data-ag-guest-field="dietaryDetails"
                   data-position="${r.position}" data-guest-type="${r.guestType}"
                   placeholder="Allergies, anything else" aria-label="Dietary details"
                   value="${val('dietaryDetails')}"
                   style="display:${dietVal === 'other' ? '' : 'none'};">
          </div>`;
      const details = detailKeys.map((key) => {
        const def = AG_DETAIL_FIELDS[key];
        return `
          <div class="col-6">
            <label class="form-label small mb-1" for="ag_g${r.position}_${key}">${_esc(def.label)}</label>
            <input type="${def.type}" class="form-control form-control-sm"
                   id="ag_g${r.position}_${key}" data-ag-guest-field="${key}"
                   data-position="${r.position}" data-guest-type="${r.guestType}"
                   ${key === 'phone' ? 'dir="ltr" ' : ''}value="${val(key)}">
          </div>`;
      }).join('');
      // This guest's own answers to the questions asked of each guest. Kept
      // across a party-size change like every other box in the card.
      const questions = _agPerGuestQuestions().map((f, qi) => {
        const key = `cf:${f.id}`;
        const id = `ag_g${r.position}_q${qi}`;
        const cur = kept[`${r.position}|${key}`] || '';
        const opts = (Array.isArray(f.options) ? f.options : []).map((o) => String(o == null ? '' : o)).filter(Boolean);
        const attrs = `id="${id}" data-ag-guest-field="${_esc(key)}" data-position="${r.position}" data-guest-type="${r.guestType}"`;
        const control = opts.length
          ? `<select class="form-select form-select-sm" ${attrs}>
               <option value="">—</option>
               ${opts.map((o) => `<option value="${_esc(o)}"${o === cur ? ' selected' : ''}>${_esc(o)}</option>`).join('')}
             </select>`
          : `<input type="text" class="form-control form-control-sm" ${attrs} value="${_esc(cur)}">`;
        return `
          <div class="col-12">
            <label class="form-label small mb-1" for="${id}">${_esc(f.label || 'Question')}</label>
            ${control}
          </div>`;
      }).join('');
      return `
      <div class="border rounded p-2 mb-2 ag-guest-card">
        <div class="small text-body-secondary mb-1">${_esc(r.label)}</div>
        <div class="mb-2">
          <input type="text" class="form-control form-control-sm ag-guest-name"
                 data-ag-guest-field="name" data-position="${r.position}" data-guest-type="${r.guestType}"
                 placeholder="Name" aria-label="${_esc(r.label)} name" value="${val('name')}">
        </div>
        <div class="row g-2">${dietHtml}${details}${questions}</div>
      </div>`;
    }).join('');
  }

  async function _submitAddGuest(modalEl) {
    const err = modalEl.querySelector('#agError');
    const btn = modalEl.querySelector('#agSave');
    const show = (msg) => { err.textContent = msg; err.style.display = 'block'; };
    err.style.display = 'none';

    const name = modalEl.querySelector('#agName').value.trim();
    if (!name) { show('A name is required.'); modalEl.querySelector('#agName').focus(); return; }

    const attending = modalEl.querySelector('#agStatus').value;
    const row = {
      guestName: name,
      attending,
      email: modalEl.querySelector('#agEmail').value.trim(),
      phoneNumber: modalEl.querySelector('#agPhone').value.trim()
    };
    if (modalEl.querySelector('#agAdults')) {
      row.adultCount = modalEl.querySelector('#agAdults').value;
      row.childrenCount = modalEl.querySelector('#agChildren').value;
    } else if (modalEl.querySelector('#agParty')) {
      row.guestCount = modalEl.querySelector('#agParty').value;
    }

    // Dietary — skipped for a decline, whose row is hidden anyway. The importer
    // already accepts both keys; the sheet's Dietary column reads dietaryDetails
    // first and falls back to dietary, so either one alone shows up there.
    // With per-guest dietary on, that box holds the canonical KEY rather than
    // the visible label — the response-level `dietary` field has always been a
    // label, so translate it back before sending, and keep the key for the main
    // guest's own entry in individualNames below.
    let mainDietKey = '';
    if (attending !== 'no') {
      const dietEl = modalEl.querySelector('#agDietary');
      let diet = String(dietEl?.value || '').trim();
      if (diet && dietEl && dietEl.dataset.agDietSelect === '1') {
        mainDietKey = diet;
        const match = _agDietaryChoices().find((c) => c.key === diet);
        if (match) diet = match.label;
      }
      const dietDetails = String(modalEl.querySelector('#agDietaryDetails')?.value || '').trim();
      if (diet) row.dietary = diet;
      if (dietDetails) row.dietaryDetails = dietDetails;
    }

    // The corporate columns. Sent as top-level keys, which is where the sheet
    // reads them from and what the import service now allowlists (task #625 r2).
    modalEl.querySelectorAll('.ag-detail').forEach((el) => {
      const v = String(el.value || '').trim();
      if (v) row[el.dataset.detailKey] = v;
    });

    // Custom answers keyed by the form's REAL field ids — the import service
    // accepts either id or label and drops anything it doesn't recognise.
    const custom = {};
    modalEl.querySelectorAll('.ag-custom').forEach((el) => {
      const v = String(el.value || '').trim();
      if (v) custom[el.dataset.fieldId] = v;
    });
    modalEl.querySelectorAll('.ag-custom-check:checked').forEach((el) => {
      const k = el.dataset.fieldId;
      custom[k] = custom[k] ? `${custom[k]}, ${el.value}` : el.value;
    });
    if (Object.keys(custom).length) row.customFields = custom;

    // The rest of the party. Blank boxes are simply left out — the importer
    // only builds the list when there is something in it, and a party of four
    // with one name filled in should not become four blank seats.
    //
    // TWO fields are written on purpose, because two different screens read
    // two different things: seating and check-in walk `individualNames`, while
    // the attendee-details card reads `additionalGuestDetails`. A card is only
    // added when something beyond the position was actually typed.
    const others = [];
    const details = [];
    modalEl.querySelectorAll('.ag-guest-card').forEach((card) => {
      const nameEl = card.querySelector('.ag-guest-name');
      if (!nameEl) return;
      const position = parseInt(nameEl.dataset.position, 10);
      const guestType = nameEl.dataset.guestType === 'child' ? 'child' : 'adult';
      const name = String(nameEl.value || '').trim();
      // Dietary rides with the NAME, not with the contact details — that is
      // where the public form puts it, and where the Dietary column, the
      // seating chart and the dietary breakdown all read it from. A meal picked
      // for a card with no name typed is dropped, exactly as the public form
      // drops an unnamed slot's answer: there is no person to attach it to.
      const dietEl = card.querySelector('[data-ag-guest-field="dietary"]');
      const diet = String(dietEl && dietEl.value || '').trim();
      // The "Other" free text belongs to the guest who chose Other. It is only
      // meaningful next to that answer, so it rides with the name and is
      // dropped when the answer is anything else.
      const dietDetailsEl = card.querySelector('[data-ag-guest-field="dietaryDetails"]');
      const dietDetail = diet === 'other' ? String(dietDetailsEl && dietDetailsEl.value || '').trim() : '';
      if (name) others.push({ position, name, guestType, ...(diet ? { dietary: diet } : {}), ...(dietDetail ? { dietaryDetails: dietDetail } : {}) });

      const entry = { position };
      let filled = false;
      card.querySelectorAll('[data-ag-guest-field]').forEach((el) => {
        if (el.dataset.agGuestField === 'dietary') return;   // handled above
        if (el.dataset.agGuestField === 'dietaryDetails') return;   // rides with the name
        if (el.dataset.agGuestField.startsWith('cf:')) return;      // answers, sent below
        const v = String(el.value || '').trim();
        if (!v) return;
        entry[el.dataset.agGuestField] = v;
        filled = true;
      });
      if (filled) details.push(entry);
    });
    // The main guest's own meal belongs on their entry (position 1). The
    // importer writes position 1 from the name at the top of this dialog, so it
    // only needs the key — but it has to have a list to build at all, which is
    // why this is only added when there is already somebody else on it.
    if (others.length && mainDietKey) row.mainGuestDietary = mainDietKey;
    if (others.length && mainDietKey === 'other') {
      const d = String(modalEl.querySelector('#agDietaryDetails')?.value || '').trim();
      if (d) row.mainGuestDietaryDetails = d;
    }
    if (others.length) row.individualNames = others;
    if (details.length) row.additionalGuestDetails = details;

    // Each guest's own answer to a question asked of every guest (task
    // #1104): slot 1 is the main guest (the box at the bottom), then the
    // cards in order. The importer stores them in the guest form's
    // per-guest shape (attendeeValues), which the +1 rows read.
    const perGuestQs = _agPerGuestQuestions();
    if (perGuestQs.length) {
      const cards = Array.from(modalEl.querySelectorAll('.ag-guest-card'));
      const attendees = {};
      perGuestQs.forEach((f) => {
        const values = [String(custom[f.id] || '').trim()];
        const names = [name];
        cards.forEach((card, ci) => {
          const box = Array.from(card.querySelectorAll('[data-ag-guest-field]'))
            .find((el) => el.dataset.agGuestField === `cf:${f.id}`);
          values.push(String(box && box.value || '').trim());
          names.push(String(card.querySelector('.ag-guest-name')?.value || '').trim() || `Guest ${ci + 2}`);
        });
        if (values.slice(1).some(Boolean)) attendees[f.id] = { values, names };
      });
      if (Object.keys(attendees).length) {
        row.customFieldAttendees = attendees;
        if (!row.customFields) row.customFields = {};
      }
    }

    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      const token = await window.firebaseAuth.currentUser.getIdToken();
      const res = await fetch(`/api/forms/${encodeURIComponent(state.formId)}/responses/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        // 'skip-dupes' so adding somebody who already replied does not quietly
        // overwrite what they themselves submitted.
        body: JSON.stringify({ mode: 'skip-dupes', rows: [row] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.error || 'Could not add this guest');

      const result = (data.results || [])[0] || {};
      // The importer reports a dedupe skip as status 'skipped' (see
      // response-import.service, skip-dupes branch) — NOT 'duplicate'.
      if (result.status === 'skipped' || (data.summary && data.summary.skippedDuplicate > 0)) {
        show(`${name} looks like somebody already on this list, so nothing was added. Search for them above — if it really is a different person, add a distinguishing detail such as their email.`);
        btn.disabled = false;
        btn.textContent = 'Add guest';
        return;
      }
      if (result.status === 'invalid') throw new Error('That entry was rejected: ' + (result.reason || 'invalid'));

      _teardownAddGuest();
      _showToast(`${name} added`);
      // Re-read from the server rather than pushing a guessed row into state:
      // the doc the server built is the one seating, check-in and the counters
      // must agree with.
      await _reloadResponses();
      _renderActiveTab();
    } catch (e) {
      show(e.message || String(e));
      btn.disabled = false;
      btn.textContent = 'Add guest';
    }
  }

  async function _reloadResponses() {
    try {
      const token = await window.firebaseAuth.currentUser.getIdToken();
      const r = await _fetchResponses({ Authorization: `Bearer ${token}` });
      if (!r.ok) return;
      const d = await r.json();
      state.responses = d.responses || [];
      _pruneSelection();
    } catch { /* leave the current list in place */ }
  }

  // ============================================================================
  // OUTREACH — campaigns sent about this event
  // ============================================================================

  // Task #903: this is the only tab that renders after a fetch. Clicking Seating
  // while campaigns were still loading drew Seating, then the late reply
  // overwrote it with Outreach (tab highlighted, wrong content) until a second
  // click. A reply now draws only if it is the latest Outreach render and the
  // host is still on Outreach for the same event.
  let _outreachRenderSeq = 0;
  // The sends last drawn, for their Preview (task #1115).
  let _outreachSends = [];

  /**
   * The saved draft's row (task #1112): its subject, how many people it is
   * for and when it was saved, with Continue draft (back into the wizard on
   * the step it was saved on) and a bin.
   */
  function _draftRowHtml(d) {
    const n = (d.recipientSource === 'guests' ? d.selectedGuests : d.selectedContacts) || [];
    const count = Array.isArray(n) ? n.length : 0;
    const saved = d.savedAt ? _fmtDate(d.savedAt) : '';
    return `
      <div class="outreach-row outreach-row-draft">
        <div class="outreach-row-main">
          <div class="outreach-subject">${d.subject ? _esc(d.subject) : '<span class="text-body-secondary fst-italic">No subject yet</span>'}</div>
          <div class="outreach-meta">${count} recipient${count === 1 ? '' : 's'}${saved ? ' \u00B7 Last saved ' + _esc(saved) : ''}</div>
        </div>
        <span class="status-pill pill-pending">Draft</span>
        <div class="outreach-row-actions">
          <button type="button" class="btn btn-org-primary btn-sm" onclick="OrgEventDetail.resumeDraft()"><i class="bi bi-pencil"></i> Continue draft</button>
          <button type="button" class="btn btn-sm btn-outline-danger" onclick="OrgEventDetail.discardDraft()" aria-label="Delete draft" title="Delete draft"><i class="bi bi-trash"></i></button>
        </div>
      </div>`;
  }

  /**
   * The Outreach tab's email-credit bar (task #1019, Shahar): the plan's
   * emails left this month, then the owner's credits that pay for the rest
   * (organization.service sendCorporateEmail). Buy Credits is the owner's -
   * credits land in the buyer's own wallet, and it is the owner's that pays.
   */
  function _creditBarHtml(stats) {
    if (!stats || !Number.isFinite(stats.allowanceLimit)) return '';
    const left = Math.max(0, stats.allowanceLimit - (stats.allowanceUsedThisMonth || 0));
    const me = window.firebaseAuth && window.firebaseAuth.currentUser && window.firebaseAuth.currentUser.uid;
    const isOwner = !!(me && stats.ownerUid && me === stats.ownerUid);
    const credits = Number.isFinite(stats.ownerCredits) ? stats.ownerCredits : null;
    const fmt = (n) => Number(n).toLocaleString();
    return `
      <div class="outreach-credit-bar">
        <i class="bi bi-envelope" aria-hidden="true"></i>
        <span class="ocb-main"><strong>${fmt(left)}</strong> of ${fmt(stats.allowanceLimit)} emails left this month</span>
        ${credits !== null ? `<span class="ocb-sep" aria-hidden="true">·</span>
        <span class="ocb-credits"><strong>${fmt(credits)}</strong> ${isOwner ? 'credits' : 'credits (the owner\u2019s)'} for more</span>` : ''}
        ${isOwner
          ? `<button type="button" class="btn btn-org-outline btn-sm ms-auto" onclick="OrgEventDetail.buyCredits()"><i class="bi bi-plus-circle"></i> Buy Credits</button>`
          : '<span class="ocb-note ms-auto">Only the organization owner can buy credits</span>'}
      </div>`;
  }

  /** The credit packs window (wallet-manager.js), then the bar again. */
  function buyCredits() {
    const wm = window.WalletManager;
    if (!wm || typeof wm.showPurchaseModal !== 'function') {
      _showToast('Credits cannot be bought right now. Please refresh the page.', 'danger');
      return;
    }
    wm.showPurchaseModal(() => { if (state.view === 'outreach') _renderActiveTab(); }, null, { corporate: true });
  }

  async function _renderOutreach(el) {
    const seq = ++_outreachRenderSeq;
    const formId = state.formId;
    const stale = () => seq !== _outreachRenderSeq || state.view !== 'outreach' || state.formId !== formId;
    el.innerHTML = '<div class="text-center py-5"><div class="org-spinner"></div><p class="text-muted mt-2">Loading campaigns…</p></div>';
    let sends = [];
    // The email credits left, read alongside (task #1019); a failure only
    // leaves the bar out.
    const statsP = window.OrgContext.orgFetch('/send-stats').then(r => r && r.stats).catch(() => null);
    // The saved draft, when it is for this event: its own row with Continue
    // (task #1112). One draft per workspace, so a draft for another event
    // shows on that event's tab.
    const draftP = Promise.resolve(window.OrgCampaign && typeof window.OrgCampaign.getDraft === 'function'
      ? window.OrgCampaign.getDraft() : null).catch(() => null);
    try {
      const res = await window.OrgContext.orgFetch(`/forms/${formId}/campaign-sends`);
      if (stale()) return;
      sends = res.sends || [];
    } catch (err) {
      if (stale()) return;
      el.innerHTML = `<div class="event-detail-empty"><i class="bi bi-exclamation-circle"></i><p>Could not load campaigns: ${_esc(err.message)}</p></div>`;
      return;
    }

    const totals = sends.reduce((a, s) => {
      a.campaigns++;
      a.recipients += s.requestedCount || 0;
      a.delivered += s.sent || 0;
      a.failed += s.failed || 0;
      a.skipped += s.skipped || 0;
      return a;
    }, { campaigns: 0, recipients: 0, delivered: 0, failed: 0, skipped: 0 });
    const deliveryRate = totals.recipients > 0
      ? Math.round((totals.delivered / totals.recipients) * 100)
      : null;
    // Over the invites whose recipients were recorded (task #1115).
    const counted = sends.filter(s => s.responded != null && s.sent > 0);
    const responseRate = counted.length
      ? Math.round((counted.reduce((n, s) => n + s.responded, 0) / counted.reduce((n, s) => n + s.sent, 0)) * 100)
      : null;
    _outreachSends = sends;

    const creditBar = _creditBarHtml(await statsP);
    const draft = await draftP;
    if (stale()) return;
    const draftRow = draft && draft.event === formId ? _draftRowHtml(draft) : '';

    if (sends.length === 0) {
      // Task #496: the empty state used to send hosts back to the Events list to
      // do the very thing this tab is about. It starts the campaign instead.
      el.innerHTML = `${creditBar}
        <div class="event-detail-empty">
          <i class="bi bi-megaphone"></i>
          <p>No campaigns sent for this event yet.</p>
          <p class="text-body-secondary" style="font-size:0.85rem;">Write one now \u2014 it goes to this event's people, and you stay right here.</p>
          <button class="btn btn-org-primary btn-sm mt-2" onclick="OrgEventDetail.newCampaign()"${_gatedAttrs('Subscribe to send campaigns')}>
            <i class="bi bi-plus-lg"></i> New campaign
          </button>
        </div>
        ${draftRow ? `<div class="outreach-list mt-3">${draftRow}</div>` : ''}`;
      return;
    }

    el.innerHTML = `${creditBar}
      <div class="outreach-actions d-flex justify-content-start mb-3">
        <button class="btn btn-org-primary btn-sm" onclick="OrgEventDetail.newCampaign()"${_gatedAttrs('Subscribe to send campaigns')}>
          <i class="bi bi-plus-lg"></i> New campaign
        </button>
      </div>

      <div class="event-detail-stats">
        <div class="event-detail-stat"><span class="value">${totals.campaigns}</span><span class="label">Campaigns</span></div>
        <div class="event-detail-stat stat-guests"><span class="value">${totals.recipients}</span><span class="label">Recipients</span></div>
        <div class="event-detail-stat stat-yes"><span class="value">${totals.delivered}</span><span class="label">Delivered</span></div>
        ${totals.failed ? `<div class="event-detail-stat stat-no"><span class="value">${totals.failed}</span><span class="label">Failed</span></div>` : ''}
        ${totals.skipped ? `<div class="event-detail-stat stat-pending"><span class="value">${totals.skipped}</span><span class="label">Skipped</span></div>` : ''}
        ${deliveryRate !== null ? `<div class="event-detail-stat"><span class="value">${deliveryRate}%</span><span class="label">Delivery rate</span></div>` : ''}
        ${responseRate !== null ? `<div class="event-detail-stat"><span class="value">${responseRate}%</span><span class="label">Response rate</span></div>` : ''}
      </div>

      <div class="outreach-list">
        ${draftRow}
        ${sends.map(s => _renderCampaignSend(s)).join('')}
      </div>`;
  }

  /**
   * Start a campaign for the event being viewed, without leaving it (task #496).
   *
   * Calls OrgCampaign.open with EXACTLY the arguments the Events-list send
   * action uses, so this is the same wizard reached the same way — not a second
   * entry point that could drift from it. `chooser: true` with a preselected
   * event skips the generic event picker and asks invite-vs-update first, then
   * lands on Recipients with this event fixed. So the recipients are this
   * event's people: its registered guests for an update, org contacts for an
   * invite (you invite the ones who have NOT replied yet — scoping an invite to
   * existing guests would make it unsendable).
   *
   * The Outreach list is re-read when the modal closes, so a campaign sent here
   * shows up in the history behind it instead of needing a tab switch.
   */
  // Re-read Outreach when the campaign window closes: a campaign sent, or a
  // draft saved (task #1112), shows up behind it.
  function _bindOutreachRefresh() {
    const modalEl = document.getElementById('orgCampaignModal');
    if (modalEl && !modalEl._outreachRefreshBound) {
      modalEl._outreachRefreshBound = true;
      modalEl.addEventListener('hidden.bs.modal', () => {
        // Only when the host is still looking at Outreach — re-rendering a tab
        // they have since navigated away from would fight the current view.
        if (state.view === 'outreach' && state.formId) _renderActiveTab();
      });
    }
  }

  function newCampaign() {
    if (!window.OrgCampaign || typeof window.OrgCampaign.open !== 'function') {
      _showToast('Campaign tools are still loading — try again in a moment.', 'warning');
      return;
    }
    _bindOutreachRefresh();
    window.OrgCampaign.open({
      preselectedEventId: state.formId,
      chooser: true,
      eventName: state.form?.title || ''
    });
  }

  /** Continue the saved draft (task #1112), in the same wizard. */
  function resumeDraft() {
    if (!window.OrgCampaign || typeof window.OrgCampaign.open !== 'function') return;
    _bindOutreachRefresh();
    window.OrgCampaign.open({ resumeDraft: true });
  }

  async function discardDraft() {
    if (!window.OrgCampaign || typeof window.OrgCampaign.discardDraft !== 'function') return;
    if (!confirm('Delete this draft? This cannot be undone.')) return;
    await window.OrgCampaign.discardDraft();
    if (state.view === 'outreach') _renderActiveTab();
  }

  /**
   * One campaign, one row (task #1115): subject, when, and the address it
   * really went out from; delivered, delivery rate and response rate; its
   * status; and Preview. It was a two-part card that named the signed-in
   * user's own email and "via quikrsvp" instead of the From address.
   *
   * Response rate = recipients of an invite who replied to the event after
   * it went out. An update goes to people who had replied already, so it has
   * none; nor does a campaign from before recipients were recorded.
   */
  function _renderCampaignSend(s) {
    const sent = _fmtDate(s.sentAt);
    const rate = s.requestedCount > 0 ? Math.round((s.sent / s.requestedCount) * 100) : 0;
    const status = s.aborted ? 'aborted' : (s.failed > s.sent ? 'degraded' : 'delivered');
    const statusLabel = { aborted: 'Aborted', degraded: 'Partial', delivered: 'Delivered' }[status];
    const statusClass = { aborted: 'pill-no', degraded: 'pill-pending', delivered: 'pill-yes' }[status];
    const isUpdate = s.recipientSource === 'guests';
    const resp = s.responded != null && s.sent > 0 ? Math.round((s.responded / s.sent) * 100) : null;
    const respTip = resp !== null
      ? `${s.responded} of ${s.sent} replied to the event after this email`
      : (isUpdate ? 'An update goes to guests who have already replied' : 'Not recorded for campaigns sent before 30 Oct 2026');
    return `
      <div class="outreach-row">
        <div class="outreach-row-main">
          <div class="outreach-subject">${_esc(s.subject || '(no subject)')}</div>
          <div class="outreach-meta">${sent || '—'}${s.fromAddress ? ` \u00B7 From <span dir="ltr">${_esc(s.fromAddress)}</span>` : ''} \u00B7 ${isUpdate ? 'Event update' : 'Invite'}</div>
        </div>
        <div class="outreach-row-stats">
          <div class="outreach-stat"><strong>${s.sent}</strong><span>delivered</span></div>
          ${s.failed ? `<div class="outreach-stat"><strong>${s.failed}</strong><span>failed</span></div>` : ''}
          ${s.skipped ? `<div class="outreach-stat"><strong>${s.skipped}</strong><span>skipped</span></div>` : ''}
          <div class="outreach-stat"><strong>${rate}%</strong><span>delivery rate</span></div>
          <div class="outreach-stat" title="${_esc(respTip)}"><strong>${resp !== null ? resp + '%' : '—'}</strong><span>response rate</span></div>
        </div>
        <span class="status-pill ${statusClass}">${statusLabel}</span>
        <div class="outreach-row-actions">
          <button type="button" class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.previewCampaign('${_esc(s.id)}')"><i class="bi bi-eye"></i> Preview</button>
        </div>
      </div>`;
  }

  /**
   * A sent campaign as its recipients got it (task #1115), read-only: the
   * subject, the From address, and the email drawn by the wizard's own
   * renderer with a sample recipient. Campaigns from before the message was
   * kept show only what was recorded.
   */
  function previewCampaign(id) {
    const s = (_outreachSends || []).find(x => x.id === id);
    if (!s) return;
    document.getElementById('oedCampaignPreviewModal')?.remove();
    const html = (s.body != null && window.OrgCampaign && typeof window.OrgCampaign.previewHtml === 'function')
      ? window.OrgCampaign.previewHtml({ event: { ...(state.form || {}), id: state.formId }, body: s.body,
          recipientSource: s.recipientSource, includeQRCode: s.includeQRCode })
      : '<p class="text-body-secondary small mb-0">The message itself is kept for campaigns sent from 30 Oct 2026. This one was sent before that.</p>';
    const wrap = document.createElement('div');
    wrap.innerHTML = `
      <div class="modal fade" id="oedCampaignPreviewModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" style="font-size:1rem;"><i class="bi bi-eye me-2"></i>Sent email</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <dl class="oed-preview-meta">
                <dt>Subject</dt><dd>${_esc(s.subject || '(no subject)')}</dd>
                ${s.fromAddress ? `<dt>From</dt><dd dir="ltr">${_esc(s.fromAddress)}</dd>` : ''}
                <dt>Sent</dt><dd>${_esc(_fmtDate(s.sentAt) || '—')} \u00B7 ${s.sent} delivered</dd>
              </dl>
              <div class="campaign-preview"><div class="campaign-preview-body oed-preview-email">${html}</div></div>
            </div>
          </div>
        </div>
      </div>`;
    const modalEl = wrap.firstElementChild;
    document.body.appendChild(modalEl);
    modalEl.addEventListener('hidden.bs.modal', () => modalEl.remove());
    new bootstrap.Modal(modalEl).show();
  }

  // ============================================================================
  // SEATING — full-featured, uses shared /api/seating/:formId infrastructure
  // Matches the personal dashboard: per-seat assignments, member expansion
  // (Alice+2 = 3 seats), auto-assign, keep-together / keep-apart rules,
  // undo/redo, CSV export, print.
  // ============================================================================

  let _seatingSearch = '';
  // The batch Auto-assign window's last choice (kept apart from the door
  // strategy, seating.defaultStrategy - see autoAssign).
  let _lastBulkStrategy = null;

  // #846: how QR Auto picks a table for ONE guest as they check in. Same five
  // the event dashboard offers; the server places them in
  // seatingService.autoAssignGuest, after the seating rules.
  const DOOR_STRATEGIES = [
    { id: 'roundrobin', label: 'Round-robin (1 per table)', icon: 'bi-arrow-repeat', desc: 'Each arriving guest goes to the emptiest table. Best for a balanced room.' },
    { id: 'sequential', label: 'Fill tables in order', icon: 'bi-list-ol', desc: 'Fill one table before starting the next. Best when the room fills from the front.' },
    { id: 'random', label: 'Random spread', icon: 'bi-shuffle', desc: 'Any table with room. Best when mixing is the point.' },
    { id: 'relationship', label: 'Group by relationship', icon: 'bi-people-fill', desc: 'Sit with guests who share their relationship (family, colleagues, …).' },
    { id: 'tags', label: 'Group by shared tags', icon: 'bi-tags', desc: 'Sit with guests who share one of their seating tags.' }
  ];

  function _seatingModeHtml(seating) {
    const qr = seating.assignmentMode === 'qr_auto';
    const strategy = seating.defaultStrategy && seating.defaultStrategy !== 'manual' ? seating.defaultStrategy : 'roundrobin';
    // Task #860: the strategy picker moved into the Seating rules window, so
    // the toolbar carries only the switch; the QR Auto banner names the
    // strategy and links to the window. `strategy` is kept for the banner.
    void strategy;
    return `
          <div class="oed-seat-mode-wrap">
          <div class="oed-seat-mode" role="group" aria-label="How guests get a table">
            <button type="button" class="oed-seat-mode-btn${qr ? '' : ' active'}" aria-pressed="${!qr}"
                    onclick="OrgEventDetail.setSeatingMode('manual')" title="You place guests (drag, or Auto-assign)">Manual</button>
            <button type="button" class="oed-seat-mode-btn${qr ? ' active' : ''}" aria-pressed="${qr}"
                    onclick="OrgEventDetail.setSeatingMode('qr_auto')" title="Guests get a table when they check in"><i class="bi bi-qr-code-scan"></i> QR Auto</button>
          </div>
          </div>`;
  }

  function _qrAutoBannerHtml(seating, rulesCount) {
    if (seating.assignmentMode !== 'qr_auto') return '';
    const strategy = DOOR_STRATEGIES.find(o => o.id === seating.defaultStrategy) || DOOR_STRATEGIES[0];
    return `
      <div class="oed-qr-banner" role="status">
        <i class="bi bi-qr-code-scan" aria-hidden="true"></i>
        <div>
          <div>Guests get a table automatically when they check in with their personal QR code or the Master QR sign: <strong>${_esc(strategy.label.toLowerCase())}</strong>${rulesCount ? ', following your seating rules' : ''}. <button type="button" class="btn btn-link btn-sm p-0 align-baseline" onclick="OrgEventDetail.openRulesModal()">Change</button></div>
          <div class="oed-qr-banner-note"><i class="bi bi-info-circle"></i> Only for guests who check in from now on. Anyone already seated keeps their seat${seating.tables.length ? '' : '. Add tables first, or nobody can be seated'}.</div>
        </div>
      </div>`;
  }

  function setSeatingMode(mode) {
    const seating = _seatingState();
    const next = mode === 'qr_auto' ? 'qr_auto' : 'manual';
    if (seating.assignmentMode === next) return;
    seating.assignmentMode = next;
    if (next === 'qr_auto' && (!seating.defaultStrategy || seating.defaultStrategy === 'manual')) seating.defaultStrategy = 'roundrobin';
    _saveSeating();
    _showToast(next === 'qr_auto' ? 'QR Auto is on: guests get a table when they check in' : 'Manual seating: guests are seated only by you');
    _renderActiveTab();
    // Task #860: QR Auto is set up in one place — open it straight away.
    if (next === 'qr_auto') openRulesModal();
  }

  function setDoorStrategy(id) {
    if (!DOOR_STRATEGIES.some(o => o.id === id)) return;
    _seatingState().defaultStrategy = id;
    _saveSeating();
    _renderActiveTab();
    document.querySelectorAll('#seatingRulesModal .autoassign-strategy').forEach((card) => {
      const on = card.dataset.strategy === id;
      card.classList.toggle('selected', on);
      const radio = card.querySelector('input[type="radio"]');
      if (radio) radio.checked = on;
    });
  }
  let _seatingLoaded = false;
  let _seatingLoadingPromise = null;
  // Collapsed category sections in the unassigned panel. Lives in memory only
  // (org dashboard doesn't persist view prefs); reset when the tab is left.
  const _seatingCollapsedSections = new Set();
  // Tracks which category is being dragged so .table dragover knows to behave
  // as a category drop (fill-to-capacity) instead of a single-member drop.
  let _seatingDragCategoryKey = null;
  const NO_CATEGORY_KEY = '__nocategory__';
  const NO_CATEGORY_LABEL = 'Uncategorized';
  // Multi-select state. Shift/cmd/ctrl-click chips to build a custom selection,
  // then drag any selected chip to move the whole set (fill-to-capacity on drop).
  // Cleared after each drop or when the tab is re-rendered with no selection.
  const _seatingSelectedKeys = new Set();

  // Whether a response is a "maybe" RSVP — for visual indicators on seated/
  // unassigned chips. Uses raw `attending` field (case-insensitive). The
  // existing _seatStatus collapses 'maybe' into 'yes' so these guests
  // still flow into the seating list; we just mark them so the planner
  // doesn't forget they're not confirmed.
  function _isMaybeResponse(r) {
    const a = r && (r.attending || r.attendingStatus || r.status);
    if (typeof a !== 'string') return false;
    return a.trim().toLowerCase() === 'maybe';
  }

  function _seatingState() {
    if (!state.seating) state.seating = { tables: [], rules: { keepTogether: [], keepApart: [] }, assignmentMode: 'manual', defaultStrategy: 'roundrobin' };
    if (!Array.isArray(state.seating.tables)) state.seating.tables = [];
    if (!state.seating.rules) state.seating.rules = { keepTogether: [], keepApart: [] };
    if (!Array.isArray(state.seating.rules.keepTogether)) state.seating.rules.keepTogether = [];
    if (!Array.isArray(state.seating.rules.keepApart)) state.seating.rules.keepApart = [];
    if (typeof state.seating.rules.groupByCompany !== 'boolean') state.seating.rules.groupByCompany = false;
    return state.seating;
  }

  // ── Company identity (task #498) ─────────────────────────────────────────
  // CLIENT MIRROR of server/utils/company-key.js, which is the source of truth.
  // It has to run here because the suggestion is computed over responses already
  // in the browser. Change one and change the other, or the host is shown a
  // grouping suggestion that the auto-assign will not actually produce.
  const _COMPANY_LEGAL_SUFFIXES = [
    'incorporated', 'inc', 'corporation', 'corp', 'company', 'co',
    'limited', 'ltd', 'llc', 'llp', 'lp', 'plc', 'pllc',
    'gmbh', 'ag', 'sa', 'sas', 'sarl', 'bv', 'nv', 'ab', 'as', 'oy', 'spa', 'srl',
    'pty', 'pte', 'kk', 'kg', 'ohg',
    'group', 'holdings', 'holding', 'partners', 'ventures',
    'ltda', 'cia'
  ];
  const _CONSUMER_DOMAINS = new Set([
    'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'ymail.com',
    'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'msn.com',
    'icloud.com', 'me.com', 'mac.com', 'aol.com', 'protonmail.com', 'proton.me',
    'gmx.com', 'gmx.de', 'mail.com', 'zoho.com', 'yandex.com', 'yandex.ru',
    'walla.co.il', 'walla.com', 'qq.com', '163.com', '126.com', 'naver.com'
  ]);

  function _normalizeCompanyName(raw) {
    let s = String(raw == null ? '' : raw).toLowerCase().trim();
    if (!s) return '';
    s = s.replace(/\([^)]*\)/g, ' ').replace(/\[[^\]]*\]/g, ' ');
    s = s.replace(/&/g, ' and ').replace(/\+/g, ' and ');
    s = s.replace(/[^a-z0-9֐-׿؀-ۿ ]+/g, ' ');
    s = s.replace(/\s+/g, ' ').trim();
    if (!s) return '';
    let parts = s.split(' ');
    while (parts.length > 1 && _COMPANY_LEGAL_SUFFIXES.includes(parts[parts.length - 1])) parts.pop();
    if (parts.length > 1 && parts[0] === 'the') parts.shift();
    const out = parts.join(' ').trim();
    return out.length >= 2 ? out : '';
  }

  function _companyFromEmail(email) {
    const s = String(email == null ? '' : email).trim().toLowerCase();
    const at = s.lastIndexOf('@');
    if (at < 1) return '';
    const domain = s.slice(at + 1).replace(/\.$/, '');
    if (!domain || !domain.includes('.')) return '';
    if (_CONSUMER_DOMAINS.has(domain)) return '';
    const labels = domain.split('.').filter(Boolean);
    if (labels.length < 2) return '';
    let core;
    if (labels.length >= 3 && labels[labels.length - 1].length <= 3 && labels[labels.length - 2].length <= 3) {
      core = labels[labels.length - 3];
    } else {
      core = labels[labels.length - 2];
    }
    return (core && core.length >= 2) ? core : '';
  }

  function _companyKeyAlone(r) {
    if (!r || typeof r !== 'object') return '';
    const named = _normalizeCompanyName(r.company || r.companyName || r.organization || r.organisation || r.employer);
    return named || _companyFromEmail(r.email);
  }

  // Colleagues on one WORK email domain are one company, whatever names they
  // typed (tasks #859, #892). CLIENT MIRROR of companyKeysForResponses in
  // server/utils/company-key.js — see the reasoning there; change both together.
  let _companyGroupMemo = { list: null, len: -1, map: null };
  function _companyGroupKeys() {
    const list = state.responses || [];
    if (_companyGroupMemo.list === list && _companyGroupMemo.len === list.length) return _companyGroupMemo.map;
    const base = new Map(), uses = new Map(), parent = new Map(), byDomain = new Map();
    list.forEach((r) => {
      if (!r || !r.id) return;
      const k = _companyKeyAlone(r);
      base.set(r.id, k);
      if (k) { uses.set(k, (uses.get(k) || 0) + 1); parent.set(k, k); }
      const d = _companyFromEmail(r.email);
      if (d && k) { if (!byDomain.has(d)) byDomain.set(d, new Set()); byDomain.get(d).add(k); }
    });
    const find = (k) => { while (parent.get(k) !== k) { parent.set(k, parent.get(parent.get(k))); k = parent.get(k); } return k; };
    byDomain.forEach((ks0) => {
      const ks = [...ks0];
      for (let i = 1; i < ks.length; i++) { const a = find(ks[0]), b = find(ks[i]); if (a !== b) parent.set(b, a); }
    });
    const best = new Map();
    uses.forEach((n, k) => {
      const root = find(k), cur = best.get(root);
      if (!cur || n > cur.n || (n === cur.n && k < cur.k)) best.set(root, { k, n });
    });
    const map = new Map();
    base.forEach((k, id) => map.set(id, k ? best.get(find(k)).k : ''));
    _companyGroupMemo = { list, len: list.length, map };
    return map;
  }

  function _companyKey(r) {
    if (!r || typeof r !== 'object') return '';
    const grouped = r.id ? _companyGroupKeys().get(r.id) : undefined;
    return grouped !== undefined ? grouped : _companyKeyAlone(r);
  }

  function _companyDisplayName(key) {
    const counts = new Map();
    state.responses.forEach((r) => {
      if (_companyKey(r) !== key) return;
      const raw = String(r.company || r.companyName || r.organization || r.organisation || r.employer || '').trim();
      if (raw) counts.set(raw, (counts.get(raw) || 0) + 1);
    });
    let best = '', bestN = 0;
    counts.forEach((n, raw) => { if (n > bestN) { best = raw; bestN = n; } });
    return best || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '');
  }

  /**
   * Companies whose attending guests are NOT all at one table yet — the basis
   * of the "seat these together?" prompt. Only reports a company with 2+
   * responses; one person is not a group. A company already fully seated at a
   * single table is not reported, because there is nothing to suggest.
   */
  function _companySeatingSuggestions() {
    const seating = _seatingState();
    // responseId → the set of tables that response's seats currently occupy.
    const tablesFor = new Map();
    (seating.tables || []).forEach((t) => {
      (t.seats || []).forEach((s) => {
        if (!tablesFor.has(s.responseId)) tablesFor.set(s.responseId, new Set());
        tablesFor.get(s.responseId).add(t.id);
      });
    });

    const byCompany = new Map();
    state.responses.filter(r => _seatStatus(r) === 'yes').forEach((r) => {
      const key = _companyKey(r);
      if (!key) return;
      if (!byCompany.has(key)) byCompany.set(key, []);
      byCompany.get(key).push(r);
    });

    const out = [];
    byCompany.forEach((rs, key) => {
      if (rs.length < 2) return;
      const tableIds = new Set();
      let anyUnseated = false;
      rs.forEach((r) => {
        const t = tablesFor.get(r.id);
        if (!t || t.size === 0) anyUnseated = true;
        else t.forEach(id => tableIds.add(id));
      });
      // Already together and fully seated → nothing to suggest.
      if (!anyUnseated && tableIds.size <= 1) return;
      out.push({
        key,
        name: _companyDisplayName(key),
        count: rs.length,
        guests: rs.reduce((a, r) => a + _partySize(r), 0),
        split: tableIds.size > 1
      });
    });
    return out.sort((a, b) => b.count - a.count);
  }

  async function _loadSeating() {
    if (_seatingLoaded) return state.seating;
    if (_seatingLoadingPromise) return _seatingLoadingPromise;
    _seatingLoadingPromise = (async () => {
      try {
        const token = await window.firebaseAuth.currentUser.getIdToken();
        const r = await fetch(`/api/seating/${encodeURIComponent(state.formId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await r.json();
        if (data.success && data.config) {
          state.seating = data.config;
          _seatingState(); // normalize
        } else {
          _seatingState();
        }
      } catch (err) {
        console.warn('Load seating failed:', err);
        _seatingState();
      } finally {
        _seatingLoaded = true;
        _seatingLoadingPromise = null;
      }
      return state.seating;
    })();
    return _seatingLoadingPromise;
  }

  // Saves go out one at a time, each saying which version of the chart it was
  // made on (task #858). The server refuses a save made on an older chart —
  // a guest seated at the door a moment ago, a colleague's edit — instead of
  // writing this copy over their seat. One at a time, because two saves in
  // flight would both claim the same version and the second would be refused.
  let _seatSaveChain = Promise.resolve();
  function _saveSeating() {
    _noteLocalWrite();
    const formId = state.formId;
    _seatSaveChain = _seatSaveChain.then(() => _sendSeating(formId)).catch(() => {});
    return _seatSaveChain;
  }

  async function _sendSeating(formId) {
    if (!formId || state.formId !== formId) return;
    try {
      const token = await window.firebaseAuth.currentUser.getIdToken();
      const s = _seatingState();
      const res = await fetch(`/api/seating/${encodeURIComponent(formId)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tables: s.tables,
          rules: s.rules,
          assignmentMode: s.assignmentMode || 'manual',
          defaultStrategy: s.defaultStrategy || 'roundrobin',
          expectedVersion: Number(s.version) || 0
        })
      });
      if (res.status === 409) {
        // The chart moved on under us. Show the real one and say what happened;
        // the host redoes the one move rather than losing a door seat.
        const data = await res.json().catch(() => ({}));
        if (state.formId === formId && data.config) {
          state.seating = data.config;
          _seatingState();
          _seatIdxCache = null;
          _renderActiveTab();
        }
        // Short, and one at a time (#902): a burst of conflicts replaces the
        // notice on screen instead of stacking more.
        _showToast('Chart updated elsewhere. Please redo your last change.', 'warning', { key: 'seating-conflict' });
        return;
      }
      if (!res.ok) throw new Error('Save failed (' + res.status + ')');
      const data = await res.json().catch(() => ({}));
      if (typeof data.version === 'number' && state.formId === formId && state.seating) state.seating.version = data.version;
      _noteLocalWrite();
    } catch (err) {
      _showToast('Could not save seating: ' + err.message, 'danger');
    }
  }

  // --- Undo/Redo ---
  state.undoStack = [];
  state.redoStack = [];
  function _pushUndo() {
    state.undoStack.push(JSON.stringify(_seatingState()));
    if (state.undoStack.length > 20) state.undoStack.shift();
    state.redoStack = [];
  }
  // A snapshot carries the chart version of its moment; the restored chart
  // is saved on top of the CURRENT one, so it takes the current version
  // (#858) — otherwise every undo after a save would be refused as stale.
  function undo() {
    if (!state.undoStack.length) return;
    const v = _seatingState().version;
    state.redoStack.push(JSON.stringify(_seatingState()));
    state.seating = JSON.parse(state.undoStack.pop());
    state.seating.version = v;
    _renderActiveTab();
    _saveSeating();
  }
  function redo() {
    if (!state.redoStack.length) return;
    const v = _seatingState().version;
    state.undoStack.push(JSON.stringify(_seatingState()));
    state.seating = JSON.parse(state.redoStack.pop());
    state.seating.version = v;
    _renderActiveTab();
    _saveSeating();
  }

  // --- Party expansion ---
  function _buildMembersForResponse(r) {
    // Mirror admin.js calculateTotalGuests: guestCount (or adultCount +
    // childrenCount) already includes the primary — do not add +1.
    let total = _partySize(r);
    if (!Number.isFinite(total) || total < 1) total = 1;

    const isMaybe = _isMaybeResponse(r);
    const primary = r.guestName || r.name || r.email || 'Guest';
    const members = [{ responseId: r.id, guestName: primary, isGroupMember: false, isMaybe }];

    if (Array.isArray(r.individualNames)) {
      r.individualNames.forEach((entry) => {
        const entryName = (typeof entry === 'object' && entry !== null) ? (entry.name || '') : String(entry || '');
        if (!entryName.trim()) return;
        const pos = (typeof entry === 'object' && entry !== null) ? entry.position : undefined;
        if (pos === 1 || entryName === primary) return;
        if (!members.some(m => m.guestName === entryName)) {
          members.push({ responseId: r.id, guestName: entryName, isGroupMember: true, isMaybe });
        }
      });
    }
    while (members.length < total) {
      members.push({ responseId: r.id, guestName: `${primary} +${members.length}`, isGroupMember: true, isMaybe });
    }
    return members;
  }

  function _memberKey(m) { return `${m.responseId}::${m.guestName}`; }

  // ---- Table per guest, for the Responses tab (task #856) -----------------
  // One pass over the chart per draw: response id -> every table its party
  // sits at, and response::name -> that one person's table. Names compare
  // trimmed and case-blind, as the server's check-in roster does.
  let _seatIdxCache = null;
  const _seatNameKey = (responseId, name) => `${responseId}::${String(name || '').trim().toLowerCase()}`;
  // ---- Check-in column on Responses (task #904) ----------------------------
  // Shown once anyone has checked in. A compact time pill ("10:14 AM"), with
  // the date added only when arrivals span more than one day; the full date is
  // on hover and in the export.
  function _checkinStarted() {
    return (state.responses || []).some(r => r && r.checkedInAt);
  }
  function _checkinMultiDay() {
    const days = new Set();
    (state.responses || []).forEach((r) => {
      const d = r && r.checkedInAt ? _toDate(r.checkedInAt) : null;
      if (d) days.add(d.toDateString());
    });
    return days.size > 1;
  }
  function _checkinPill(v, col) {
    const d = v ? _toDate(v) : null;
    if (!d) return '<span class="text-muted">—</span>';
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    const label = col && col.multiDay
      ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ', ' + time
      : time;
    return `<span class="oed-checkin-pill" title="Checked in ${_esc(_fmtDate(d))}"><i class="bi bi-check2-circle"></i> ${_esc(label)}</span>`;
  }

  function _seatingHasTables() {
    return !!(_seatingLoaded && state.seating && Array.isArray(state.seating.tables) && state.seating.tables.length);
  }
  function _seatIdx() {
    if (_seatIdxCache) return _seatIdxCache;
    const byResponse = new Map();
    const byMember = new Map();
    const tables = (state.seating && Array.isArray(state.seating.tables)) ? state.seating.tables : [];
    tables.forEach(t => (t.seats || []).forEach(seat => {
      if (!seat || !seat.responseId) return;
      const name = String(t.name || '').trim() || 'Table';
      const list = byResponse.get(seat.responseId) || [];
      if (!list.includes(name)) list.push(name);
      byResponse.set(seat.responseId, list);
      byMember.set(_seatNameKey(seat.responseId, seat.guestName), name);
    }));
    _seatIdxCache = { byResponse, byMember };
    return _seatIdxCache;
  }

  // ── Potential overflow (task #887) ──────────────────────────────────────
  // At the door only the people who have ARRIVED get seats, so a table can
  // read 7/8 with one of a party of five at it. Per table: who is still
  // expected to join it - the rest of a party that has checked in and sits
  // there, and (QR Auto + Group by company) colleagues not here yet and not
  // seated, counted at the table holding most of their company, which is
  // where QR Auto will put them - and whether they would fit. The party half
  // mirrors seating.service.js _overflowNote (the door's heads-up).
  // Map tableId -> { toCome, free, who: [label] }, tables that would overflow only.
  function _tableOverflow() {
    const idx = _seatIdx();
    if (idx.overflow) return idx.overflow;
    const seating = _seatingState();
    const byId = new Map(state.responses.map(r => [r.id, r]));
    const seatsOf = new Map(); // responseId -> Map(tableId -> seats)
    seating.tables.forEach(t => (t.seats || []).forEach((s) => {
      if (!s || !s.responseId) return;
      if (!seatsOf.has(s.responseId)) seatsOf.set(s.responseId, new Map());
      const m = seatsOf.get(s.responseId);
      m.set(t.id, (m.get(t.id) || 0) + 1);
    }));
    const homeOf = (m) => { let best = null, n = 0; m.forEach((c, id) => { if (c > n) { n = c; best = id; } }); return best; };
    const expected = new Map();
    const add = (tableId, n, label) => {
      if (!expected.has(tableId)) expected.set(tableId, { toCome: 0, who: [] });
      const e = expected.get(tableId);
      e.toCome += n;
      e.who.push(label);
    };
    const nameOf = (r) => r.guestName || r.name || r.email || 'Guest';
    seatsOf.forEach((m, rid) => {
      const r = byId.get(rid);
      if (!r || !r.checkedInAt) return;
      let seated = 0;
      m.forEach((c) => { seated += c; });
      const toCome = _partySize(r) - seated;
      if (toCome > 0) add(homeOf(m), toCome, `${nameOf(r)}'s party (${toCome})`);
    });
    if (seating.assignmentMode === 'qr_auto' && seating.rules.groupByCompany) {
      const companyAt = new Map(); // company key -> Map(tableId -> seats)
      seatsOf.forEach((m, rid) => {
        const k = _companyKey(byId.get(rid));
        if (!k) return;
        if (!companyAt.has(k)) companyAt.set(k, new Map());
        const ct = companyAt.get(k);
        m.forEach((c, tid) => ct.set(tid, (ct.get(tid) || 0) + c));
      });
      state.responses.forEach((r) => {
        if (seatsOf.has(r.id) || r.checkedInAt || _seatStatus(r) !== 'yes') return;
        const k = _companyKey(r);
        const ct = k && companyAt.get(k);
        if (ct) add(homeOf(ct), _partySize(r), `${nameOf(r)} (${_companyDisplayName(k)})`);
      });
    }
    const warn = new Map();
    seating.tables.forEach((t) => {
      const e = expected.get(t.id);
      const cap = parseInt(t.capacity, 10) || 0;
      if (!e || !cap) return;
      const free = Math.max(0, cap - (t.seats || []).length);
      if (e.toCome > free) warn.set(t.id, { toCome: e.toCome, free, who: e.who });
    });
    idx.overflow = warn;
    return warn;
  }

  function _overflowSentence(o) {
    return `Potential overflow if everyone arrives: ${o.toCome} more expected, ${o.free ? `${o.free} free seat${o.free === 1 ? '' : 's'}` : 'no free seats'}.`;
  }
  function _memberTable(responseId, name) {
    return _seatIdx().byMember.get(_seatNameKey(responseId, name)) || '';
  }

  function _assignedMemberKeys() {
    const keys = new Set();
    _seatingState().tables.forEach(t => (t.seats || []).forEach(s => keys.add(_memberKey(s))));
    return keys;
  }

  // --- Rule violation checks ---
  function _tableViolations(t) {
    const rules = _seatingState().rules;
    const ids = new Set((t.seats || []).map(s => s.responseId));
    const v = [];
    for (const [a, b] of rules.keepApart) if (ids.has(a) && ids.has(b)) v.push({ type: 'apart', ids: [a, b] });
    return v;
  }

  function _renderSeating(el) {
    if (!_seatingLoaded) {
      el.innerHTML = '<div class="text-center py-5"><div class="org-spinner"></div><p class="text-muted mt-2">Loading seating…</p></div>';
      _loadSeating().then(() => { if (state.view === 'seating') _renderSeating(el); });
      return;
    }

    const seating = _seatingState();
    const tables = seating.tables;
    const attending = state.responses.filter(r => _seatStatus(r) === 'yes');
    const allMembers = attending.flatMap(_buildMembersForResponse);
    const assignedKeys = _assignedMemberKeys();
    const unassigned = allMembers.filter(m => !assignedKeys.has(_memberKey(m)));

    const totalSeats = tables.reduce((a, t) => a + (parseInt(t.capacity, 10) || 0), 0);
    const seatedCount = tables.reduce((a, t) => a + (t.seats?.length || 0), 0);
    // The company switch counts as a rule too — the badge is how a host sees
    // at a glance that something is shaping their auto-assign (task #498).
    const rulesCount = seating.rules.keepTogether.length + seating.rules.keepApart.length
      + (seating.rules.groupByCompany ? 1 : 0);

    el.innerHTML = `
      <div class="event-detail-stats">
        <div class="event-detail-stat"><span class="value">${tables.length}</span><span class="label">Tables</span></div>
        <div class="event-detail-stat stat-guests"><span class="value">${seatedCount}</span><span class="label">Seated</span></div>
        <div class="event-detail-stat"><span class="value">${unassigned.length}</span><span class="label">Unassigned</span></div>
        <div class="event-detail-stat"><span class="value">${totalSeats}</span><span class="label">Total seats</span></div>
      </div>

      ${_companySuggestionBanner()}

      <div class="event-detail-toolbar">
        <div class="event-detail-toolbar-left">
          <input type="search" class="form-control form-control-sm oed-seating-search" value="${_esc(_seatingSearch)}"
                 placeholder="Search tables or guests…" id="seatingSearchInput">
          <!-- #846 round 2: undo/redo as one compact pair, and the Manual / QR
               Auto switch (plus its picker) beside them on the LEFT, so the
               action buttons on the right never move when the mode changes. -->
          <div class="oed-history" role="group" aria-label="Undo and redo">
            <button type="button" class="oed-history-btn" onclick="OrgEventDetail.undo()" ${state.undoStack.length === 0 ? 'disabled' : ''} title="Undo" aria-label="Undo">
              <i class="bi bi-arrow-counterclockwise"></i>
            </button>
            <button type="button" class="oed-history-btn" onclick="OrgEventDetail.redo()" ${state.redoStack.length === 0 ? 'disabled' : ''} title="Redo" aria-label="Redo">
              <i class="bi bi-arrow-clockwise"></i>
            </button>
          </div>
          ${_seatingModeHtml(seating)}
        </div>
        <!-- Rules / Export / Print were ghost buttons: borderless on the grey
             bar, they read as labels beside Add tables (task #1120). Outline, as
             on every other toolbar on this page; Auto-assign stays the one fill. -->
        <div class="event-detail-toolbar-right">
          <button class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.openRulesModal()">
            <i class="bi bi-shield-check"></i> Rules${rulesCount ? ` (${rulesCount})` : ''}
          </button>
          <button class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.exportSeatingCsv()">
            <i class="bi bi-download"></i> Export
          </button>
          <button class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.printSeating()">
            <i class="bi bi-printer"></i> Print
          </button>
          <!-- Deliberately NOT disabled when there are no tables yet (task #497,
               round 2). A host looking at five unassigned guests reads a dead
               button as broken, because the one thing it will not tell them is
               why. It now explains and takes them straight to Add tables. -->
          <button class="btn btn-org-primary btn-sm" onclick="OrgEventDetail.autoAssign()"
                  ${tables.length === 0 ? 'title="Add some tables first — auto-assign needs somewhere to seat people."' : ''}>
            <i class="bi bi-magic"></i> Auto-assign
          </button>
          <button class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.openAddTableModal()">
            <i class="bi bi-plus-lg"></i> Add tables
          </button>
        </div>
      </div>

      ${_qrAutoBannerHtml(seating, rulesCount)}

      <div class="seating-layout">
        <aside class="seating-unassigned-panel" data-drop-target="unassigned">
          <div class="seating-unassigned-head">
            <strong><i class="bi bi-people"></i> Unassigned</strong>
            <span class="badge-count">${unassigned.length}</span>
          </div>
          <div class="seating-unassigned-list">
            ${unassigned.length === 0
              ? (attending.length === 0
                  ? '<div class="seating-unassigned-empty">No confirmed attendees yet.</div>'
                  : '<div class="seating-unassigned-empty"><i class="bi bi-check-circle"></i> Everyone is seated.</div>')
              : _renderUnassignedSections(unassigned, attending)}
          </div>
        </aside>
        <div class="seating-main">
          ${tables.length === 0
            ? `<div class="event-detail-empty"><i class="bi bi-grid-3x3"></i><p>No tables yet. Add tables to start seating.</p></div>`
            : `<div class="seating-grid">${tables.map(_renderTableCard).join('')}</div>`}
        </div>
      </div>
    `;

    _wireSeatingInteractions(el);
  }

  /**
   * "Would you like to seat these together?" (task #498) — shown only when
   * there is something to act on: the rule is off AND at least one company has
   * colleagues who are unseated or scattered across tables. Silent once the
   * rule is on, and silent when every company is already sitting together,
   * because a prompt that is always there stops being read.
   */
  function _companySuggestionBanner() {
    const seating = _seatingState();
    if (seating.rules.groupByCompany || _companySuggestionDismissed) return '';
    const sug = _companySeatingSuggestions();
    if (sug.length === 0) return '';
    const top = sug.slice(0, 3);
    const rest = sug.length - top.length;
    const phrase = top.map(s =>
      `<strong>${_esc(s.name)}</strong> (${s.count} ${s.count === 1 ? 'guest' : 'guests'}${s.split ? ', currently split' : ''})`
    ).join(', ');
    return `
      <div class="alert alert-info d-flex flex-wrap align-items-center gap-2 py-2" role="status"
           style="font-size:0.88rem;">
        <i class="bi bi-building"></i>
        <span style="flex:1 1 260px;">
          Guests from the same company are not sitting together: ${phrase}${rest > 0 ? `, and ${rest} more` : ''}.
        </span>
        <button class="btn btn-org-primary btn-sm" onclick="OrgEventDetail.setGroupByCompany(true)">
          Seat companies together
        </button>
        <button class="btn btn-org-ghost btn-sm" onclick="OrgEventDetail.dismissCompanySuggestion()">
          Not now
        </button>
      </div>`;
  }

  /** Hide the prompt for this session only. Deliberately not persisted: it is
   *  advice about the CURRENT guest list, and a list that changes tomorrow
   *  deserves to be asked about again. */
  function dismissCompanySuggestion() {
    _companySuggestionDismissed = true;
    _renderActiveTab();
  }
  let _companySuggestionDismissed = false;

  function _renderMemberChip(m, tableId) {
    const badge = m.isGroupMember ? '<span class="chip-group-dot" title="Party member"></span>' : '';
    const maybeMark = m.isMaybe ? '<i class="bi bi-question-circle chip-maybe-mark" title="Maybe attending"></i>' : '';
    const memberKey = `${m.responseId}::${m.guestName}`;
    const selected = _seatingSelectedKeys.has(memberKey);
    const extraClasses = [
      tableId ? 'seated' : '',
      m.isMaybe ? 'is-maybe' : '',
      selected ? 'selected' : ''
    ].filter(Boolean).join(' ');
    return `
      <div class="seating-chip ${extraClasses}" draggable="true"
           data-response-id="${_esc(m.responseId)}"
           data-guest-name="${_esc(m.guestName)}"
           ${tableId ? `data-from-table="${_esc(tableId)}"` : ''}>
        ${badge}${maybeMark}<span class="chip-name">${_esc(m.guestName)}</span>
        <span class="dropdown chip-assign">
          <button class="chip-assign-btn" data-bs-toggle="dropdown" data-bs-strategy="fixed"
                  data-response-id="${_esc(m.responseId)}"
                  data-guest-name="${_esc(m.guestName)}"
                  data-from-table="${tableId ? _esc(tableId) : ''}"
                  title="Assign to…">
            <i class="bi bi-three-dots"></i>
          </button>
          <!-- menu populated lazily on show -->
          <ul class="dropdown-menu dropdown-menu-end chip-assign-menu"></ul>
        </span>
        ${tableId ? `<button class="chip-remove" title="Unseat" onclick="OrgEventDetail.unseatMember('${_esc(tableId)}','${_esc(m.responseId)}','${_esc(m.guestName.replace(/'/g, "\\'"))}')"><i class="bi bi-x"></i></button>` : ''}
      </div>`;
  }

  // Small color palette for category dots. Keyed by stable hash of the
  // relationship label so the same label always gets the same color across renders.
  const _CATEGORY_COLORS = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#FF5722', '#607D8B', '#795548', '#E91E63'];
  function _categoryColor(key) {
    if (key === NO_CATEGORY_KEY) return '#9aa0a6';
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return _CATEGORY_COLORS[h % _CATEGORY_COLORS.length];
  }

  /**
   * Group unassigned members by their response's relationship/category and
   * render each as a collapsible, draggable section. Dragging a section header
   * onto a table fills that table with as many of the section's members as fit.
   */
  function _renderUnassignedSections(unassigned, attending) {
    // Index responses by id for quick lookup, then group members by category
    const byId = new Map();
    for (const r of state.responses) byId.set(r.id, r);

    const sections = new Map(); // key -> { label, color, members: [] }
    for (const m of unassigned) {
      const r = byId.get(m.responseId);
      const raw = (r?.relationship || '').trim();
      const key = raw.toLowerCase() || NO_CATEGORY_KEY;
      if (!sections.has(key)) {
        sections.set(key, {
          key,
          label: raw || NO_CATEGORY_LABEL,
          color: _categoryColor(key),
          members: []
        });
      }
      sections.get(key).members.push(m);
    }

    return Array.from(sections.values()).map(s => {
      const collapsed = _seatingCollapsedSections.has(s.key);
      const draggable = s.key !== NO_CATEGORY_KEY;
      return `
        <div class="seating-cat-section${collapsed ? ' collapsed' : ''}" data-cat-key="${_esc(s.key)}">
          <div class="seating-cat-header"
            data-action="toggle-cat-section"
            data-section-key="${_esc(s.key)}"
            ${draggable ? `draggable="true" data-drag-mode="category" data-category-key="${_esc(s.key)}"` : ''}
            ${draggable ? 'title="Drag to seat the whole category on a table"' : ''}>
            <i class="bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-down'} seating-cat-chevron"></i>
            <span class="seating-cat-dot" style="background:${s.color};"></span>
            <span class="seating-cat-label">${_esc(s.label)}</span>
            <span class="seating-cat-count">${s.members.length}</span>
          </div>
          <div class="seating-cat-body">
            ${s.members.map(m => _renderMemberChip(m, null)).join('')}
          </div>
        </div>
      `;
    }).join('');
  }

  function _renderTableCard(t) {
    const capacity = parseInt(t.capacity, 10) || 0;
    const seats = t.seats || [];
    const seated = seats.length;
    const over = capacity > 0 && seated > capacity;
    const shape = t.type === 'rectangular' ? 'rectangular' : 'round';
    const shapeIcon = shape === 'round' ? 'bi-circle' : 'bi-square';
    const emptySlots = Math.max(0, capacity - seated);
    const violations = _tableViolations(t);
    const hasViolation = violations.length > 0;

    // Count seated guests whose underlying response is a "maybe" RSVP, so the
    // planner sees at a glance how many seats at this table aren't confirmed.
    const respById = new Map();
    for (const r of state.responses) respById.set(r.id, r);
    const maybeCount = seats.reduce((acc, s) => acc + (_isMaybeResponse(respById.get(s.responseId)) ? 1 : 0), 0);
    const maybeBadge = maybeCount > 0
      ? `<span class="seating-maybe-badge" title="${maybeCount} maybe-attending guest(s) at this table"><i class="bi bi-question-circle"></i>${maybeCount}</span>`
      : '';
    const overflow = _tableOverflow().get(t.id);
    const overflowLine = overflow
      ? `<div class="seating-overflow-note" title="Still to come: ${_esc(overflow.who.join(', '))}"><i class="bi bi-exclamation-circle"></i><span>${_esc(_overflowSentence(overflow))}</span></div>`
      : '';

    return `
      <div class="seating-table shape-${shape} ${over ? 'is-over' : ''} ${hasViolation ? 'has-violation' : ''}" data-table-id="${_esc(t.id)}">
        <div class="seating-table-head">
          <i class="bi ${shapeIcon} seating-shape-icon" title="Toggle shape"
             onclick="OrgEventDetail.toggleShape('${_esc(t.id)}')"></i>
          <input type="text" class="seating-table-name" value="${_esc(t.name || 'Table')}"
                 onblur="OrgEventDetail.renameTable('${_esc(t.id)}', this.value)"
                 aria-label="Table name">
          <div class="seating-table-meta">
            ${hasViolation ? '<span class="seating-violation" title="Seating rule conflict"><i class="bi bi-exclamation-triangle-fill"></i></span>' : ''}
            ${maybeBadge}
            <span class="${over ? 'text-danger' : 'text-body-secondary'}">${seated}/${capacity || '∞'}</span>
            <button class="btn-icon-ghost" title="Remove table" onclick="OrgEventDetail.removeTable('${_esc(t.id)}')">
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>
        ${overflowLine}
        <div class="seating-table-capacity">
          <label>Capacity</label>
          <input type="number" min="0" value="${capacity}"
                 onchange="OrgEventDetail.setCapacity('${_esc(t.id)}', this.value)">
        </div>
        <div class="seating-table-seats" data-drop-target="table" data-table-id="${_esc(t.id)}">
          ${seats.map(s => _renderMemberChip(s, t.id)).join('')}
          ${Array.from({ length: emptySlots }, () => '<div class="seating-empty-slot"><i class="bi bi-person-dash"></i></div>').join('')}
        </div>
      </div>`;
  }

  /**
   * The Seating tab search (task #895). The unassigned list narrows to the
   * guests who match; the tables NEVER disappear - they are where the found
   * guests get dropped. A seated guest or a table whose name matches is
   * highlighted instead. It used to hide every table card that did not
   * contain the text, so a guest search emptied the whole grid. Matching is
   * on the guest's name and the table's name only (a chip's text also holds
   * its "Assign to" menu, which lists every table).
   */
  function _applySeatingSearch(el) {
    const q = String(_seatingSearch || '').toLowerCase().trim();
    const hit = (s) => !!q && String(s || '').toLowerCase().includes(q);
    const chipName = (chip) => chip.dataset.guestName || chip.querySelector('.chip-name')?.textContent || '';
    el.querySelectorAll('.seating-unassigned-list .seating-chip').forEach((chip) => {
      chip.style.display = !q || hit(chipName(chip)) ? '' : 'none';
    });
    // A category with no match left folds away, so the list is just the hits.
    el.querySelectorAll('.seating-unassigned-list .seating-cat-section').forEach((sec) => {
      const any = [...sec.querySelectorAll('.seating-chip')].some(c => c.style.display !== 'none');
      sec.style.display = !q || any ? '' : 'none';
    });
    el.querySelectorAll('.seating-table').forEach((card) => {
      card.style.display = '';
      card.classList.toggle('search-hit', hit(card.querySelector('.seating-table-name')?.value));
      card.querySelectorAll('.seating-chip').forEach(chip => chip.classList.toggle('search-hit', hit(chipName(chip))));
    });
  }

  function _wireSeatingInteractions(el) {
    const search = document.getElementById('seatingSearchInput');
    if (search) search.addEventListener('input', (e) => {
      _seatingSearch = e.target.value;
      _applySeatingSearch(el);
    });
    // A redraw (a drop, an undo, a live update) keeps the search applied.
    _applySeatingSearch(el);

    el.querySelectorAll('.seating-chip').forEach(chip => {
      // Shift/cmd/ctrl-click to toggle multi-select. Plain clicks pass through
      // (so DnD still works and clicking the dropdown button still opens it).
      chip.addEventListener('click', (e) => {
        if (!(e.shiftKey || e.metaKey || e.ctrlKey)) return;
        if (e.target.closest('.chip-assign, .chip-remove')) return;
        e.preventDefault();
        const key = `${chip.dataset.responseId}::${chip.dataset.guestName}`;
        if (_seatingSelectedKeys.has(key)) _seatingSelectedKeys.delete(key);
        else _seatingSelectedKeys.add(key);
        chip.classList.toggle('selected');
      });

      chip.addEventListener('dragstart', (e) => {
        const key = `${chip.dataset.responseId}::${chip.dataset.guestName}`;
        // If the dragged chip is part of a multi-selection, move the whole set.
        if (_seatingSelectedKeys.size > 1 && _seatingSelectedKeys.has(key)) {
          e.dataTransfer.setData('text/plain', JSON.stringify({
            dragMode: 'multi',
            memberKeys: [..._seatingSelectedKeys]
          }));
        } else {
          e.dataTransfer.setData('text/plain', JSON.stringify({
            dragMode: 'member',
            responseId: chip.dataset.responseId,
            guestName: chip.dataset.guestName,
            fromTable: chip.dataset.fromTable || null
          }));
        }
        chip.classList.add('dragging');
      });
      chip.addEventListener('dragend', () => chip.classList.remove('dragging'));
    });

    // Per-chip "Assign to…" dropdown. The menu is populated lazily on `show`
    // because the table list (and which tables have remaining capacity) can
    // change between renders. Popper strategy:"fixed" lets the menu escape
    // the unassigned panel's overflow:auto.
    if (typeof bootstrap !== 'undefined' && bootstrap.Dropdown) {
      el.querySelectorAll('.chip-assign-btn').forEach(btn => {
        bootstrap.Dropdown.getOrCreateInstance(btn, {
          popperConfig: (defaultConfig) => ({ ...defaultConfig, strategy: 'fixed' })
        });
        btn.parentElement.addEventListener('show.bs.dropdown', () => {
          const menu = btn.parentElement.querySelector('.chip-assign-menu');
          if (!menu) return;
          const tables = _seatingState().tables;
          const fromTable = btn.dataset.fromTable || '';
          if (tables.length === 0) {
            menu.innerHTML = '<li><span class="dropdown-item-text text-muted small">No tables yet</span></li>';
            return;
          }
          menu.innerHTML = '<li><h6 class="dropdown-header">Assign to…</h6></li>' +
            tables.map(t => {
              const cap = parseInt(t.capacity, 10) || 0;
              const seated = (t.seats || []).length;
              const free = Math.max(0, cap - seated);
              const isCurrent = fromTable === t.id;
              const disabled = (free <= 0 && !isCurrent) || isCurrent;
              return `<li>
                <a class="dropdown-item ${disabled ? 'disabled' : ''}" href="#"
                   data-chip-assign-target="${_esc(t.id)}">
                  <span>${_esc(t.name || 'Table')}</span>
                  <span class="chip-assign-cap ${free <= 0 ? 'full' : ''}">${seated}/${cap || '∞'}</span>
                </a>
              </li>`;
            }).join('');
        });
      });

      // Click handler for the menu items (event-delegated on the unassigned/main areas).
      el.addEventListener('click', (e) => {
        const item = e.target.closest('[data-chip-assign-target]');
        if (!item || item.classList.contains('disabled')) return;
        e.preventDefault();
        const trigger = item.closest('.chip-assign')?.querySelector('.chip-assign-btn');
        if (!trigger) return;
        const responseId = trigger.dataset.responseId;
        const guestName = trigger.dataset.guestName;
        const fromTable = trigger.dataset.fromTable || null;
        const toTable = item.dataset.chipAssignTarget;
        if (responseId && guestName && toTable) {
          _moveMember(responseId, guestName, fromTable, toTable);
        }
      });
    }

    // Category sections — click to collapse/expand; drag the header to seat
    // the entire category on a table.
    el.querySelectorAll('.seating-cat-header').forEach(header => {
      header.addEventListener('click', (e) => {
        // Don't toggle on drag motion (mousedown→mousemove→mouseup); only on plain click.
        const key = header.dataset.sectionKey;
        if (!key) return;
        if (_seatingCollapsedSections.has(key)) _seatingCollapsedSections.delete(key);
        else _seatingCollapsedSections.add(key);
        _renderActiveTab();
      });
      if (header.dataset.dragMode === 'category') {
        header.addEventListener('dragstart', (e) => {
          _seatingDragCategoryKey = header.dataset.categoryKey;
          e.dataTransfer.setData('text/plain', JSON.stringify({
            dragMode: 'category',
            categoryKey: header.dataset.categoryKey
          }));
          e.dataTransfer.effectAllowed = 'move';
          header.classList.add('dragging');
        });
        header.addEventListener('dragend', () => {
          _seatingDragCategoryKey = null;
          header.classList.remove('dragging');
        });
      }
    });

    el.querySelectorAll('[data-drop-target]').forEach(zone => {
      zone.addEventListener('dragover', (e) => { e.preventDefault(); zone.classList.add('drag-over'); });
      zone.addEventListener('dragleave', (e) => {
        if (!zone.contains(e.relatedTarget)) zone.classList.remove('drag-over');
      });
      zone.addEventListener('drop', (e) => {
        e.preventDefault();
        zone.classList.remove('drag-over');
        try {
          const data = JSON.parse(e.dataTransfer.getData('text/plain'));
          const toTable = zone.dataset.dropTarget === 'table' ? zone.dataset.tableId : null;
          if (data.dragMode === 'category' && toTable) {
            _assignCategoryToTable(data.categoryKey, toTable);
          } else if (data.dragMode === 'multi' && toTable) {
            _assignMultiToTable(data.memberKeys || [], toTable);
          } else {
            _moveMember(data.responseId, data.guestName, data.fromTable, toTable);
          }
        } catch {}
      });
    });
  }

  /**
   * Fill `toTableId` with the provided member keys (from a multi-select drag),
   * up to its remaining capacity. Toasts the overflow.
   */
  function _assignMultiToTable(memberKeys, toTableId) {
    const seating = _seatingState();
    const table = seating.tables.find(t => t.id === toTableId);
    if (!table) return;
    if (!table.seats) table.seats = [];

    const assignedKeys = _assignedMemberKeys();
    const respById = new Map();
    for (const r of state.responses) respById.set(r.id, r);

    const candidates = [];
    for (const key of memberKeys) {
      if (assignedKeys.has(key)) continue;
      const sep = key.indexOf('::');
      if (sep < 0) continue;
      const responseId = key.slice(0, sep);
      const guestName = key.slice(sep + 2);
      const r = respById.get(responseId);
      const isGroup = r ? _buildMembersForResponse(r).length > 1 : false;
      candidates.push({ responseId, guestName, isGroupMember: isGroup });
    }
    if (candidates.length === 0) return;

    const cap = parseInt(table.capacity, 10) || 0;
    const remaining = Math.max(0, cap - table.seats.length);
    const toPlace = candidates.slice(0, remaining);
    if (toPlace.length === 0) {
      _showToast(`${table.name || 'Table'} is full — pick another table`, 'warning');
      return;
    }
    _pushUndo();
    toPlace.forEach(m => table.seats.push(m));
    _seatingSelectedKeys.clear();
    _renderActiveTab();
    _saveSeating();
    const overflow = candidates.length - toPlace.length;
    if (overflow > 0) {
      _showToast(`Seated ${toPlace.length} of ${candidates.length}. ${overflow} couldn’t fit — pick another table.`, 'info');
    } else if (toPlace.length > 1) {
      _showToast(`Seated ${toPlace.length} guests.`, 'success');
    }
  }

  /**
   * Move every unassigned member of `categoryKey` onto `toTableId`, filling
   * up to the table's remaining capacity. Reports overflow via toast.
   */
  function _assignCategoryToTable(categoryKey, toTableId) {
    const seating = _seatingState();
    const table = seating.tables.find(t => t.id === toTableId);
    if (!table) return;
    if (!table.seats) table.seats = [];

    // Build candidates: every unassigned member from responses in this category.
    const attending = state.responses.filter(r => _seatStatus(r) === 'yes');
    const assignedKeys = _assignedMemberKeys();
    const candidates = [];
    for (const r of attending) {
      const key = (r.relationship || '').trim().toLowerCase() || NO_CATEGORY_KEY;
      if (key !== categoryKey) continue;
      const isGroup = _buildMembersForResponse(r).length > 1;
      for (const m of _buildMembersForResponse(r)) {
        if (!assignedKeys.has(_memberKey(m))) {
          candidates.push({ responseId: m.responseId, guestName: m.guestName, isGroupMember: isGroup });
        }
      }
    }
    if (candidates.length === 0) return;

    const cap = parseInt(table.capacity, 10) || 0;
    const remaining = Math.max(0, cap - table.seats.length);
    const toPlace = candidates.slice(0, remaining);
    if (toPlace.length === 0) {
      _showToast(`${table.name || 'Table'} is full — pick another table`, 'warning');
      return;
    }
    _pushUndo();
    toPlace.forEach(m => table.seats.push(m));
    _renderActiveTab();
    _saveSeating();
    const overflow = candidates.length - toPlace.length;
    if (overflow > 0) {
      _showToast(`Seated ${toPlace.length} of ${candidates.length}. ${overflow} couldn’t fit — pick another table.`, 'info');
    } else if (toPlace.length > 1) {
      _showToast(`Seated ${toPlace.length} guests.`, 'success');
    }
  }

  function _moveMember(responseId, guestName, fromTableId, toTableId) {
    const seating = _seatingState();
    _pushUndo();
    if (fromTableId) {
      const from = seating.tables.find(t => t.id === fromTableId);
      if (from) from.seats = (from.seats || []).filter(s => !(s.responseId === responseId && s.guestName === guestName));
    }
    if (toTableId) {
      const to = seating.tables.find(t => t.id === toTableId);
      if (!to) return;
      if (!to.seats) to.seats = [];
      if (!to.seats.some(s => s.responseId === responseId && s.guestName === guestName)) {
        to.seats.push({ responseId, guestName, isGroupMember: false });
      }
    }
    _renderActiveTab();
    _saveSeating();
  }

  function unseatMember(tableId, responseId, guestName) {
    _moveMember(responseId, guestName, tableId, null);
  }

  function openAddTableModal() {
    const id = 'seatingAddTableModal';
    document.getElementById(id)?.remove();
    const modal = document.createElement('div');
    modal.className = 'modal fade';
    modal.id = id;
    modal.tabIndex = -1;
    const seating = _seatingState();
    const nextNum = seating.tables.length + 1;
    modal.innerHTML = `
      <div class="modal-dialog modal-sm">
        <div class="modal-content">
          <div class="modal-header py-2">
            <h6 class="modal-title">Add tables</h6>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-2">
              <label class="form-label small fw-medium">How many?</label>
              <input type="number" class="form-control form-control-sm" id="addTableCount" min="1" max="50" value="1">
            </div>
            <div class="mb-2" id="addTableNameGroup">
              <label class="form-label small fw-medium">Name</label>
              <input type="text" class="form-control form-control-sm" id="addTableName" value="Table ${nextNum}" placeholder="Head table">
            </div>
            <div class="mb-2">
              <label class="form-label small fw-medium">Capacity (each)</label>
              <input type="number" class="form-control form-control-sm" id="addTableCapacity" min="1" max="50" value="8">
            </div>
            <div class="mb-2" id="addTableDescGroup">
              <label class="form-label small fw-medium">Description</label>
              <textarea class="form-control form-control-sm" id="addTableDescription" rows="2"
                placeholder="Only you see this — e.g. near the stage"></textarea>
            </div>
          </div>
          <div class="modal-footer py-2">
            <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-org-primary btn-sm" id="addTableConfirmBtn">Add</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(modal);

    // Shape picker removed (task #499): the seating view is a list, so the
    // choice never showed up anywhere a host could see. Existing tables keep
    // whatever shape they were saved with.
    const selectedShape = 'round';

    const countInput = modal.querySelector('#addTableCount');
    const nameGroup = modal.querySelector('#addTableNameGroup');
    const descGroup = modal.querySelector('#addTableDescGroup');
    countInput.addEventListener('input', () => {
      const c = parseInt(countInput.value, 10) || 1;
      // A name and a note are per-table; neither means anything across a batch.
      nameGroup.style.display = c > 1 ? 'none' : '';
      descGroup.style.display = c > 1 ? 'none' : '';
    });

    modal.querySelector('#addTableConfirmBtn').addEventListener('click', () => {
      const count = Math.max(1, Math.min(50, parseInt(countInput.value, 10) || 1));
      const capacity = parseInt(modal.querySelector('#addTableCapacity').value, 10) || 8;
      const customName = modal.querySelector('#addTableName').value.trim();
      const description = modal.querySelector('#addTableDescription').value.trim();
      const seating = _seatingState();
      _pushUndo();
      for (let i = 0; i < count; i++) {
        seating.tables.push({
          id: 't_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
          name: count === 1 && customName ? customName : `Table ${seating.tables.length + 1}`,
          capacity,
          type: selectedShape,
          // Host-only note, same field the wedding dashboard stores (task #499).
          ...(count === 1 && description ? { description } : {}),
          seats: []
        });
      }
      bootstrap.Modal.getInstance(modal).hide();
      _renderActiveTab();
      _saveSeating();
    });
    modal.addEventListener('hidden.bs.modal', () => modal.remove(), { once: true });
    new bootstrap.Modal(modal).show();
  }

  function toggleShape(tableId) {
    const t = _seatingState().tables.find(x => x.id === tableId);
    if (!t) return;
    _pushUndo();
    t.type = t.type === 'round' ? 'rectangular' : 'round';
    _renderActiveTab();
    _saveSeating();
  }

  function removeTable(tableId) {
    const seating = _seatingState();
    const t = seating.tables.find(x => x.id === tableId);
    const seatCount = t?.seats?.length || 0;
    const msg = seatCount > 0
      ? `Delete "${t.name}"? ${seatCount} guest${seatCount === 1 ? '' : 's'} will be moved to unassigned.`
      : `Delete "${t?.name || 'this table'}"?`;
    if (!confirm(msg)) return;
    _pushUndo();
    seating.tables = seating.tables.filter(t => t.id !== tableId);
    _renderActiveTab();
    _saveSeating();
  }

  function renameTable(tableId, name) {
    const t = _seatingState().tables.find(x => x.id === tableId);
    if (!t || t.name === name) return;
    _pushUndo();
    t.name = name || 'Table';
    _saveSeating();
  }

  function setCapacity(tableId, capacity) {
    const t = _seatingState().tables.find(x => x.id === tableId);
    if (!t) return;
    _pushUndo();
    t.capacity = Math.max(0, parseInt(capacity, 10) || 0);
    _renderActiveTab();
    _saveSeating();
  }

  function autoAssign() {
    const seating = _seatingState();
    const attending = state.responses.filter(r => _seatStatus(r) === 'yes');

    // Say what is missing, and open the thing that fixes it. "Create tables
    // first" on its own left the host to work out both the reason and the next
    // step from a four-word toast (task #497).
    if (!seating.tables.length) {
      _showToast(attending.length
        ? `There is nowhere to seat anyone yet — add some tables and auto-assign will place your ${attending.length} ${attending.length === 1 ? 'party' : 'parties'}.`
        : 'Add some tables first — auto-assign needs somewhere to seat people.', 'warning');
      openAddTableModal();
      return;
    }
    // Tables but nobody to put in them: auto-assign would run and change
    // nothing, which reads exactly like a broken button.
    if (!attending.length) {
      _showToast('Nobody has said they are coming yet, so there is nobody to seat. Auto-assign works once you have guests marked as attending.', 'warning');
      return;
    }

    const guestCount = attending.reduce((sum, r) => sum + _partySize(r), 0);
    const totalSeats = seating.tables.reduce((a, t) => a + (parseInt(t.capacity, 10) || 0), 0);
    const alreadySeated = seating.tables.reduce((a, t) => a + (t.seats?.length || 0), 0);
    const capacityWarn = guestCount > totalSeats
      ? `<div class="autoassign-warn"><i class="bi bi-exclamation-triangle"></i> ${guestCount} guests but only ${totalSeats} seats — some will be left unassigned.</div>`
      : '';
    const rulesCount = seating.rules.keepTogether.length + seating.rules.keepApart.length;
    const rulesNote = rulesCount > 0
      ? `<div class="autoassign-info"><i class="bi bi-shield-check"></i> ${rulesCount} seating rule${rulesCount === 1 ? '' : 's'} will be respected.</div>`
      : '';

    const id = 'seatingAutoAssignModal';
    document.getElementById(id)?.remove();
    const modal = document.createElement('div');
    modal.className = 'modal fade';
    modal.id = id;
    modal.tabIndex = -1;
    // The batch run remembers its own choice. defaultStrategy is the QR Auto
    // DOOR strategy (#846), and running a batch must not quietly change how
    // guests are seated at check-in (the regular dashboard learnt this in #378).
    const current = _lastBulkStrategy || 'roundrobin';
    const strategies = [
      { id: 'roundrobin',   icon: 'bi-arrow-repeat',   label: 'Round-robin',  desc: 'Distribute parties evenly across tables. Best for a balanced spread.' },
      { id: 'sequential',   icon: 'bi-list-ol',        label: 'Sequential',   desc: 'Fill one table before moving to the next. Best for a venue that seats by arrival order.' },
      { id: 'random',       icon: 'bi-shuffle',        label: 'Random',       desc: 'Shuffle parties across tables with random placement. Best when mixing is desirable.' },
      { id: 'relationship', icon: 'bi-people-fill',    label: 'By relationship', desc: 'Group guests by their relationship tag (family, colleagues, etc.) at the same tables.' }
    ];

    modal.innerHTML = `
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header py-2">
            <h6 class="modal-title"><i class="bi bi-magic"></i> Auto-assign seating</h6>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="autoassign-summary">
              <span><strong>${attending.length}</strong> parties · <strong>${guestCount}</strong> guests</span>
              <span><strong>${seating.tables.length}</strong> tables · <strong>${totalSeats}</strong> seats</span>
            </div>
            ${capacityWarn}
            ${rulesNote}

            <div class="autoassign-strategies">
              ${strategies.map(s => `
                <label class="autoassign-strategy ${s.id === current ? 'selected' : ''}">
                  <input type="radio" name="autoassignStrategy" value="${s.id}" ${s.id === current ? 'checked' : ''}>
                  <div class="autoassign-strategy-icon"><i class="bi ${s.icon}"></i></div>
                  <div class="autoassign-strategy-body">
                    <div class="autoassign-strategy-label">${_esc(s.label)}</div>
                    <div class="autoassign-strategy-desc">${_esc(s.desc)}</div>
                  </div>
                </label>`).join('')}
            </div>

            ${alreadySeated > 0 ? `
            <div class="form-check mt-3">
              <input class="form-check-input" type="checkbox" id="autoassignClear" checked>
              <label class="form-check-label small" for="autoassignClear">
                Clear <strong>${alreadySeated}</strong> existing assignment${alreadySeated === 1 ? '' : 's'} before running
              </label>
            </div>` : ''}
          </div>
          <div class="modal-footer py-2">
            <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-org-primary btn-sm" id="autoassignRunBtn">
              <i class="bi bi-magic"></i> Run
            </button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(modal);

    modal.querySelectorAll('input[name="autoassignStrategy"]').forEach(input => {
      input.addEventListener('change', () => {
        modal.querySelectorAll('.autoassign-strategy').forEach(l => l.classList.remove('selected'));
        input.closest('.autoassign-strategy').classList.add('selected');
      });
    });

    modal.querySelector('#autoassignRunBtn').addEventListener('click', async () => {
      const strategy = modal.querySelector('input[name="autoassignStrategy"]:checked')?.value || 'roundrobin';
      const clearFirst = modal.querySelector('#autoassignClear')?.checked ?? true;
      const btn = modal.querySelector('#autoassignRunBtn');
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Assigning…';
      _pushUndo();
      // Remember the choice for the next batch run (not the door strategy).
      _lastBulkStrategy = strategy;
      try {
        // If the user opted to keep current assignments, we need to pre-filter them out
        // from the "to assign" pool. The server auto-assign clears all seats first, so
        // when clearFirst is false we merge post-hoc.
        const configToSend = clearFirst ? seating : JSON.parse(JSON.stringify(seating));
        const existingSeats = clearFirst ? [] : seating.tables.map(t => [...(t.seats || [])]);

        const token = await window.firebaseAuth.currentUser.getIdToken();
        const r = await fetch(`/api/seating/${encodeURIComponent(state.formId)}/auto-assign`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ strategy, currentConfig: configToSend })
        });
        const data = await r.json();
        if (!data.success) {
          _showToast(data.error || 'Auto-assign failed', 'danger');
          btn.disabled = false;
          btn.innerHTML = '<i class="bi bi-magic"></i> Run';
          return;
        }
        state.seating = data.config;

        // Merge previously-assigned members back in when clearFirst is false
        if (!clearFirst) {
          state.seating.tables.forEach((t, i) => {
            const keep = existingSeats[i] || [];
            const haveKeys = new Set(keep.map(_memberKey));
            // Remove duplicates that auto-assign may have seated into this table
            t.seats = (t.seats || []).filter(s => !haveKeys.has(_memberKey(s)));
            // Re-insert existing seats first
            t.seats = [...keep, ...t.seats];
          });
        }

        bootstrap.Modal.getInstance(modal).hide();
        _renderActiveTab();
        _saveSeating();
        const label = strategies.find(s => s.id === strategy)?.label || strategy;
        _showToast(`Auto-assigned using ${label}`);
      } catch (err) {
        _showToast('Auto-assign failed: ' + err.message, 'danger');
        btn.disabled = false;
        btn.innerHTML = '<i class="bi bi-magic"></i> Run';
      }
    });

    modal.addEventListener('hidden.bs.modal', () => modal.remove(), { once: true });
    new bootstrap.Modal(modal).show();
  }

  // Seating rules window (tasks #859/#860): one place for everything that
  // decides where a guest sits. Top to bottom, in the order that matters:
  //   1. how QR Auto picks a table (only while QR Auto is on) — the same
  //      cards as Auto-assign;
  //   2. Group by company, its explanation behind an info icon;
  //   3. guest pairs, together or apart, one list with one add row.
  // The guest pairs list, on its own (task #880): adding or removing a pair
  // redraws just this list. It used to close and rebuild the whole window,
  // which flashed it white and threw the host back to the top every time.
  function _rulePairsHtml() {
    const seating = _seatingState();
    const byId = new Map(state.responses.map(r => [r.id, r.guestName || r.name || r.email || 'Guest']));
    const pairRow = (pair, i, type) => {
      const [a, b] = pair;
      const together = type === 'keepTogether';
      return `<div class="rule-pair">
        <span class="rule-kind ${together ? 'is-together' : 'is-apart'}"><i class="bi ${together ? 'bi-people-fill' : 'bi-arrow-left-right'}"></i> ${together ? 'Together' : 'Apart'}</span>
        <span class="rule-names">${_esc(byId.get(a) || a)} · ${_esc(byId.get(b) || b)}</span>
        <button class="btn-icon-ghost" title="Remove this rule" onclick="OrgEventDetail._removeRule('${type}', ${i})"><i class="bi bi-x"></i></button>
      </div>`;
    };
    return seating.rules.keepTogether.map((p, i) => pairRow(p, i, 'keepTogether'))
      .concat(seating.rules.keepApart.map((p, i) => pairRow(p, i, 'keepApart'))).join('')
      || '<div class="text-body-secondary small">None yet.</div>';
  }
  function _refreshRulePairs() {
    const list = document.getElementById('rulePairList');
    if (!list) { openRulesModal(); return; }
    list.innerHTML = _rulePairsHtml();
  }

  function openRulesModal() {
    const id = 'seatingRulesModal';
    document.getElementById(id)?.remove();
    const attending = state.responses.filter(r => _seatStatus(r) === 'yes');
    const seating = _seatingState();
    const qr = seating.assignmentMode === 'qr_auto';
    const current = seating.defaultStrategy && seating.defaultStrategy !== 'manual' ? seating.defaultStrategy : 'roundrobin';
    const pairs = _rulePairsHtml();

    const guestOptions = attending.map(r => `<option value="${_esc(r.id)}">${_esc(r.guestName || r.name || r.email || 'Guest')}</option>`).join('');
    const companyHelp = 'Matches on the company name, ignoring differences like \u201cInc.\u201d versus \u201cCorp\u201d, and falls back to the email domain when nobody typed a company. Colleagues with the same work email domain are grouped even when their company names differ. Free email addresses such as Gmail are never treated as a company. A \u201ckeep apart\u201d rule still wins.';

    const modal = document.createElement('div');
    modal.className = 'modal fade';
    modal.id = id;
    modal.tabIndex = -1;
    modal.innerHTML = `
      <div class="modal-dialog modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header py-2">
            <h6 class="modal-title"><i class="bi bi-shield-check"></i> Seating rules</h6>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            ${qr ? `
            <div class="rule-section">
              <h6 class="rule-section-head"><i class="bi bi-qr-code-scan"></i> How QR Auto picks a table</h6>
              <div class="text-body-secondary small mb-2">Each guest gets a table the moment they check in.</div>
              <div class="autoassign-strategies">
                ${DOOR_STRATEGIES.map(o => `
                <label class="autoassign-strategy ${o.id === current ? 'selected' : ''}" data-strategy="${o.id}">
                  <input type="radio" name="doorStrategy" value="${o.id}" ${o.id === current ? 'checked' : ''}
                         onchange="OrgEventDetail.setDoorStrategy(this.value)">
                  <div class="autoassign-strategy-icon"><i class="bi ${o.icon}"></i></div>
                  <div class="autoassign-strategy-body">
                    <div class="autoassign-strategy-label">${_esc(o.label)}</div>
                    <div class="autoassign-strategy-desc">${_esc(o.desc)}</div>
                  </div>
                </label>`).join('')}
              </div>
            </div>` : `
            <p class="text-body-secondary small">Auto-assign follows these rules, and so does QR Auto when it is on. Conflicts show a warning on the table card.</p>`}

            <!-- Task #498: one switch instead of a keep-together pair for every
                 employee of every company. -->
            <div class="rule-section${qr ? ' mt-3' : ''}">
              <h6 class="rule-section-head"><i class="bi bi-building"></i> Group by company
                <i class="bi bi-info-circle text-body-secondary ms-1 rule-info" tabindex="0" role="img"
                   aria-label="${_esc(companyHelp)}" title="${_esc(companyHelp)}" data-bs-toggle="tooltip" data-bs-placement="top"></i>
              </h6>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="ruleGroupByCompany"
                       ${seating.rules.groupByCompany ? 'checked' : ''}
                       onchange="OrgEventDetail.setGroupByCompany(this.checked)">
                <label class="form-check-label small" for="ruleGroupByCompany">
                  Seat guests from the same company together
                </label>
              </div>
            </div>

            <div class="rule-section mt-3">
              <h6 class="rule-section-head"><i class="bi bi-person-lines-fill"></i> Guest pairs</h6>
              <div id="rulePairList">${pairs}</div>
              <div class="rule-pair-add rule-pair-add-3">
                <select class="form-select form-select-sm" id="rule-a" aria-label="First guest"><option value="">Select guest…</option>${guestOptions}</select>
                <select class="form-select form-select-sm rule-kind-select" id="rule-type" aria-label="Keep together or apart">
                  <option value="keepTogether">Keep together</option>
                  <option value="keepApart">Keep apart</option>
                </select>
                <select class="form-select form-select-sm" id="rule-b" aria-label="Second guest"><option value="">Select guest…</option>${guestOptions}</select>
                <button class="btn btn-org-primary btn-sm" onclick="OrgEventDetail._addRule()">Add</button>
              </div>
            </div>
          </div>
          <div class="modal-footer py-2">
            <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(modal);
    modal.addEventListener('hidden.bs.modal', () => modal.remove(), { once: true });
    if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
      modal.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => bootstrap.Tooltip.getOrCreateInstance(el));
    }
    if (typeof bootstrap !== 'undefined' && bootstrap.Modal) new bootstrap.Modal(modal).show();
  }

  function setGroupByCompany(on) {
    _pushUndo();
    const seating = _seatingState();
    seating.rules.groupByCompany = !!on;
    _saveSeating();
    _showToast(on ? 'Auto-assign will keep each company together' : 'Company grouping off');
    _renderActiveTab();
  }

  function _addRule(typeArg) {
    const type = typeArg || document.getElementById('rule-type')?.value || 'keepTogether';
    if (type !== 'keepTogether' && type !== 'keepApart') return;
    const a = document.getElementById('rule-a').value;
    const b = document.getElementById('rule-b').value;
    if (!a || !b || a === b) { _showToast('Pick two different guests', 'warning'); return; }
    _pushUndo();
    const seating = _seatingState();
    const pairs = seating.rules[type];
    if (!pairs.some(([x, y]) => (x === a && y === b) || (x === b && y === a))) {
      pairs.push([a, b]);
      _saveSeating();
    }
    // In place: the window stays where the host scrolled it (#880). The two
    // guests clear for the next pair; the together/apart choice stays.
    _refreshRulePairs();
    document.getElementById('rule-a').value = '';
    document.getElementById('rule-b').value = '';
    document.getElementById('rule-a').focus({ preventScroll: true });
    _renderActiveTab();
  }

  function _removeRule(type, idx) {
    _pushUndo();
    const seating = _seatingState();
    seating.rules[type].splice(idx, 1);
    _saveSeating();
    _refreshRulePairs();
    _renderActiveTab();
  }

  function exportSeatingCsv() {
    const seating = _seatingState();
    const rows = ['Table,Type,Capacity,Guest'];
    for (const t of seating.tables) {
      if (!t.seats || t.seats.length === 0) {
        rows.push(_csvLine([t.name, t.type || '', t.capacity || '', '(empty)']));
      } else {
        for (const s of t.seats) rows.push(_csvLine([t.name, t.type || '', t.capacity || '', s.guestName]));
      }
    }
    // Lead with a UTF-8 BOM or Excel renders Hebrew/Arabic guest names as mojibake.
    const blob = new Blob([String.fromCharCode(0xFEFF) + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(state.form?.title || 'event').replace(/[^\w]+/g, '-').toLowerCase()}-seating.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function _csvLine(cells) {
    return cells.map(c => {
      const s = String(c ?? '').replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(',');
  }

  function printSeating() {
    const seating = _seatingState();
    const title = state.form?.title || 'Event';
    const date = state.form?.eventDate || '';
    const blocks = seating.tables.map(t => {
      const seats = t.seats || [];
      const rows = seats.length
        ? seats.map((s, i) => `<li>${i + 1}. ${_esc(s.guestName)}</li>`).join('')
        : '<li class="empty">(no guests assigned)</li>';
      return `
        <section class="print-table">
          <h3>${_esc(t.name)} <small>· ${t.type || 'round'} · ${seats.length}/${t.capacity || '∞'}</small></h3>
          <ol>${rows}</ol>
        </section>`;
    }).join('');

    const html = `<!doctype html>
<html><head><title>${_esc(title)} — Seating</title>
<style>
 body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:2rem;color:#1a1320;}
 h1{margin:0 0 0.25rem;font-size:1.8rem;}
 .subtitle{color:#6b6470;margin-bottom:2rem;}
 .print-grid{column-count:2;column-gap:2rem;}
 .print-table{break-inside:avoid;margin-bottom:1.5rem;page-break-inside:avoid;}
 .print-table h3{margin:0 0 0.25rem;font-size:1.05rem;border-bottom:1px solid #ccc;padding-bottom:0.25rem;}
 .print-table h3 small{font-weight:400;color:#888;}
 ol{margin:0.25rem 0 0 1.25rem;padding:0;font-size:0.9rem;line-height:1.5;}
 li.empty{color:#aaa;font-style:italic;list-style:none;margin-left:-1.25rem;}
 @media print{body{padding:1rem;}}
</style></head>
<body>
  <h1>${_esc(title)}</h1>
  <div class="subtitle">${_esc(date)} \u00B7 Seating chart \u00B7 ${seating.tables.length} tables</div>
  <div class="print-grid">${blocks}</div>
  <script>window.print();</scri` + `pt>
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) { _showToast('Popup blocked — allow popups to print', 'warning'); return; }
    w.document.write(html);
    w.document.close();
  }

  // ============================================================================
  // CHECK-IN — tab view for org staff to track arrivals
  // ============================================================================

  let _checkinSearch = '';
  // Task #894: 'all', or the stat tile pressed - 'in' (checked in), 'out'
  // (registered, not here yet), 'expected' (everyone registered), 'walkin'.
  // Each matches what its tile counts. Kept while the tab is redrawn.
  let _checkinStatusFilter = 'all';
  function _checkinMatchesStatus(r) {
    switch (_checkinStatusFilter) {
      case 'in': return !!r.checkedInAt;
      case 'out': return !r.checkedInAt && r.walkIn !== true;
      case 'expected': return r.walkIn !== true;
      case 'walkin': return r.walkIn === true;
      default: return true;
    }
  }
  // 'name' (A-Z) | 'out' (not yet first) | 'in' (checked in first) |
  // 'recent' (latest arrivals first). Every order ends in A-Z.
  let _checkinSort = 'name';

  // The rows the Check-in tab shows: the search, then the status filter,
  // in the chosen order. Used by the full render and the live patch alike.
  function _checkinVisible(attending) {
    const filter = _checkinSearch.toLowerCase().trim();
    const name = (r) => String(r.guestName || r.name || r.email || '');
    const at = (r) => (r.checkedInAt ? (_toDate(r.checkedInAt)?.getTime() || 0) : 0);
    return attending
      .filter(r => !filter || _checkinSearchMatches(r, filter))
      .filter(_checkinMatchesStatus)
      .sort((a, b) => {
        if (_checkinSort !== 'name' && !!a.checkedInAt !== !!b.checkedInAt) {
          return (_checkinSort === 'out') === !a.checkedInAt ? -1 : 1;
        }
        if (_checkinSort === 'recent' && a.checkedInAt && b.checkedInAt && at(a) !== at(b)) return at(b) - at(a);
        return name(a).localeCompare(name(b), undefined, { sensitivity: 'base' });
      });
  }

  const CHECKIN_SORT_LABELS = { name: 'Name A–Z', out: 'Not yet first', in: 'Checked in first', recent: 'Latest arrivals first' };
  function _checkinSortHtml() {
    const opt = (v) => `<option value="${v}"${_checkinSort === v ? ' selected' : ''}>${CHECKIN_SORT_LABELS[v]}</option>`;
    return `<select class="form-select form-select-sm oed-checkin-status-filter" id="checkinSort" aria-label="Sort guests">
            ${opt('name')}${opt('out')}${opt('in')}${opt('recent')}</select>`;
  }


  function _checkinSearchMatches(r, q) {
    // Company counts as a search term too (task #849): "everyone from Acme".
    const hay = `${r.guestName || ''} ${r.name || ''} ${r.email || ''} ${_phoneOf(r)} ${r.company || r.organization || ''}`.toLowerCase();
    return hay.includes(q);
  }

  // Walk-ins (task #852) are registered at the door, so they were never
  // "expected": Expected, Not yet and Rate describe the guests who
  // registered, and walk-ins get their own count. Checked in is everyone.
  function _checkinStats(responses) {
    const attending = responses.filter(r => _seatStatus(r) === 'yes');
    const registered = attending.filter(r => r.walkIn !== true);
    const registeredIn = registered.filter(r => r.checkedInAt).length;
    const walkIns = attending.filter(r => r.walkIn === true);
    return {
      attending,
      checkedIn: attending.filter(r => r.checkedInAt).length,
      expected: registered.length,
      registeredIn,
      notYet: registered.length - registeredIn,
      walkIns: walkIns.length,
      walkInPeople: walkIns.reduce((n, r) => n + (typeof r.checkedInCount === 'number' ? r.checkedInCount : _partySize(r)), 0),
      pct: registered.length > 0 ? Math.round((registeredIn / registered.length) * 100) : 0
    };
  }

  // Task #894 round 3: the tiles ARE the filter - press one to show only
  // those guests, press it again for everyone. Rate is a ratio, not a group.
  function _checkinStatsHtml(s) {
    const tile = (f, cls, value, label, title = '') => {
      const on = _checkinStatusFilter === f;
      return `<button type="button" class="event-detail-stat ${cls}${on ? ' is-active' : ''}" data-checkin-filter="${f}" aria-pressed="${on}"${title ? ` title="${title}"` : ''}><span class="value">${value}</span><span class="label">${label}</span></button>`;
    };
    return `
      <div class="event-detail-stats">
        ${tile('in', 'stat-yes', s.checkedIn, 'Checked in')}
        ${tile('out', '', s.notYet, 'Not yet')}
        ${tile('expected', 'stat-guests', s.expected, 'Expected')}
        ${tile('walkin', 'stat-walkin', s.walkIns, 'Walk-ins', `${s.walkInPeople} ${s.walkInPeople === 1 ? 'person' : 'people'} registered at the door`)}
        <div class="event-detail-stat"><span class="value">${s.pct}%</span><span class="label">Rate</span></div>
      </div>`;
  }

  function _checkinProgressHtml(s) {
    return s.attending.length > 0 ? `
      <div class="event-detail-capacity">
        <div class="capacity-header"><span>Check-in progress</span><span class="capacity-meta">${s.registeredIn} of ${s.expected} registered${s.walkIns ? ` · +${s.walkIns} walk-in${s.walkIns === 1 ? '' : 's'}` : ''}</span></div>
        <div class="capacity-bar"><div class="capacity-fill" style="width:${s.pct}%;"></div></div>
      </div>` : '';
  }

  /**
   * Live update of the Check-in tab IN PLACE (task #864). A check-in on
   * another device used to rebuild the whole tab — every row, the search box,
   * the tools — so the list flickered and could jump while someone was
   * scrolling or searching it. Now the counters and progress line are
   * refreshed, and only the rows whose content changed are replaced; rows are
   * added or removed individually. Returns false (caller does a full render)
   * when the tab's structure itself would change: no list on screen yet, the
   * empty state, or a different seating note.
   */
  let _checkinNoteShown = null;
  function _patchCheckin(el) {
    const list = el.querySelector('.checkin-list-tab');
    if (!list) return false;
    _seatIdxCache = null; // a live check-in can change who is seated and who is still to come (#887)
    if (_checkinSeatingNoteHtml() !== _checkinNoteShown) return false;
    const s = _checkinStats(state.responses);
    const filtered = _checkinVisible(s.attending);
    if (!filtered.length) return false;

    const stats = el.querySelector('.event-detail-stats');
    const statsHtml = _checkinStatsHtml(s).trim();
    if (stats && stats.outerHTML !== statsHtml) stats.outerHTML = statsHtml;
    const progress = el.querySelector('.event-detail-capacity');
    const progressHtml = _checkinProgressHtml(s).trim();
    if (progress && progress.outerHTML !== progressHtml) progress.outerHTML = progressHtml;

    const existing = new Map();
    [...list.children].forEach(row => existing.set(row.dataset.responseId, row));
    const keep = new Set();
    let cursor = list.firstElementChild;
    const tmp = document.createElement('div');
    filtered.forEach((r) => {
      const html = _renderCheckinRow(r).trim();
      keep.add(r.id);
      let row = existing.get(r.id);
      if (!row || row.dataset.sig !== html) {
        tmp.innerHTML = html;
        const fresh = tmp.firstElementChild;
        fresh.dataset.sig = html;
        if (row) { row.replaceWith(fresh); if (cursor === row) cursor = fresh; }
        row = fresh;
      }
      if (row === cursor) cursor = cursor.nextElementSibling;
      else list.insertBefore(row, cursor);
    });
    existing.forEach((row, id) => { if (!keep.has(id)) row.remove(); });
    return true;
  }

  function _renderCheckin(el) {
    const s = _checkinStats(state.responses);
    const attending = s.attending;
    const filtered = _checkinVisible(attending);
    _ensureDoorKey();
    _checkinNoteShown = _checkinSeatingNoteHtml();

    el.innerHTML = `
      ${_checkinStatsHtml(s)}

      ${_checkinProgressHtml(s)}

      <div class="event-detail-toolbar">
        <div class="event-detail-toolbar-left">
          <input type="search" class="form-control form-control-sm" id="checkinSearchTab" name="checkin-search" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" value="${_esc(_checkinSearch)}"
                 placeholder="Search by name, email, or phone…" style="max-width:320px;">
          ${_checkinSortHtml()}
        </div>
        ${_checkinToolsHtml()}
      </div>
      ${_checkinSeatingNoteHtml()}
      ${state.form && state.form.enableCheckin !== true ? `
      <div class="alert alert-light border small py-2">
        <i class="bi bi-info-circle me-1"></i>QR check-in is off for this event, so guests' confirmations carry no personal QR code.
        The scanner reads the codes you send with an <strong>Event Update</strong> (Outreach tab) with "Include each guest's personal check-in QR code" ticked. The entrance QR and the door staff check-in page find guests by name.
      </div>` : ''}

      ${attending.length === 0
        ? '<div class="event-detail-empty"><i class="bi bi-people"></i><p>No confirmed attendees yet.</p></div>'
        : filtered.length === 0
          ? '<div class="event-detail-empty"><i class="bi bi-search"></i><p>No attendees match.</p></div>'
          : `<div class="checkin-list-tab">${filtered.map(r => _renderCheckinRow(r).trim()).join('')}</div>`}
    `;

    el.querySelectorAll('.checkin-list-tab > .checkin-row').forEach((row, i) => { row.dataset.sig = _renderCheckinRow(filtered[i]).trim(); });
    const search = document.getElementById('checkinSearchTab');
    if (search) {
      // Through _renderActiveTab, which keeps the box focused across the redraw.
      search.addEventListener('input', (e) => { _checkinSearch = e.target.value; _renderActiveTab(); });
    }
    // Delegated from the tab, because a live arrival swaps the tiles in place.
    if (el.dataset.ckTilesWired !== '1') {
      el.dataset.ckTilesWired = '1';
      el.addEventListener('click', (e) => {
        const tile = e.target.closest('[data-checkin-filter]');
        if (!tile || !el.contains(tile)) return;
        const f = tile.dataset.checkinFilter;
        _checkinStatusFilter = _checkinStatusFilter === f ? 'all' : f;
        _renderActiveTab();
      });
    }
    document.getElementById('checkinSort')?.addEventListener('change', (e) => {
      _checkinSort = e.target.value;
      _renderActiveTab();
    });
  }

  // Task #828: the door-staff tools the ordinary event dashboard's Check-in
  // tab has (dashboard-checkin.js) - the camera scanner for guests' personal
  // QR codes, the printable entrance ("master") QR, and the guest check-in
  // link it encodes, which can be sent to whoever runs the door. None of them
  // needs an account: the scanner authenticates each guest's QR by its own
  // signed token, and the entrance page is public by design.
  function _guestCheckinUrl() {
    return `${window.location.origin}/guest-lookup.html?formId=${encodeURIComponent(state.formId)}`;
  }

  // Task #852: the door staff's link is the same page plus a door key, which
  // is what lets it add walk-ins. The printed entrance QR stays keyless, so a
  // guest scanning it can check in but cannot add people. The key comes from
  // the server (host-only) and is cached per event.
  let _doorKey = { formId: null, key: null, pending: null, failed: false };

  function _doorStaffUrl() {
    const key = _doorKey.formId === state.formId ? _doorKey.key : null;
    return key ? `${_guestCheckinUrl()}&door=${encodeURIComponent(key)}` : _guestCheckinUrl();
  }

  function _ensureDoorKey() {
    const formId = state.formId;
    if (!formId) return Promise.resolve(null);
    if (_doorKey.formId === formId && (_doorKey.key || _doorKey.pending || _doorKey.failed)) return _doorKey.pending || Promise.resolve(_doorKey.key);
    _doorKey = { formId, key: null, pending: null, failed: false };
    _doorKey.pending = (async () => {
      try {
        const token = await window.firebaseAuth.currentUser.getIdToken();
        const r = await fetch(`/api/checkin/${encodeURIComponent(formId)}/door-link`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json().catch(() => ({}));
        if (!r.ok || !data.doorKey) throw new Error(data.error || 'door link failed');
        if (_doorKey.formId === formId) _doorKey.key = data.doorKey;
        // Repaint so "Open check-in page" carries the key.
        if (state.formId === formId && state.view === 'checkin') _renderActiveTab();
        return data.doorKey;
      } catch (err) {
        // Not retried on every repaint; the copy button asks again.
        console.warn('Door-staff link unavailable:', err.message);
        if (_doorKey.formId === formId) _doorKey.failed = true;
        return null;
      } finally {
        if (_doorKey.formId === formId) _doorKey.pending = null;
      }
    })();
    return _doorKey.pending;
  }

  // #846: whether checking in also seats people, said where door staff look.
  // The switch itself lives on the Seating tab (one place to change it).
  // Task #857: QR Auto with no tables seats nobody, silently. Say so where the
  // door is being watched, once per event per browser — staff who mean to run
  // without tables can close it.
  const QR_AUTO_NO_TABLES_KEY = 'qrAutoNoTablesDismissed_';
  function _qrAutoNoTablesDismissed() {
    try { return localStorage.getItem(QR_AUTO_NO_TABLES_KEY + state.formId) === '1'; } catch { return false; }
  }
  function dismissQrAutoNoTables() {
    try { localStorage.setItem(QR_AUTO_NO_TABLES_KEY + state.formId, '1'); } catch { /* private mode: closes for this view */ }
    document.querySelectorAll('.oed-qr-notables').forEach(el => el.remove());
  }

  // Task #857 r2: the banner is easy to scroll past. Opening the scanner or
  // the check-in page with QR Auto on and no tables now asks first: add
  // tables, open anyway, or cancel. "Don't warn me again" uses the banner's
  // own dismissal, so a host who means to run without tables is asked once.
  function guardDoorOpen(e, link) {
    const s = state.seating;
    const noTables = s && s.assignmentMode === 'qr_auto' && !(Array.isArray(s.tables) && s.tables.length);
    if (!noTables || _qrAutoNoTablesDismissed()) return true;
    e.preventDefault();
    const href = link.href;
    document.getElementById('oedQrAutoAsk')?.remove();
    document.body.insertAdjacentHTML('beforeend', `
      <div class="modal fade" id="oedQrAutoAsk" tabindex="-1" aria-labelledby="oedQrAutoAskTitle" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered"><div class="modal-content">
          <div class="modal-header border-0 pb-0">
            <h6 class="modal-title" id="oedQrAutoAskTitle"><i class="bi bi-exclamation-triangle-fill text-warning me-2"></i>QR Auto seating is on, but this event has no tables</h6>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body pt-2">
            <p class="mb-2">Guests who check in will not be given a table. Add tables on the Seating tab first, or open it anyway.</p>
            <label class="form-check small text-muted mb-0"><input type="checkbox" class="form-check-input" id="oedQrAutoAskMute"> Don't warn me again for this event</label>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-org-outline btn-sm" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-org-outline btn-sm" data-qa-act="open">Open anyway</button>
            <button type="button" class="btn btn-org-primary btn-sm" data-qa-act="tables"><i class="bi bi-grid-3x3"></i> Add tables</button>
          </div>
        </div></div>
      </div>`);
    const el = document.getElementById('oedQrAutoAsk');
    const modal = new bootstrap.Modal(el);
    el.addEventListener('click', (ev) => {
      const act = ev.target.closest('[data-qa-act]')?.dataset.qaAct;
      if (!act) return;
      if (el.querySelector('#oedQrAutoAskMute').checked) dismissQrAutoNoTables();
      // Opened inside the click itself, or the browser blocks the new tab.
      if (act === 'open') window.open(href, '_blank', 'noopener');
      modal.hide();
      if (act === 'tables') switchView('seating');
    });
    el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });
    modal.show();
    return false;
  }

  function _checkinSeatingNoteHtml() {
    const s = state.seating;
    if (s && s.assignmentMode === 'qr_auto' && !(Array.isArray(s.tables) && s.tables.length)) {
      if (_qrAutoNoTablesDismissed()) return '';
      return `
      <div class="alert alert-warning oed-qr-notables d-flex align-items-start gap-2 py-2" role="alert">
        <i class="bi bi-exclamation-triangle-fill mt-1" aria-hidden="true"></i>
        <div class="flex-grow-1">
          <strong>QR Auto seating is on, but this event has no tables.</strong>
          Guests who check in will not be given a table.
          <button type="button" class="btn btn-link btn-sm p-0 align-baseline" onclick="OrgEventDetail.switchView('seating')">Add tables</button>
        </div>
        <button type="button" class="btn-close" aria-label="Dismiss" title="Dismiss — check in without tables" onclick="OrgEventDetail.dismissQrAutoNoTables()"></button>
      </div>`;
    }
    if (!s || !Array.isArray(s.tables) || s.tables.length === 0) return '';
    const on = s.assignmentMode === 'qr_auto';
    const label = (DOOR_STRATEGIES.find(o => o.id === s.defaultStrategy) || DOOR_STRATEGIES[0]).label.toLowerCase();
    // Task #887: tables that cannot hold everyone still expected at them,
    // spelled out here as guests arrive (the row's table chip turns amber).
    const tables = _seatingState().tables;
    const overflow = [..._tableOverflow().entries()].map(([id, o]) => {
      const t = tables.find(x => x.id === id);
      const name = String((t && t.name) || '').trim() || 'Table';
      return `<div title="Still to come: ${_esc(o.who.join(', '))}"><strong>${_esc(name)} may overflow.</strong> ${_esc(_overflowSentence(o))}</div>`;
    }).join('');
    return `${overflow ? `
      <div class="oed-checkin-overflow" role="status"><i class="bi bi-exclamation-circle" aria-hidden="true"></i><div>${overflow}</div></div>` : ''}
      <div class="oed-checkin-seat-note${on ? ' is-on' : ''}">
        <i class="bi ${on ? 'bi-qr-code-scan' : 'bi-grid-3x3'}" aria-hidden="true"></i>
        <span>${on
          ? `QR Auto seating is on: each guest gets a table as they check in (${_esc(label)}).`
          : 'Checking in does not seat anyone: tables are assigned by hand.'}</span>
        <button type="button" class="btn btn-link btn-sm p-0" onclick="OrgEventDetail.switchView('seating')">${on ? 'Change' : 'Turn on QR Auto'}</button>
      </div>`;
  }

  // None of the door tools depends on QR check-in. The entrance sign and the
  // door staff page find guests by name, and the server serves them for every
  // corporate event (checkin.service isMasterCheckinEligible: any paid tier),
  // so greying them out locked working tools (task #1121). The scanner reads
  // guests' personal QR codes, which an Event Update can attach even with QR
  // check-in off (task #1123).
  // A workspace that never subscribed gets none of them: every door page lists
  // guest names, which #1018 keeps out of the browser until it subscribes.
  function _checkinToolsHtml() {
    if (!state.formId) return '';
    if (_paywalled()) {
      const lock = (icon, label) => `<button type="button" class="btn btn-org-outline btn-sm"${_gatedAttrs('Subscribe to run check-in')}><i class="bi ${icon}"></i> ${label}</button>`;
      return `
        <div class="event-detail-toolbar-right">
          ${lock('bi-qr-code-scan', 'Open scanner')}${lock('bi-qr-code', 'Master QR code')}${lock('bi-box-arrow-up-right', 'Open check-in page')}${lock('bi-link-45deg', 'Copy check-in link')}
        </div>`;
    }
    // Task #1123: the scanner is open whether or not QR check-in is on. With
    // it off, guests' confirmations carry no code, but an Event Update can
    // still attach each guest's personal QR, and the scan checks only the
    // code's own signature - so greying the button out stranded those guests.
    const scanner = `<a class="btn btn-org-outline btn-sm" href="/checkin?formId=${encodeURIComponent(state.formId)}"
             target="_blank" rel="noopener" onclick="return OrgEventDetail.guardDoorOpen(event, this)"><i class="bi bi-qr-code-scan"></i> Open scanner</a>`;
    return `
        <div class="event-detail-toolbar-right">
          ${scanner}
          <button type="button" class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.showMasterQr()">
            <i class="bi bi-qr-code"></i> Master QR code</button>
          <a class="btn btn-org-primary btn-sm" href="${_esc(_doorStaffUrl())}" target="_blank" rel="noopener" onclick="return OrgEventDetail.guardDoorOpen(event, this)"
             title="The door staff page: check guests in and add walk-ins"><i class="bi bi-box-arrow-up-right"></i> Open check-in page</a>
          <button type="button" class="btn btn-org-outline btn-sm" onclick="OrgEventDetail.copyGuestCheckinLink()"
             title="A link for door staff. It can also add walk-ins, so share it with staff only"><i class="bi bi-link-45deg"></i> Copy check-in link</button>
        </div>`;
  }

  // `kind` 'sign' = the entrance QR's own (keyless) link, from the QR dialog;
  // anything else = the door staff link, which can add walk-ins (#852).
  async function copyGuestCheckinLink(kind) {
    const sign = kind === 'sign';
    if (!sign) await _ensureDoorKey();
    const url = sign ? _guestCheckinUrl() : _doorStaffUrl();
    const done = sign || url === _guestCheckinUrl()
      ? 'Guest check-in link copied'
      : 'Door staff link copied. It can also add walk-ins, so share it with staff only';
    try {
      await navigator.clipboard.writeText(url);
      _showToast(done, 'success');
    } catch (_) {
      window.prompt('Copy the check-in link:', url);
    }
  }

  // A QR image (data URL) for `url`, loading the generator the first time.
  // Null, with the host told why, when it cannot be made.
  async function _qrDataUrl(url, cell = 8, margin = 16) {
    if (typeof window.qrcode === 'undefined') {
      try {
        await new Promise((resolve, reject) => {
          const sc = document.createElement('script');
          sc.src = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js';
          sc.onload = resolve;
          sc.onerror = reject;
          document.head.appendChild(sc);
        });
      } catch (_) {
        _showToast('Could not load the QR code. Please try again.', 'danger');
        return null;
      }
    }
    try {
      const qr = window.qrcode(0, 'M');
      qr.addData(url);
      qr.make();
      return qr.createDataURL(cell, margin);
    } catch (err) {
      console.error('QR failed:', err);
      _showToast('Could not make the QR code.', 'danger');
      return null;
    }
  }

  // The event's sign-up QR, for posters, slides and badges (task #1017 round
  // 2, Shahar): the same address as the general link, downloaded as an image.
  async function downloadEventQr() {
    const img = await _qrDataUrl(_generalLink(), 12, 24);
    if (!img) return;
    const a = document.createElement('a');
    a.href = img;
    a.download = `event-qr-${(state.form && (state.form.slug || state.form.eventSlug)) || state.formId}.gif`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function showMasterQr() {
    if (!state.formId) return;
    const url = _guestCheckinUrl();
    const img = await _qrDataUrl(url);
    if (!img) return;
    document.getElementById('orgMasterQrModal')?.remove();
    const wrap = document.createElement('div');
    wrap.innerHTML = `
      <div class="modal fade" id="orgMasterQrModal" tabindex="-1" aria-labelledby="orgMasterQrTitle" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content border-0 shadow">
            <div class="modal-header border-0 pb-0">
              <h5 class="modal-title fw-bold" id="orgMasterQrTitle"><i class="bi bi-qr-code me-2"></i>Master check-in QR</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body text-center pt-3 pb-4">
              <p class="text-muted small mb-3">Print this and put it at the entrance. Guests scan it, find their name and check themselves in. Door staff should use <strong>Copy check-in link</strong> on the Check-in tab instead: their link can also add walk-ins, this one cannot.</p>
              <img src="${img}" alt="Master check-in QR code" style="width:240px;max-width:100%;border:1px solid #eee;border-radius:10px;padding:8px;background:#fff;">
              <div class="input-group input-group-sm mt-3">
                <input type="text" class="form-control" readonly value="${_esc(url)}" aria-label="Guest check-in link">
                <button type="button" class="btn btn-outline-secondary" onclick="OrgEventDetail.copyGuestCheckinLink('sign')"><i class="bi bi-clipboard"></i> Copy</button>
              </div>
              <div class="d-flex justify-content-center gap-2 mt-3">
                <a class="btn btn-outline-secondary btn-sm" href="${_esc(url)}" target="_blank" rel="noopener"><i class="bi bi-box-arrow-up-right"></i> Open guest check-in link</a>
                <a class="btn btn-primary btn-sm" href="${img}" download="checkin-qr-${_esc(state.formId)}.gif"><i class="bi bi-download me-1"></i>Download image</a>
              </div>
            </div>
          </div>
        </div>
      </div>`;
    const modal = wrap.firstElementChild;
    document.body.appendChild(modal);
    modal.addEventListener('hidden.bs.modal', () => modal.remove());
    bootstrap.Modal.getOrCreateInstance(modal).show();
  }

  // ---- Change a guest's table from the Check-in tab (task #871) ----------
  // A guest at the door asks to sit elsewhere: the row's table chip opens the
  // tables with how full each is, and a pick moves the WHOLE party there, in
  // the same chart the Seating tab edits and through the same save (versioned,
  // #858, so a seat given at the door a moment ago is never written over).
  // Moved by the host, the seats are the host's (no source:'checkin'), so
  // undoing the check-in later keeps them.
  let _ctm = null; // { el, responseId }

  function _checkinTableChipHtml(r) {
    if (!_seatingHasTables()) return '';
    const at = _seatIdx().byResponse.get(r.id) || [];
    const label = at.length ? at.join(', ') : 'No table';
    // Task #887: their table may not hold everyone still expected there -
    // flag it on the chip that opens "Move to", so the fix is one tap away.
    const overflow = _tableOverflow();
    let warn = null;
    _seatingState().tables.forEach((t) => {
      if (!warn && overflow.has(t.id) && (t.seats || []).some(s => s && s.responseId === r.id)) warn = overflow.get(t.id);
    });
    const title = warn ? `${_overflowSentence(warn)} Still to come: ${warn.who.join(', ')}. Click to move this party.` : (at.length ? 'Change table' : 'Give a table');
    return `<button type="button" class="checkin-table${at.length ? '' : ' is-none'}${warn ? ' may-overflow' : ''}" data-checkin-table="${_esc(r.id)}"
        aria-haspopup="true" title="${_esc(title)}">
        <i class="bi ${warn ? 'bi-exclamation-circle' : 'bi-grid-3x3-gap'}"></i><span>${_esc(label)}</span><i class="bi bi-chevron-down"></i></button>`;
  }

  function _checkinChip(responseId) {
    return [...document.querySelectorAll('#eventDetailBody [data-checkin-table]')].find(b => b.dataset.checkinTable === responseId) || null;
  }

  function _closeCheckinTableMenu() {
    if (!_ctm) return;
    _ctm.el.remove();
    _ctm = null;
    if (_livePendingRender) _liveRender();
  }

  // The party as it sits now (seats keep their names), or - not seated yet -
  // built from the response the way the Seating tab builds it.
  function _partySeatsFor(responseId) {
    const seats = [];
    _seatingState().tables.forEach(t => (t.seats || []).forEach(s => { if (s && s.responseId === responseId) seats.push(s); }));
    if (seats.length) return seats.map(s => ({ responseId, guestName: s.guestName, isGroupMember: !!s.isGroupMember }));
    const r = state.responses.find(x => x.id === responseId);
    return r ? _buildMembersForResponse(r).map(m => ({ responseId, guestName: m.guestName, isGroupMember: m.isGroupMember })) : [];
  }

  function _openCheckinTableMenu(btn) {
    const responseId = btn.dataset.checkinTable;
    if (_ctm && _ctm.responseId === responseId) { _closeCheckinTableMenu(); return; }
    _closeCheckinTableMenu();
    const r = state.responses.find(x => x.id === responseId);
    if (!r) return;
    const party = _partySeatsFor(responseId);
    const need = Math.max(1, party.length);
    // Part of the party is here (#887): say which tables also have room for
    // the rest, so the host can make space for everyone in one move.
    const whole = r.checkedInAt ? Math.max(need, _partySize(r)) : need;
    const tables = _seatingState().tables;
    const items = tables.map((t) => {
      const seats = t.seats || [];
      const mine = seats.filter(s => s && s.responseId === responseId).length;
      const cap = parseInt(t.capacity, 10) || 0;
      const others = seats.length - mine;
      const fits = !cap || others + need <= cap;
      const fitsAll = !cap || others + whole <= cap;
      const here = mine > 0;
      const hint = !fits ? ' · full' : (!fitsAll ? ` · no room for all ${whole}` : '');
      return `<button type="button" class="ctm-item${here ? ' is-here' : ''}${fits ? '' : ' is-full'}" data-ctm-table="${_esc(t.id)}" role="menuitem">
          <span class="ctm-name">${here ? '<i class="bi bi-check2"></i>' : ''}${_esc(String(t.name || '').trim() || 'Table')}</span>
          <span class="ctm-count">${seats.length}/${cap || '∞'}${hint}</span></button>`;
    }).join('');
    const seated = tables.some(t => (t.seats || []).some(s => s && s.responseId === responseId));
    const el = document.createElement('div');
    el.className = 'checkin-table-menu';
    el.setAttribute('role', 'menu');
    el.innerHTML = `<div class="ctm-head">Move ${_esc(r.guestName || r.name || r.email || 'guest')}${whole > need ? ` (${need} here of ${whole})` : (need > 1 ? ` (party of ${need})` : '')} to</div>
      <div class="ctm-list">${items}</div>
      ${seated ? '<button type="button" class="ctm-item ctm-remove" data-ctm-table="" role="menuitem"><span class="ctm-name"><i class="bi bi-x-lg"></i>Remove from table</span></button>' : ''}`;
    document.body.appendChild(el);
    _ctm = { el, responseId };
    _placeCheckinTableMenu();
    el.querySelector('.ctm-item:not(.is-here)')?.focus();
  }

  function _placeCheckinTableMenu() {
    if (!_ctm) return;
    // Rows are redrawn by the live sync; find this guest's chip again.
    const btn = _checkinChip(_ctm.responseId);
    if (!btn) { _closeCheckinTableMenu(); return; }
    const b = btn.getBoundingClientRect();
    const m = _ctm.el;
    const h = m.offsetHeight, w = m.offsetWidth;
    const below = b.bottom + 6 + h <= window.innerHeight || b.top < h + 12;
    m.style.top = (below ? b.bottom + 6 : b.top - 6 - h) + 'px';
    m.style.left = Math.min(Math.max(8, b.right - w), window.innerWidth - w - 8) + 'px';
  }

  function _moveCheckinParty(responseId, toTableId) {
    const seating = _seatingState();
    const r = state.responses.find(x => x.id === responseId);
    const name = (r && (r.guestName || r.name || r.email)) || 'Guest';
    const party = _partySeatsFor(responseId);
    const to = toTableId ? seating.tables.find(t => t.id === toTableId) : null;
    if (toTableId && !to) return;
    if (to) {
      const cap = parseInt(to.capacity, 10) || 0;
      const others = (to.seats || []).filter(s => s && s.responseId !== responseId).length;
      const free = Math.max(0, cap - others);
      const tableName = String(to.name || '').trim() || 'Table';
      // Over capacity only on purpose (as at the door, #851).
      if (cap && others + party.length > cap
          && !confirm(`${tableName} has ${free} free seat${free === 1 ? '' : 's'} and ${name}'s party needs ${party.length}. Seat them there anyway? It will read ${others + party.length}/${cap}.`)) return;
    }
    _pushUndo();
    seating.tables.forEach(t => { t.seats = (t.seats || []).filter(s => !(s && s.responseId === responseId)); });
    if (to) {
      if (!to.seats) to.seats = [];
      party.forEach(m => to.seats.push(m));
    }
    _seatIdxCache = null;
    _closeCheckinTableMenu();
    const body = document.getElementById('eventDetailBody');
    if (!(state.view === 'checkin' && body && _patchCheckin(body))) _renderActiveTab();
    // The row shows the new table at once; no toast repeating it (#888).
    _saveSeating();
  }

  document.addEventListener('click', (e) => {
    const chip = e.target.closest && e.target.closest('[data-checkin-table]');
    if (chip && document.getElementById('eventDetailBody')?.contains(chip)) { e.preventDefault(); _openCheckinTableMenu(chip); return; }
    const item = _ctm && e.target.closest && e.target.closest('.checkin-table-menu [data-ctm-table]');
    if (item) { _moveCheckinParty(_ctm.responseId, item.dataset.ctmTable); return; }
    if (_ctm && !_ctm.el.contains(e.target)) _closeCheckinTableMenu();
  });
  document.addEventListener('keydown', (e) => {
    if (!_ctm) return;
    if (e.key === 'Escape') {
      const id = _ctm.responseId;
      _closeCheckinTableMenu();
      _checkinChip(id)?.focus();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const items = [..._ctm.el.querySelectorAll('.ctm-item')];
      const i = items.indexOf(document.activeElement);
      const next = items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
      if (next) { e.preventDefault(); next.focus(); }
    }
  });
  window.addEventListener('scroll', _placeCheckinTableMenu, true);
  window.addEventListener('resize', _placeCheckinTableMenu);

  // Task #829: the row shows whether a guest has arrived; it no longer has a
  // "Mark in" switch. Door staff check people in on the check-in page ("Open
  // check-in page" above), which shows each guest's table - a switch here
  // did not, and was the wrong place to do it.
  function _renderCheckinRow(r) {
    const isIn = !!r.checkedInAt;
    const name = r.guestName || r.name || '';
    const initials = (name || r.email || '?').trim().slice(0, 1).toUpperCase();
    const additionals = Math.max(0, _partySize(r) - 1);
    const party = additionals > 0 ? ` \u00B7 +${additionals}` : '';
    const checkedByLabel = isIn && r.checkedInBy ? ` \u00B7 by ${_esc(r.checkedInBy)}` : '';
    const checkedAtLabel = isIn && r.checkedInAt ? _fmtDate(r.checkedInAt) : '';
    const primary = name || r.email || '(unnamed)';
    const secondary = name ? (r.email || _phoneOf(r)) : _phoneOf(r);
    // Company / organisation under the name (task #849), so door staff can
    // see at a glance who belongs with whom.
    const company = String(r.company || r.companyName || r.organization || r.organisation || r.employer || '').trim();
    return `
      <div class="checkin-row ${isIn ? 'is-in' : ''}" data-response-id="${_esc(r.id)}">
        <div class="checkin-avatar">${_esc(initials)}</div>
        <div class="checkin-info">
          <div class="checkin-name">${_esc(primary)}${r.walkIn === true ? ' <span class="checkin-walkin">Walk-in</span>' : ''}</div>
          ${company ? `<div class="checkin-company"><i class="bi bi-building"></i> ${_esc(company)}</div>` : ''}
          <div class="checkin-meta">${_esc(secondary)}${party}</div>
          ${isIn && checkedAtLabel ? `<div class="checkin-when">Checked in ${checkedAtLabel}${checkedByLabel}</div>` : ''}
        </div>
        ${_checkinTableChipHtml(r)}
        <span class="checkin-status ${isIn ? 'in' : 'out'}">
          ${isIn ? '<i class="bi bi-check-circle-fill"></i><span>Checked in</span>' : '<i class="bi bi-clock"></i><span>Not yet</span>'}
        </span>
      </div>`;
  }

  // ============================================================================
  // ACTIONS
  // ============================================================================

  async function _postAction(path, body) {
    try {
      const r = await window.OrgContext.orgFetch(path, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined
      });
      return r;
    } catch (err) {
      _showToast('Action failed: ' + err.message, 'danger');
      throw err;
    }
  }

  async function approve(responseId) {
    await _postAction(`/forms/${state.formId}/responses/${responseId}/approve`);
    _showToast('Approved');
    _load();
  }

  async function deny(responseId) {
    await _postAction(`/forms/${state.formId}/responses/${responseId}/deny`);
    _showToast('Denied');
    _load();
  }

  // The org bulk endpoints take { items: [{ formId, responseId }] } and answer
  // { updated }. This used to send { formId, responseIds }, which the server
  // read as "no items": nothing changed, and the toast said they had (#839).
  async function _bulkDecide(action, ids) {
    const items = ids.map(responseId => ({ formId: state.formId, responseId }));
    const r = await _postAction(`/responses/bulk-${action}`, { items });
    const n = r && typeof r.updated === 'number' ? r.updated : 0;
    _showToast(`${n} ${action === 'approve' ? 'approved' : 'denied'}`);
    state.selected.clear();
    _load();
  }

  async function bulkApprove() {
    const ids = [...state.selected];
    if (!ids.length) return;
    await _bulkDecide('approve', ids);
  }

  async function bulkDeny() {
    const ids = [...state.selected];
    if (!ids.length) return;
    if (!confirm(`Deny ${ids.length} response${ids.length === 1 ? '' : 's'}?`)) return;
    await _bulkDecide('deny', ids);
  }

  function clearSelection() {
    state.selected.clear();
    _renderActiveTab();
  }

  // A guest deleted elsewhere leaves the list on the next refresh; drop them
  // from the selection too, or the bar counts one more than the ticked rows
  // and the header box disagrees with it (task #1125).
  function _pruneSelection() {
    if (!state.selected.size) return;
    const ids = new Set(state.responses.map(r => r.id));
    [...state.selected].forEach(id => { if (!ids.has(id)) state.selected.delete(id); });
  }

  // 'all' | 'some' | 'none' of the rows on screen are selected (task #1125).
  function _selectAllState(rows) {
    const n = rows.reduce((k, r) => k + (state.selected.has(r.id) ? 1 : 0), 0);
    if (!n) return 'none';
    return n === rows.length ? 'all' : 'some';
  }

  function _selectedRows() {
    return state.responses.filter((r) => state.selected.has(r.id));
  }

  // Task #850: delete guest rows — one from the row, or the whole selection.
  async function _deleteRows(ids) {
    const items = ids.map((responseId) => ({ formId: state.formId, responseId }));
    const r = await _postAction('/responses/bulk-delete', { items });
    const n = r && typeof r.deleted === 'number' ? r.deleted : 0;
    _showToast(n === 1 ? 'Guest deleted' : n + ' guests deleted');
    ids.forEach((id) => state.selected.delete(id));
    _load();
  }

  async function deleteResponse(responseId) {
    const r = state.responses.find((x) => x.id === responseId);
    const name = (r && (r.guestName || r.name || r.email)) || 'this guest';
    if (!confirm('Delete ' + name + ' from this event? This cannot be undone.')) return;
    await _deleteRows([responseId]);
  }

  async function bulkDelete() {
    const ids = [...state.selected];
    if (!ids.length) return;
    if (!confirm('Delete ' + ids.length + ' guest' + (ids.length === 1 ? '' : 's') + ' from this event? This cannot be undone.')) return;
    await _deleteRows(ids);
  }

  // Task #850: change the status of the selected settled guests (pending and
  // waitlisted rows are decisions, not statuses — Approve / Deny handle those).
  async function bulkSetStatus(value) {
    const attending = value === 'yes' ? 'yes' : 'no';
    const rows = _selectedRows().filter((r) => !['pending', 'waitlist'].includes(_normalizeStatus(r)));
    if (!rows.length) return;
    let ok = 0;
    try {
      const token = await window.firebaseAuth.currentUser.getIdToken();
      for (const r of rows) {
        try {
          const res = await fetch(
            '/api/forms/' + encodeURIComponent(state.formId) + '/responses/' + encodeURIComponent(r.id),
            { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }, body: JSON.stringify({ attending }) }
          );
          if (res.ok) {
            r.attending = attending;
            if (r.importedEntry === true) r.attendingStated = true; // an answer (#1090)
            ok++;
          }
        } catch (_) { /* counted below */ }
      }
    } catch (err) {
      _showToast('Could not change status: ' + err.message, 'danger');
      return;
    }
    _showToast(ok + ' marked ' + (attending === 'yes' ? 'attending' : 'declined') + (ok < rows.length ? ' (' + (rows.length - ok) + ' failed)' : ''), ok < rows.length ? 'danger' : undefined);
    state.selected.clear();
    _load();
  }

  /**
   * "Manual" for a row a host typed in (here or via the spreadsheet importer),
   * "RSVP" for one a guest submitted. Reads the flags the import service
   * already stamps — `addedByHost` / `manualEntry` — rather than inventing a
   * parallel marker that older rows would not carry.
   */
  function _responseSource(r) {
    return (r && (r.addedByHost === true || r.manualEntry === true || r.importedEntry === true))
      ? 'Manual' : 'RSVP';
  }

  /**
   * The columns an export carries: the ones the table is actually showing, in
   * the order it shows them (task #615).
   *
   * The export used to carry eight hard-coded columns and no custom questions
   * at all — so an event asking "which session?" or "shirt size" exported a
   * file with the answers missing, which is most of why anyone exports. It now
   * reads the SAME column list the table builds, so a question added to the
   * form appears in the file without anyone remembering to add it here, and a
   * column hidden from the table stays out of the file.
   *
   * Dropped from that list: the checkbox and the row-actions column, which
   * aren't data. Added at the end: "Source" (task #495 — which rows a host
   * typed in themselves), which the table doesn't show but a file being
   * reconciled needs.
   */
  function _exportColumns() {
    const cols = _buildSpreadsheetColumns()
      .filter(c => c.key !== '_select' && c.key !== '_actions');
    // A flat file needs to say which row is the registrant and which are the
    // people they are bringing — the table says it with an indent and a badge,
    // which a spreadsheet has no way to carry.
    const nameIdx = cols.findIndex(c => c.key === 'guestName');
    const partyCol = { key: '_partyMember', label: 'Attendee', type: 'computed' };
    // The form asks first and last name apart (task #891): the file keeps
    // them apart too, for badges and CRM imports, beside the full name.
    const extra = state.form && state.form.splitGuestName === true
      ? [{ key: 'firstName', label: 'First name', type: 'text' }, { key: 'lastName', label: 'Last name', type: 'text' }, partyCol]
      : [partyCol];
    if (nameIdx >= 0) cols.splice(nameIdx + 1, 0, ...extra); else cols.push(...extra);
    cols.push({ key: '_source', label: 'Source', type: 'computed' });
    return cols;
  }

  /**
   * One response becomes one row per ATTENDEE: the registrant, then each named
   * +1 (task #615). The table already shows them on their own indented rows;
   * the file used to squash them into a party-size number, so a list of 40
   * people exported as 18 rows and could not be used for badges, seating or
   * catering without re-typing.
   *
   * A +1 carries their own name and contact details, and INHERITS the things
   * that belong to the party rather than to a person: the RSVP status, when it
   * was submitted, and whether a host typed it in. Without that, filtering the
   * file by "yes" would silently drop every +1. Everything else — party size,
   * the answers to the form's questions — stays on the registrant's row, where
   * the table also keeps it.
   */
  function _exportRows(responses, cols) {
    const out = [];
    responses.forEach((r) => {
      const status = _normalizeStatus(r);
      const source = _responseSource(r);
      out.push(cols.map((col) => {
        if (col.key === '_partyMember') return 'Main';
        if (col.key === '_source') return source;
        if (col.key === '_rownum') return out.length + 1;
        if (col.key === '_total') return status === 'yes' ? _partySize(r) : '';
        return _readCell(r, col);
      }));

      const guests = Array.isArray(r.additionalGuestDetails) ? r.additionalGuestDetails : [];
      const perAttendee = _perAttendeeKeys();
      guests.forEach((g, i) => {
        const pos = g.position || (i + 2);
        out.push(cols.map((col) => {
          if (col.key === '_partyMember') return `Guest ${pos}`;
          if (col.key === '_source') return source;
          if (col.key === '_rownum') return out.length + 1;
          if (col.key === '_total') return '';
          if (col.key === 'status') return status;
          if (col.key === 'submittedAt') return _fmtDate(r.submittedAt || r.createdAt);
          return _readGuestExportCell(g, col, r, pos, perAttendee);
        }));
      });
    });
    return out;
  }

  // First / last name of a response (task #891): as the guest typed them, or,
  // for a guest added another way (an import, Add guest) on such a form, the
  // name split at its last word.
  function _namePart(r, key) {
    if (r.firstName || r.lastName) return String(r[key] || '');
    const words = String(r.guestName || r.name || '').trim().split(/\s+/).filter(Boolean);
    if (key === 'lastName') return words.length > 1 ? words[words.length - 1] : '';
    return words.length > 1 ? words.slice(0, -1).join(' ') : (words[0] || '');
  }

  function _exportCsv(rows, filename) {
    const cols = _exportColumns();
    const lines = [cols.map(c => c.label || c.key).join(',')];
    _exportRows(rows, cols).forEach((cells) => {
      lines.push(cells.map(c => {
        const s = String(c ?? '').replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      }).join(','));
    });
    // Lead with a UTF-8 BOM or Excel renders Hebrew/Arabic guest names as mojibake.
    const blob = new Blob([String.fromCharCode(0xFEFF) + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  // Task #1083: name tags for this event's guests, as a PDF on a label sheet.
  function openNameTags() {
    if (_paywalled() || !window.OrgNameTags) return;
    window.OrgNameTags.open({
      formId: state.formId,
      form: state.form,
      responses: state.responses,
      statusOf: _normalizeStatus,
      getToken: () => window.firebaseAuth.currentUser.getIdToken(),
      toast: _showToast
    });
  }

  function exportAll() {
    _seatIdxCache = null;
    const rows = _filteredResponses();
    const name = (state.form?.title || 'event').replace(/[^\w]+/g, '-').toLowerCase();
    _exportCsv(rows, `${name}-responses.csv`);
  }

  function exportSelected() {
    _seatIdxCache = null;
    const rows = state.responses.filter(r => state.selected.has(r.id));
    const name = (state.form?.title || 'event').replace(/[^\w]+/g, '-').toLowerCase();
    _exportCsv(rows, `${name}-selected.csv`);
  }

  // ============================================================================
  // EDIT A GUEST (task #1103)
  // ============================================================================
  //
  // The pencil at the end of each row. It used to open a dump of the raw
  // response document (submissionDate, ipAddress, guestNameKey...) with no way
  // to change anything. Now it is the guest's answers as labelled fields: the
  // same columns the table shows, in its order, with the same controls (a
  // choice question is its options, not a text box), plus the party's other
  // guests. Every field saves through _writeCellPayload, the table's own
  // cell-to-PUT mapping, so a field saved here is stored exactly as the same
  // edit made in the cell would be. The regular dashboard's editor (admin.js)
  // writes Firestore from the page and lives inside that page's globals, so it
  // cannot be reused here; this one goes through PUT /responses/:id like every
  // other write on this dashboard.

  const EG_GUEST_KEYS = ['name', 'email', 'phone', 'jobTitle', 'company', 'department'];
  const EG_GUEST_LABELS = { name: 'Name', email: 'Email', phone: 'Phone', jobTitle: 'Job title', company: 'Company', department: 'Department' };

  function _teardownEditGuest() {
    const el = document.getElementById('editGuestModal');
    if (el) {
      try { bootstrap.Modal.getInstance(el)?.dispose(); } catch (e) { /* already disposed */ }
      el.remove();
    }
    document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove());
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('overflow');
  }

  /** The labelled control for one column of the guest's row. */
  function _egFieldHtml(col, r) {
    const id = 'eg_' + col.key.replace(/[^a-z0-9-]/gi, '_');
    const label = _esc(col.label || col.originalLabel || col.key);
    const cur = String(_readCell(r, col) ?? '');
    const wide = col.customFieldId || ['notes', 'dietary'].includes(col.key);
    const wrapCls = wide ? 'col-12' : 'col-12 col-sm-6';
    if (col.customFieldType === 'image') {
      const safe = cur ? _esc(cur) : '';
      return `<div class="${wrapCls}"><div class="form-label small mb-1">${label}</div>${safe
        ? `<a href="${safe}" target="_blank" rel="noopener"><img src="${safe}" alt="" style="max-width:120px;max-height:120px;object-fit:cover;border-radius:8px;border:1px solid #ddd;"></a>
           <a href="${safe}" download target="_blank" rel="noopener" class="ms-2 small"><i class="bi bi-download me-1"></i>Download</a>`
        : '<span class="small text-body-secondary">No photo</span>'}</div>`;
    }
    if (_isMultiChoiceColumn(col)) {
      const opts = col.customFieldOptions || [];
      if (_choiceTakesCounts(col, r)) {
        const stored = r.customFieldsData?.[col.customFieldId];
        const counts = (stored && typeof stored === 'object' && stored.perGuestQuantities) || _choiceCountsFromText(cur, opts, _partySize(r)) || {};
        return `<div class="${wrapCls}"><div class="form-label small mb-1">${label} <span class="text-body-secondary">(how many of each)</span></div>
          <div class="d-flex flex-wrap gap-2" data-eg-col="${_esc(col.key)}" data-eg-kind="counts">${opts.map((o, i) => `
            <label class="d-flex align-items-center gap-1 small border rounded px-2 py-1">
              <input type="number" class="form-control form-control-sm" style="width:4.2rem" min="0" step="1" data-i="${i}" value="${parseInt(counts[o], 10) || 0}">
              <span>${_esc(o)}</span>
            </label>`).join('')}</div></div>`;
      }
      return `<div class="${wrapCls}"><div class="form-label small mb-1">${label}</div>
        <div data-eg-col="${_esc(col.key)}" data-eg-kind="ticks">${opts.map((o, i) => `
          <div class="form-check form-check-inline">
            <input class="form-check-input" type="checkbox" id="${id}_${i}" data-i="${i}"${_choicePicked(cur, o) ? ' checked' : ''}>
            <label class="form-check-label small" for="${id}_${i}">${_esc(o)}</label>
          </div>`).join('')}</div></div>`;
    }
    if (_isChoiceColumn(col)) {
      const opts = col.customFieldOptions || [];
      const all = !cur || opts.includes(cur) ? opts : [cur, ...opts];
      return `<div class="${wrapCls}"><label class="form-label small mb-1" for="${id}">${label}</label>
        <select class="form-select form-select-sm" id="${id}" data-eg-col="${_esc(col.key)}" data-eg-kind="value">
          <option value="">—</option>
          ${all.map((o) => `<option value="${_esc(o)}"${o === cur ? ' selected' : ''}>${_esc(o)}</option>`).join('')}
        </select></div>`;
    }
    const long = col.key === 'notes' || col.customFieldType === 'textarea' || cur.length > 60;
    if (long) {
      return `<div class="${wrapCls}"><label class="form-label small mb-1" for="${id}">${label}</label>
        <textarea class="form-control form-control-sm" id="${id}" rows="2" data-eg-col="${_esc(col.key)}" data-eg-kind="value">${_esc(cur)}</textarea></div>`;
    }
    const type = col.type === 'email' ? 'email' : col.key === 'phone' ? 'tel' : 'text';
    return `<div class="${wrapCls}"><label class="form-label small mb-1" for="${id}">${label}</label>
      <input type="${type}" class="form-control form-control-sm" id="${id}" data-eg-col="${_esc(col.key)}" data-eg-kind="value"${type === 'tel' ? ' dir="ltr"' : ''} value="${_esc(cur)}"></div>`;
  }

  /** What a field in the dialog now says, in the cell's own text form. */
  function _egReadField(el, col) {
    const kind = el.dataset.egKind;
    const opts = col.customFieldOptions || [];
    if (kind === 'ticks') {
      return Array.from(el.querySelectorAll('input[type="checkbox"]:checked')).map((i) => opts[+i.dataset.i]).join(', ');
    }
    if (kind === 'counts') {
      return Array.from(el.querySelectorAll('input[type="number"]'))
        .map((i) => ({ o: opts[+i.dataset.i], n: Math.max(0, parseInt(i.value, 10) || 0) }))
        .filter((x) => x.n > 0).map((x) => `${x.n} ${x.o}`).join(', ');
    }
    return String(el.value ?? '');
  }

  function editGuest(responseId) {
    const r = state.responses.find(x => x.id === responseId);
    if (!r) return;
    _teardownEditGuest();

    const cols = _allPossibleColumns().filter((c) => c.key.startsWith('cf:') || (c.editable && c.key !== 'status'));
    const status = _normalizeStatus(r);
    const decided = status === 'pending' || status === 'waitlist';
    const separateKids = state.form?.separateAdultsChildren === true;
    const adults = r.numberOfAdults ?? r.adultCount ?? '';
    const kids = r.numberOfChildren ?? r.childrenCount ?? r.childCount ?? '';
    const guests = Array.isArray(r.additionalGuestDetails) ? r.additionalGuestDetails : [];

    const statusHtml = decided
      ? `<div class="form-control-plaintext form-control-sm">${status === 'pending' ? 'Waiting for your approval' : 'On the waitlist'}
           <span class="small text-body-secondary">- use Approve or Deny on the row</span></div>`
      : `<select class="form-select form-select-sm" id="egStatus">
           ${status === 'awaiting' ? '<option value="" selected>Awaiting reply</option>' : ''}
           <option value="yes"${status === 'yes' ? ' selected' : ''}>Attending</option>
           <option value="no"${status === 'no' ? ' selected' : ''}>Declined</option>
         </select>`;
    const partyHtml = separateKids ? `
        <div class="col-6 col-sm-3"><label class="form-label small mb-1" for="egAdults">Adults</label>
          <input type="number" class="form-control form-control-sm" id="egAdults" min="0" max="50" value="${_esc(String(adults))}"></div>
        <div class="col-6 col-sm-3"><label class="form-label small mb-1" for="egChildren">Children</label>
          <input type="number" class="form-control form-control-sm" id="egChildren" min="0" max="50" value="${_esc(String(kids))}"></div>` : `
        <div class="col-12 col-sm-6"><label class="form-label small mb-1" for="egParty">Party size</label>
          <input type="number" class="form-control form-control-sm" id="egParty" min="1" max="50" value="${_partySize(r)}"></div>`;

    const nameCol = cols.find((c) => c.key === 'guestName');
    const contactCols = cols.filter((c) => !c.key.startsWith('cf:') && c.key !== 'guestName');
    const questionCols = cols.filter((c) => c.key.startsWith('cf:'));
    const guestsHtml = guests.length ? `
        <hr class="my-3"><div class="small text-body-secondary mb-2">Who else is coming</div>
        ${guests.map((g, gi) => {
          const keys = EG_GUEST_KEYS.filter((k) => k === 'name' || g[k] != null || _agDetailKeys().includes(k));
          return `<div class="border rounded p-2 mb-2">
            <div class="small text-body-secondary mb-1">Guest ${_esc(String(g.position || gi + 2))}</div>
            <div class="row g-2">${keys.map((k) => `
              <div class="col-12 col-sm-6"><label class="form-label small mb-1" for="eg_g${gi}_${k}">${EG_GUEST_LABELS[k]}</label>
                <input type="${k === 'email' ? 'email' : k === 'phone' ? 'tel' : 'text'}" class="form-control form-control-sm"
                       id="eg_g${gi}_${k}" data-eg-guest="${gi}" data-eg-guest-key="${k}"${k === 'phone' ? ' dir="ltr"' : ''}
                       value="${_esc(String(g[k] ?? ''))}"></div>`).join('')}
            </div></div>`;
        }).join('')}` : '';
    const when = _fmtDate(r.submittedAt || r.createdAt);
    const meta = [when ? `Submitted ${when}` : '', _responseSource(r) === 'Manual' ? 'Added by your team' : 'Replied through the form']
      .filter(Boolean).join(' · ');

    const html = `
      <div class="modal fade" id="editGuestModal" tabindex="-1" aria-hidden="true" aria-labelledby="editGuestTitle">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="editGuestTitle" style="font-size:1rem;"><i class="bi bi-pencil me-2"></i>Edit guest response</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div class="row g-2 mb-2">
                ${nameCol ? _egFieldHtml(nameCol, r) : ''}
                <div class="col-12 col-sm-6"><label class="form-label small mb-1" for="egStatus">Status</label>${statusHtml}</div>
                ${partyHtml}
                ${contactCols.map((c) => _egFieldHtml(c, r)).join('')}
              </div>
              ${questionCols.length ? `<hr class="my-3"><div class="small text-body-secondary mb-2">Your event's own questions</div>
                <div class="row g-2">${questionCols.map((c) => _egFieldHtml(c, r)).join('')}</div>` : ''}
              ${guestsHtml}
              <div class="small text-body-secondary mt-3">${_esc(meta)}</div>
              <div class="alert alert-danger py-2 mb-0 mt-2" id="egError" style="display:none;font-size:0.85rem;"></div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-org-ghost btn-sm" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-org-primary btn-sm" id="egSave">Save changes</button>
            </div>
          </div>
        </div>
      </div>`;
    document.body.insertAdjacentHTML('beforeend', html);
    const modalEl = document.getElementById('editGuestModal');
    const modal = new bootstrap.Modal(modalEl);
    // Every way out is wired directly - Bootstrap's hide events do not reach
    // listeners on this page (see openAddGuest).
    modalEl.querySelectorAll('[data-bs-dismiss="modal"]').forEach((b) => {
      b.addEventListener('click', (e) => { e.preventDefault(); _teardownEditGuest(); });
    });
    modalEl.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); _teardownEditGuest(); } });
    modalEl.addEventListener('click', (e) => { if (e.target === modalEl) _teardownEditGuest(); });
    modalEl.querySelector('#egSave').addEventListener('click', () => _saveEditGuest(modalEl, r, cols));
    modal.show();
  }

  async function _saveEditGuest(modalEl, r, cols) {
    const err = modalEl.querySelector('#egError');
    const btn = modalEl.querySelector('#egSave');
    const show = (msg) => { err.textContent = msg; err.style.display = 'block'; };
    err.style.display = 'none';
    const colByKey = Object.fromEntries(cols.map((c) => [c.key, c]));

    // A working copy the edits are applied to in turn, so each custom answer
    // builds on the ones before it and the per-guest counts see the new party.
    const work = { ...r, customFieldsData: { ...(r.customFieldsData || {}) } };
    const payload = {};
    const apply = (p) => {
      Object.assign(payload, p);
      Object.assign(work, p);
    };

    const nameEl = modalEl.querySelector('[data-eg-col="guestName"]');
    if (nameEl && !nameEl.value.trim()) { show('A name is required.'); nameEl.focus(); return; }

    const statusEl = modalEl.querySelector('#egStatus');
    if (statusEl && statusEl.value && statusEl.value !== _normalizeStatus(r)) {
      apply(_writeCellPayload({ key: 'status' }, statusEl.value, work));
    }
    const num = (sel) => { const el = modalEl.querySelector(sel); const v = parseInt(el && el.value, 10); return Number.isFinite(v) ? Math.max(0, Math.min(50, v)) : null; };
    if (modalEl.querySelector('#egParty')) {
      const n = num('#egParty');
      if (n && n !== _partySize(r)) apply({ guestCount: n });
    } else {
      const a = num('#egAdults'), k = num('#egChildren');
      if (a != null && String(a) !== String(r.numberOfAdults ?? r.adultCount ?? '')) apply(_writeCellPayload({ key: 'numberOfAdults' }, a, work));
      if (k != null && String(k) !== String(r.numberOfChildren ?? r.childrenCount ?? r.childCount ?? '')) apply(_writeCellPayload({ key: 'numberOfChildren' }, k, work));
    }

    modalEl.querySelectorAll('[data-eg-col]').forEach((el) => {
      const col = colByKey[el.dataset.egCol];
      if (!col) return;
      const next = _egReadField(el, col);
      const before = String(_readCell(r, col) ?? '');
      if (next.trim() === before.trim()) return;
      apply(_writeCellPayload(col, next.trim(), work));
    });

    const guests = Array.isArray(r.additionalGuestDetails) ? r.additionalGuestDetails : [];
    if (guests.length) {
      let changed = false;
      const nextGuests = guests.map((g, gi) => {
        const out = { ...g };
        modalEl.querySelectorAll(`[data-eg-guest="${gi}"]`).forEach((inp) => {
          const k = inp.dataset.egGuestKey;
          const v = inp.value.trim();
          if (v !== String(g[k] ?? '').trim()) { changed = true; out[k] = v; }
        });
        return out;
      });
      if (changed) apply({ additionalGuestDetails: nextGuests });
    }

    if (!Object.keys(payload).length) { _teardownEditGuest(); return; }
    btn.disabled = true;
    _noteLocalWrite();
    try {
      const token = await window.firebaseAuth.currentUser.getIdToken();
      const res = await fetch(
        `/api/forms/${encodeURIComponent(state.formId)}/responses/${encodeURIComponent(r.id)}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) }
      );
      if (!res.ok) {
        let msg = 'Save failed (' + res.status + ')';
        try { const j = await res.json(); if (j && j.error) msg = j.error; } catch (_) { /* not JSON */ }
        throw new Error(msg);
      }
      Object.assign(r, payload);
      _noteLocalWrite();
      _teardownEditGuest();
      _showToast('Saved');
      _renderActiveTab();
    } catch (e) {
      btn.disabled = false;
      show('Could not save: ' + e.message);
    }
  }

  // The row's old "View details" entry point; kept so nothing that still calls
  // it breaks.
  function showDetails(responseId) { return editGuest(responseId); }

  // ============================================================================
  // URL HYDRATION — let the org dashboard deep-link into an event
  // ============================================================================

  function hydrateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const eventId = params.get('event');
    if (eventId) open(eventId, params.get('view') || 'responses');
  }

  window.addEventListener('popstate', (e) => {
    const params = new URLSearchParams(window.location.search);
    const eventId = params.get('event');
    if (eventId && eventId !== state.formId) {
      open(eventId, params.get('view') || 'responses');
    } else if (!eventId && state.formId) {
      close();
    }
  });

  /**
   * The per-event read-only viewer link (task #614) — the SAME modal the events
   * list opens, so the link, the column picker and revoke all behave
   * identically. Owned by OrgDashboard because that is where the modal markup
   * lives; this just hands it the event we are already looking at.
   */
  function showViewerLink() {
    if (!state.formId) return;
    const title = (state.form && (state.form.title || state.form.eventTitle)) || '';
    const dash = _dash();
    if (dash && typeof dash.showEventViewerLinks === 'function') {
      dash.showEventViewerLinks(state.formId, title);
    } else {
      console.warn('Viewer link modal is not available on this page');
    }
  }

  return {
    open, close, switchView, hydrateFromUrl, showViewerLink,
    approve, deny, bulkApprove, bulkDeny, clearSelection, bulkDelete, deleteResponse, bulkSetStatus,
    approveAllPending, denyAllPending, showPendingInTable, toggleApprovals, openImport,
    copyGeneralLink, sendCampaignFromResponses, buyCredits, downloadEventQr,
    exportAll, exportSelected, openNameTags, showDetails, editGuest, sortBy, dismissQrAutoNoTables,
    showMasterQr, copyGuestCheckinLink,
    openAddTableModal, removeTable, renameTable, setCapacity, toggleShape,
    unseatMember, undo, redo, autoAssign, openRulesModal, _addRule, _removeRule,
    setSeatingMode, setDoorStrategy,
    exportSeatingCsv, printSeating,
    newCampaign, resumeDraft, discardDraft, previewCampaign, openAddGuest,
    setGroupByCompany, dismissCompanySuggestion, guardDoorOpen
  };
})();
